import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Copy, Check, KeyRound, Loader2, ExternalLink, Mail, ShieldCheck, Inbox, CheckCircle2, XCircle, Building2, Send, Ban, RotateCcw, Percent, Network, RefreshCw, Wallet, AlertTriangle, Handshake, Truck, UserCheck, ChevronDown, ChevronRight, FileText, Database, Search, Users, CreditCard, Printer, Download, X, Landmark, FileCheck2, Repeat, Layers, Clock, Activity, Scale, Lock, CalendarClock, FolderOpen, Pen, Gavel, FolderKanban, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { SupportMessaging } from "../components/SupportMessaging";
import { InboxBell } from "../components/InboxBell";
import { AccessActivity, BorrowerAccessHistory } from "../components/AccessActivity";
import { FaxPanel, FaxPresetPicker } from "../components/FaxPanel";
import { CreditReserve } from "../components/CreditReserve";
import { SecuritiesAdmin } from "../components/SecuritiesAdmin";
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { US_STATES, recommendedNoteStructure } from "../lib/securityInstrument";
import { GSA_FORMS } from "../pages/GsaBondPackage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import AccountDetailsPanel from "../components/AccountDetailsPanel";
import BrokerAccessManager from "../components/BrokerAccessManager";
import LenderSubmissionsPanel from "../components/LenderSubmissionsPanel";
import BatchSalePanel from "../components/BatchSalePanel";
import BulkDocsPanel from "../components/BulkDocsPanel";
import { SealWatermark } from "../components/SealWatermark";

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AdminDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const isOwner = !!user?.is_owner;
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ label: "", ttl_days: 30, note: "" });
  const [copiedId, setCopiedId] = useState(null);
  const [err, setErr] = useState(null);
  const [activeView, setActiveView] = useState("dashboard");
  const [fciuNew, setFciuNew] = useState(0);

  // Live badge: count newly-paid FCIU engagements since the board was last opened.
  useEffect(() => {
    if (!token) return;
    const cfg = { headers: { Authorization: `Bearer ${token}` } };
    const check = async () => {
      try {
        const r = await axios.get(`${API}/admin/fraud-report/cases`, cfg);
        const seen = Number(localStorage.getItem("fciu_last_seen") || 0);
        const n = (r.data.cases || []).filter(
          (c) => c.paid && c.paid_at && new Date(c.paid_at).getTime() > seen
        ).length;
        setFciuNew(n);
      } catch { /* noop */ }
    };
    check();
    const iv = setInterval(check, 45000);
    return () => clearInterval(iv);
  }, [token]);

  // Scoped (non-owner) admins never land on owner-only views.
  useEffect(() => {
    if (user && !user.is_owner && !SCOPED_ALLOWED.has(activeView)) {
      setActiveView("borrower-apps");
    }
  }, [user, activeView]);

  // ---- Offer Inbox state ----
  const [offers, setOffers] = useState({ pending: [], resolved: [], pending_count: 0 });
  const [offersLoading, setOffersLoading] = useState(true);
  const [decidingId, setDecidingId] = useState(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/market-tokens`);
      setTokens(r.data.tokens || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  }, []);

  const reloadOffers = useCallback(async () => {
    try {
      setOffersLoading(true);
      const r = await axios.get(`${API}/admin/offers`);
      setOffers(r.data || { pending: [], resolved: [], pending_count: 0 });
    } catch {
      /* noop — keep last state */
    }
    setOffersLoading(false);
  }, []);

  // ---- Borrower Applications state ----
  const [bApps, setBApps] = useState({ pending: [], resolved: [], pending_count: 0 });
  const [bDecidingId, setBDecidingId] = useState(null);
  const reloadBApps = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/borrower-applications`);
      setBApps(r.data || { pending: [], resolved: [], pending_count: 0 });
    } catch { /* noop */ }
  }, []);

  // ---- Payment receipts (admin/admin-team copy) ----
  const [receipts, setReceipts] = useState([]);
  const reloadReceipts = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/borrower-applications/receipts`); setReceipts(r.data.receipts || []); } catch { /* noop */ }
  }, []);
  const deleteReceipt = useCallback(async (rc) => {
    if (!window.confirm(`Delete receipt ${rc.receipt_no}? This permanently removes it.`)) return;
    try { await axios.delete(`${API}/admin/borrower-applications/receipts/${rc.id}`); toast.success("Receipt deleted"); await reloadReceipts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }, [reloadReceipts]);

  // ---- Trading book (note deals) for CFO analytics ----
  const [txns, setTxns] = useState([]);
  const reloadTxns = useCallback(async () => {
    try { const r = await axios.get(`${API}/audit/transactions`); setTxns(r.data.transactions || []); } catch { /* noop */ }
  }, []);

  // ---- Broker Applications state ----
  const [brokers, setBrokers] = useState({ pending: [], resolved: [], pending_count: 0 });
  const [brDecidingId, setBrDecidingId] = useState(null);
  const reloadBrokers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/broker/applications`);
      setBrokers(r.data || { pending: [], resolved: [], pending_count: 0 });
    } catch { /* noop */ }
  }, []);

  const decideBroker = async (brokerId, action, note) => {
    setBrDecidingId(brokerId);
    try {
      await axios.post(`${API}/admin/broker/${brokerId}/${action}`, { note: note || null });
      toast.success(`Broker ${action}d.`);
      await reloadBrokers();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBrDecidingId(null);
  };

  const disburseClient = async (appId) => {
    if (!window.confirm("Confirm disbursement of financing instruments to the previous lender/counterparty?")) return;
    try {
      await axios.post(`${API}/admin/broker/clients/${appId}/disburse`, {});
      toast.success("Marked disbursed — pipeline advanced.");
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const chargeAutopay = async (appId) => {
    if (!window.confirm("Run auto-debit now — charge the borrower's saved card for the next installment?")) return;
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${appId}/charge-autopay`, {});
      if (r.data.ok) toast.success(`Auto-debit succeeded — installment #${r.data.installment_n} ($${r.data.amount}).`);
      else toast.error(`Auto-debit ${r.data.status}: ${r.data.detail || "declined"}`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const markFeesPaid = async (appId) => {
    if (!window.confirm("Record closing costs as collected for this borrower?")) return;
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/mark-closing-fees-paid`, {});
      toast.success("Closing costs marked collected.");
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const setDiscounting = async (appId, status) => {
    if (!status) return;
    let bank_name = "";
    if (status === "submitted_to_bank") bank_name = window.prompt("Discounting bank name (optional):", "") || "";
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/discounting-status`, { status, bank_name });
      toast.success(`Discounting status → ${status.replace(/_/g, " ")}`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleWaiveFees = async (appId, name, currentlyWaived) => {
    const on = !currentlyWaived;
    if (!window.confirm(`${on ? "WAIVE all fees" : "Remove the fee waiver"} for ${name || "this borrower"}? ${on ? "All closing costs, monthly/remaining balance and broker commission on this account become $0 (No Cost)." : "Normal fees, balance and commission will resume."}`)) return;
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/waive-fees`, { waived: on });
      toast.success(on ? "Fees waived — account now No Cost." : "Fee waiver removed.");
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const refreshSavedBinders = async (appId, name) => {
    if (!window.confirm(`Refresh the saved binder copies for ${name || "this loan"} with the latest loan data? Manually entered fields (payoff figures, remittance, signers) are preserved.`)) return;
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${appId}/refresh-saved-binders`, {});
      const w = r.data.refreshed || {};
      toast.success(`Saved copy refreshed & marked current${w.closing ? " · Closing" : ""}${w.draft ? " · Draft" : ""}.`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };


  const reconcileFees = async (appId) => {
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${appId}/reconcile-closing-fees`, {});
      const st = r.data.status;
      const inst = r.data.installments_reconciled || 0;
      if (st === "reconciled_paid") toast.success(`Verified on Stripe — payment(s) found and marked collected${inst ? ` (${inst} installment${inst !== 1 ? "s" : ""})` : ""}. Receipt issued.`);
      else if (st === "already_paid") toast.success(`Already recorded as paid${inst ? ` · reconciled ${inst} installment(s)` : ""}. Receipt re-issued.`);
      else toast.message("No unconfirmed paid session found on Stripe for this file yet.");
      await reloadBApps();
      await reloadReceipts();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const runAllAutodebit = async () => {
    if (!window.confirm("Run the monthly auto-debit batch now? This charges every card-on-file whose installment is due (LIVE charges).")) return;
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/autodebit/run-all`, {});
      toast.success(`Auto-debit batch: ${r.data.charged_count} charged, ${r.data.failed_count} failed, ${r.data.skipped} not yet due.`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const sendPaymentReminders = async () => {
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/send-payment-reminders?days=5`, {});
      const n = r.data.sent_count || 0;
      toast.success(n ? `Sent ${n} payment reminder${n !== 1 ? "s" : ""} for installments due within 5 days.` : "No installments due within the next 5 days — no reminders needed right now.");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const runCommissionPayouts = async () => {
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/run-commission-payouts`, {});
      const t = r.data.transferred || 0, d = r.data.deferred || 0;
      const af = r.data.admin_fee_transferred || 0, afAmt = r.data.admin_fee_amount || 0;
      const afMsg = af ? ` Paid ${af} administrative fee${af !== 1 ? "s" : ""} (${usd0(afAmt)}) to brokers.` : "";
      if (t) toast.success(`Paid ${t} broker commission${t !== 1 ? "s" : ""} (${usd0(r.data.amount)}) to connected accounts.${afMsg}${d ? ` ${d} deferred until funds clear.` : ""}`);
      else if (af) toast.success(`Paid ${af} administrative fee${af !== 1 ? "s" : ""} (${usd0(afAmt)}) to brokers.`);
      else if (d) toast.message(`${d} payout(s) waiting on Stripe funds to clear — will retry automatically.`);
      else toast.message("No broker commissions or administrative fees to pay out right now.");
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const rebaselineSchedules = async () => {
    if (!window.confirm("Re-run the 45-day payment schedule across all approved loans? Paid installments are preserved and e-signatures are not affected.")) return;
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/rebaseline-schedules`, {});
      toast.success(`Re-baselined ${r.data.updated} loan${r.data.updated !== 1 ? "s" : ""} to the 45-day payment schedule.`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const setAllDueDates = async () => {
    const date = window.prompt("Set the NEXT payment due date for ALL approved loans (YYYY-MM-DD). Each following payment advances one month. Paid installments are untouched.", "2026-10-01");
    if (!date) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { toast.error("Enter the date as YYYY-MM-DD, e.g. 2026-10-01."); return; }
    const nextOnly = !window.confirm(`Move the next payment to ${date} AND shift the whole remaining schedule monthly from there?\n\nOK = shift entire schedule.  Cancel = change ONLY the next payment.`);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/set-due-dates`, { target_date: date, mode: nextOnly ? "next_only" : "reschedule" });
      toast.success(`Updated ${r.data.updated} loan${r.data.updated !== 1 ? "s" : ""} — next payment due ${date}${nextOnly ? "" : ", schedule shifted monthly"}.${r.data.skipped ? ` (${r.data.skipped} skipped — no unpaid installments.)` : ""}`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const markPaidInFull = async (appId, name) => {
    if (!window.confirm(`Mark ${name || "this borrower"}'s account PAID IN FULL? Every remaining installment will be recorded as paid and the balance owed becomes $0.00.`)) return;
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${appId}/mark-paid-in-full`, {});
      toast.success(`${name || "Account"} marked paid in full — ${r.data.installments_marked_paid} installment(s) settled, $0.00 owed.`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const payCommission = async (appId, canAuto) => {
    const msg = canAuto
      ? "Send the accrued broker commission now via a real Stripe transfer to the broker's connected account?"
      : "The broker hasn't connected Stripe payouts, so this will be RECORDED only (no money moves). Continue?";
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${appId}/pay-commission`, {});
      const p = r.data.payment || {};
      const via = p.payout_status === "transferred" ? ` — Stripe transfer ${p.stripe_transfer_id}` : " (recorded)";
      toast.success(`Commission ${p.payout_status === "transferred" ? "transferred" : "recorded"} — $${p.amount.toLocaleString()} (ref ${p.ref})${via}.`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const decideApp = async (appId, action) => {
    setBDecidingId(appId);
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/${action}`, {});
      toast.success(action === "approve" ? "Application approved — 0% note issued." : "Application rejected.");
      await reloadBApps();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setBDecidingId(null);
  };

  const deleteApp = async (appId) => {
    if (!window.confirm("Delete this borrower transaction? It will be archived (hidden from all lists) and can be restored later. Its documents and payment records are preserved.")) return;
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/archive`, {});
      toast.success("Borrower transaction archived.", {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(`${API}/admin/borrower-applications/${appId}/unarchive`, {});
              toast.success("Restored — transaction is back.");
              await reloadBApps();
            } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
          },
        },
      });
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const staffEsignApp = async (appId, borrower) => {
    const name = window.prompt(`E-SIGN BORROWER'S PACKAGE\n\nType the borrower's full legal name to apply their e-signature to every document. This signature is FINAL.`, borrower || "");
    if (!name || !name.trim()) return;
    const co = window.prompt("Co-borrower's full legal name (optional):", "") || "";
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/staff-esign`, { signer_name: name.trim(), co_signer_name: co.trim() || null });
      toast.success(`Package e-signed for ${name.trim()}.`);
      await reloadBApps();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const deleteBroker = async (brokerId) => {
    if (!window.confirm("Permanently DELETE this broker and all their client transactions? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/broker/${brokerId}`);
      toast.success("Broker record deleted.");
      await reloadBrokers();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const [egress, setEgress] = useState(null);
  const [egressLoading, setEgressLoading] = useState(false);
  const loadEgressIp = useCallback(async () => {
    setEgressLoading(true);
    try {
      const r = await axios.get(`${API}/admin/egress-ip`);
      setEgress(r.data);
    } catch (e) {
      setEgress({ error: e.response?.data?.detail || e.message });
    }
    setEgressLoading(false);
  }, []);

  const [inbox, setInbox] = useState({ count: 0, payloads: [] });
  const [inboxLoading, setInboxLoading] = useState(false);
  const loadInbox = useCallback(async () => {
    setInboxLoading(true);
    try {
      const r = await axios.get(`${API}/admin/inbound-payloads`);
      setInbox(r.data || { count: 0, payloads: [] });
    } catch { /* noop */ }
    setInboxLoading(false);
  }, []);

  const [vault, setVault] = useState({ count: 0, drafts: [] });
  const [vaultLoading, setVaultLoading] = useState(false);
  const [faxFocus, setFaxFocus] = useState(null);
  const [appFax, setAppFax] = useState(null);
  const loadVault = useCallback(async () => {
    setVaultLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vault/drafts`);
      setVault(r.data || { count: 0, drafts: [] });
    } catch { /* noop */ }
    setVaultLoading(false);
  }, []);

  useEffect(() => { if (user) { reload(); reloadOffers(); reloadBApps(); reloadBrokers(); loadEgressIp(); loadInbox(); reloadTxns(); loadVault(); reloadReceipts(); } }, [user, reload, reloadOffers, reloadBApps, reloadBrokers, loadEgressIp, loadInbox, reloadTxns, loadVault, reloadReceipts]);

  const supportClient = useMemo(() => axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem("th_admin_token")}` } }), []);

  const voidDraft = async (vaultId) => {    if (!window.confirm("Void this draft? Its redemption token will be permanently invalidated and the instrument can no longer be withdrawn. This cannot be undone.")) return;
    try {
      await axios.post(`${API}/admin/vault/drafts/${vaultId}/void`);
      toast.success("Draft voided — redemption token invalidated.");
      loadVault();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not void the draft.");
    }
  };

  if (user === undefined) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-[#5C5C5C]"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const decideOffer = async (txId, decision) => {
    const verb = decision === "accept" ? "ACCEPT" : "REJECT";
    let note = null;
    if (decision === "reject") {
      note = window.prompt("Optional reason for rejecting this offer (the deal will be re-listed):", "");
      if (note === null) return; // cancelled
    } else if (!window.confirm(`${verb} this offer? This locks the counterparty as the buyer of record and emails them a secure link to the executed binder.`)) {
      return;
    }
    setDecidingId(txId);
    try {
      const r = await axios.post(`${API}/admin/offers/${txId}/${decision}`, { note: note || null });
      if (decision === "accept") {
        const dlv = r.data?.delivery;
        if (dlv?.ok) toast.success(`Offer accepted · binder link emailed to ${dlv.to_email}`);
        else toast.warning(`Offer accepted, but binder delivery failed: ${dlv?.reason || "no counterparty email on file"}`);
      }
      await reloadOffers();
    } catch (e) {
      alert(`${verb} failed: ` + (e.response?.data?.detail || e.message));
    }
    setDecidingId(null);
  };

  const deliverBinder = async (txId) => {
    setDecidingId(txId);
    try {
      const r = await axios.post(`${API}/admin/offers/${txId}/deliver`, {});
      toast.success(`Binder link re-sent to ${r.data?.to_email}`);
    } catch (e) {
      toast.error("Delivery failed: " + (e.response?.data?.detail || e.message));
    }
    setDecidingId(null);
  };


  const issueToken = async () => {
    if (!newForm.label.trim()) { alert("Counterparty label is required."); return; }
    setCreating(true); setErr(null);
    try {
      const r = await axios.post(`${API}/admin/market-tokens`, {
        label: newForm.label,
        ttl_days: Number(newForm.ttl_days) || 30,
        note: newForm.note || null,
      });
      setNewForm({ label: "", ttl_days: 30, note: "" });
      await reload();
      // copy share URL to clipboard
      const fullUrl = (r.data.share_url || "").startsWith("http")
        ? r.data.share_url
        : `${window.location.origin}${r.data.share_url}`;
      try { await navigator.clipboard.writeText(fullUrl); setCopiedId(r.data.id); setTimeout(() => setCopiedId(null), 3000); } catch { /* noop */ }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setCreating(false);
  };

  const revoke = async (id, label) => {
    if (!window.confirm(`Revoke token for "${label}"? Counterparty link will stop working immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/market-tokens/${id}`);
      await reload();
    } catch (e) {
      alert("Revoke failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const suspend = async (id, label) => {
    const reason = window.prompt(`Suspend / restrict "${label}" for a policy violation?\nOptional reason (counterparty access stops immediately):`, "Policy violation");
    if (reason === null) return;
    try {
      await axios.post(`${API}/admin/market-tokens/${id}/suspend`, { reason: reason || "Policy violation" });
      await reload();
    } catch (e) {
      alert("Suspend failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const restore = async (id, label) => {
    if (!window.confirm(`Restore access for "${label}"?`)) return;
    try {
      await axios.post(`${API}/admin/market-tokens/${id}/restore`, {});
      await reload();
    } catch (e) {
      alert("Restore failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const copyUrl = async (t) => {
    const url = `${window.location.origin}/market?market_token=${t.token}`;
    try { await navigator.clipboard.writeText(url); setCopiedId(t.id); setTimeout(() => setCopiedId(null), 2500); }
    catch { window.prompt("Copy this URL:", url); }
  };

  const mailtoLink = (t) => {
    const url = `${window.location.origin}/market?market_token=${t.token}`;
    const subject = encodeURIComponent(`Treasury House Marketplace — Access Invitation for ${t.label}`);
    const body = encodeURIComponent(
      `You have been invited by Treasury House Trust to view our private secondary marketplace.\n\n` +
      `Access link (expires ${new Date(t.expires_at).toLocaleDateString()}):\n${url}\n\n` +
      `This link is single-tenant and may be revoked at any time.\n\nThomas Franklin\nManaging Trustee · Treasury House Trust`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4]" data-testid="admin-dashboard">
      <SealWatermark fixed size={720} opacity={0.035} />
      <header className="border-b border-[#2A2A22] bg-[#13130E] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-[#B8860B]" size={18} />
            <div>
              <div className="text-[9px] tracking-[0.24em] uppercase text-[#B8860B] font-bold">Restricted · Back Office</div>
              <h1 className="text-lg font-serif">Treasury House Admin</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative" data-testid="doc-builder-dropdown-wrap">
              <select
                data-testid="doc-builder-select"
                defaultValue=""
                onChange={(e) => { const v = e.target.value; if (v) navigate(v); e.target.value = ""; }}
                className="appearance-none bg-[#0F0F0A] border border-[#B8860B]/50 text-[#E4C97A] text-[12px] font-semibold rounded-md pl-3 pr-8 py-2 hover:border-[#B8860B] focus:outline-none focus:border-[#d4a017] cursor-pointer transition-colors"
              >
                <option value="" disabled>Document Builders ▾</option>
                <option value="/draft-package">Private Negotiable Instrument Package</option>
                <option value="/closing-binder">Closing Binder</option>
                <option value="/respa-notice">RESPA Notice</option>
                <option value="/funding-commitment">Funding Commitment</option>
                <option value="/master-funding">Master Funding Agmt</option>
                <option value="/mcc">Tax Credit (MCC)</option>
                <option value="/forbearance">Forbearance</option>
                <option value="/debt-consolidation">Debt Consolidation</option>
                <option value="/tro-motion">TRO Motion</option>
                <option value="/collateral-motion">Collateral Preservation Motion (Auto/Mortgage)</option>
                <option value="/motion-packet">Motion Packet (Cover + Motion + Order + Cert. of Service)</option>
                <option value="/treasury-repo">Treasury Repo &amp; Collateral Financing (Fed Reserve MRA)</option>
                <option value="/federal-assistance">Federal Assistance / Mortgage Collateral Instrument</option>
                <option value="/gsa-bond">GSA Bond Package (SF-24/25/25A/28, OF-90/91)</option>
                <option value="/loss-mitigation">Loss Mitigation Package (Form 710 + Dodd-Frank + 4506-C)</option>
                <option value="/mortgage-replacement">Integrated Mortgage Replacement Agreement (+ Payment Coupon)</option>
                <option value="/tax-credit-transfer">Tax Credit Transfer &amp; IRS Lien/Levy Release (+ Coupon)</option>
                <option value="/note-sale-binder">Outright Note Sale Binder</option>
              </select>
              <ChevronDown size={14} className="text-[#B8860B] absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
            <a href="/admin/tax-program" data-testid="admin-tax-program-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold text-[#06160F] bg-[#6FD3AE] hover:bg-[#54c097] px-3.5 py-2 rounded-md transition-colors">
              <Landmark size={13} /> Tax Debt Program
            </a>
            <a href="/admin/private-bank" data-testid="admin-private-bank-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3.5 py-2 rounded-md transition-colors">
              <Landmark size={13} /> Bank of International Settlements and Securities
            </a>
            <a href="/fraud-report" data-testid="admin-fraud-report-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold text-[#E4C97A] border border-[#8A6D1F] hover:border-[#E4C97A] px-3.5 py-2 rounded-md transition-colors">
              <ShieldAlert size={13} /> Fraud Report
            </a>
            <a href="/investigations" data-testid="admin-investigations-link"
              className="relative inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold text-[#06160F] bg-[#E4C97A] hover:bg-[#d4b964] px-3.5 py-2 rounded-md transition-colors">
              <ShieldAlert size={13} /> Investigations
              {fciuNew > 0 && (
                <span data-testid="admin-investigations-badge"
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#e5484d] text-white text-[10px] font-extrabold ring-2 ring-[#06160F] animate-pulse">
                  {fciuNew}
                </span>
              )}
            </a>
            <a href="/certificate?scope=master" target="_blank" rel="noreferrer" data-testid="admin-master-cert-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold text-[#0B0B07] bg-[#E4C97A] hover:bg-[#d4b964] px-3.5 py-2 rounded-md transition-colors">
              <ShieldCheck size={13} /> Master Certificate
            </a>
            <a href="/certificate?scope=member" target="_blank" rel="noreferrer" data-testid="admin-member-cert-link"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold text-[#E4C97A] border border-[#8A6D1F] hover:border-[#E4C97A] px-3.5 py-2 rounded-md transition-colors">
              <ShieldCheck size={13} /> My Certificate
            </a>
            <div className="text-right">
              <div className="text-[12px] font-mono">{user.email}</div>
              <div className="text-[10px] text-[#5C5C5C]">{user.name}</div>
            </div>
            <InboxBell client={supportClient} accent="#E4C97A" extraUnreadUrl="/admin/access-activity/unseen-count" />
            <button onClick={logout} className="flex items-center gap-1.5 text-[12px] text-[#FF8585] hover:text-[#ff6060]" data-testid="admin-logout-btn">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="flex max-w-[1500px] mx-auto" data-testid="admin-shell">
        <AdminSidebar
          activeView={activeView}
          setActiveView={setActiveView}
          isOwner={isOwner}
          counts={{
            "broker-apps": brokers.pending_count || 0,
            "borrower-apps": bApps.pending_count || 0,
            offers: offers.pending_count || 0,
            acceptance: inbox.count || 0,
            vault: vault.drafts.filter((d) => d.status === "vaulted").length,
          }}
        />
        <main className="flex-1 min-w-0 px-6 py-10" data-testid="admin-main">
          {!isOwner && (
            <div className="mb-6 rounded-lg border border-[#3DBB8E]/40 bg-[#0d2a20] px-4 py-3 flex items-center gap-2.5" data-testid="admin-scoped-banner">
              <Lock size={14} className="text-[#3DBB8E]" />
              <span className="text-[12px] text-[#B7E4D0]">Scoped access — you see only your own brokerages and the ones you're authorized for. Company-wide financials and controls are visible to the owner only.</span>
            </div>
          )}
          {activeView === "permissions" && isOwner && <BrokerAccessManager client={supportClient} />}
          {activeView === "team" && <AdminTeamSection />}
          {activeView === "batch-sale" && <BatchSalePanel />}
          {activeView === "documents" && <BulkDocsPanel loans={bApps.resolved} />}
        {/* ============ ACTION REQUIRED ============ */}
        {activeView === "dashboard" && (() => {
          const approvedLoans = bApps.resolved.filter((x) => x.status === "approved");
          const toDisburse = approvedLoans.filter((a) => !a.disbursed).length;
          const feesToCollect = approvedLoans.filter((a) => !a.closing_fees?.paid && Number(a.closing_fees?.total || 0) > 0).length;
          const bp = brokers.pending_count || 0;
          const ap = bApps.pending_count || 0;
          const total = bp + ap + toDisburse + feesToCollect;
          const Item = ({ view, label, count, color }) => (
            <button type="button" onClick={() => setActiveView(view)} data-testid={`action-item-${view}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-[#2A2A22] bg-[#0F0F0A] hover:border-[#C9A227] transition-colors">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${color}`}>{count}</span>
              <span className="text-[12px] text-[#ECECE4]">{label}</span>
            </button>
          );
          return (
            <section className="mb-10" data-testid="action-required-card">
              <div className={`rounded-lg border p-5 ${total > 0 ? "border-[#B8860B]/50 bg-[#1A1606]" : "border-[#2A2A22] bg-[#13130E]"}`}>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={16} className={total > 0 ? "text-[#E4C97A]" : "text-[#5C5C5C]"} />
                  <h2 className="text-[13px] uppercase tracking-[0.14em] font-bold text-[#E4C97A]">Action Required</h2>
                  {total === 0 && <span className="text-[12px] text-[#5C5C5C]" data-testid="action-required-clear">All caught up — nothing pending. ✓</span>}
                </div>
                {total > 0 && (
                  <div className="flex flex-wrap gap-3" data-testid="action-required-items">
                    {bp > 0 && <Item view="broker-apps" label={`broker${bp > 1 ? "s" : ""} awaiting approval`} count={bp} color="bg-[#C9A227] text-[#0A0A0A]" />}
                    {ap > 0 && <Item view="borrower-apps" label={`borrower${ap > 1 ? "s" : ""} awaiting approval`} count={ap} color="bg-[#C9A227] text-[#0A0A0A]" />}
                    {toDisburse > 0 && <Item view="brokerage" label={`payoff${toDisburse > 1 ? "s" : ""} to disburse`} count={toDisburse} color="bg-[#6C8CD5] text-[#0A0A0A]" />}
                    {feesToCollect > 0 && <Item view="brokerage" label={`closing fee${feesToCollect > 1 ? "s" : ""} to collect`} count={feesToCollect} color="bg-[#3DBB8E] text-[#0A0A0A]" />}
                  </div>
                )}
              </div>
            </section>
          );
        })()}

        {/* ============ COUNTERPARTY CONNECTIVITY ============ */}
        {activeView === "dashboard" && (
        <section className="mb-12" data-testid="counterparty-connectivity">
          <div className="mb-5 flex items-center gap-3">
            <Network size={18} className="text-[#B8860B]" />
            <h2 className="text-2xl font-serif">Counterparty Connectivity</h2>
          </div>
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#9A9A90] font-bold">Outbound (Egress) IP for Whitelisting</div>
                <div className="text-[12px] text-[#7A7A70] mt-1 max-w-xl">
                  Source IP that SFTP / webhook deliveries (e.g. Bank of America CashPro) originate from. Give this to the counterparty for their firewall allow-list.
                </div>
              </div>
              <button
                onClick={loadEgressIp}
                disabled={egressLoading}
                className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] border border-[#2A2A22] hover:border-[#B8860B] text-[#B8860B] rounded-md px-3 py-1.5 disabled:opacity-50"
                data-testid="egress-ip-refresh-btn"
              >
                {egressLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
              </button>
            </div>

            {egress?.error ? (
              <div className="mt-4 text-[12px] text-red-400" data-testid="egress-ip-error">Could not fetch egress IP: {egress.error}</div>
            ) : egress ? (
              <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="egress-ip-block">
                <span className="font-mono text-[20px] text-[#ECECE4] bg-[#0A0A0A] border border-[#2A2A22] rounded-md px-4 py-2" data-testid="egress-ip-value">
                  {egress.egress_ip}
                </span>
                <span className="font-mono text-[12px] text-[#9A9A90]">CIDR {egress.cidr}</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(egress.egress_ip); toast.success("Egress IP copied"); }}
                  className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] border border-[#2A2A22] hover:border-[#B8860B] text-[#B8860B] rounded-md px-3 py-1.5"
                  data-testid="egress-ip-copy-btn"
                >
                  <Copy size={12} /> Copy IP
                </button>
              </div>
            ) : (
              <div className="mt-4 text-[12px] text-[#7A7A70]" data-testid="egress-ip-loading">Loading…</div>
            )}

            <div className="mt-4 text-[11px] text-[#8A6D00] bg-[#1A1606] border border-[#3A2F08] rounded-md p-3 leading-relaxed" data-testid="egress-ip-note">
              ⚠️ This is the live IP of the environment you are viewing. Preview and Production run on different infrastructure — give CashPro the value shown on your <span className="font-semibold">production</span> deployment. If it changes between deploys, a static-egress/NAT add-on may be needed to pin a stable IP.
            </div>
          </div>
        </section>
        )}

        {/* ============ PAYLOAD ACCEPTANCE INBOX ============ */}
        {activeView === "acceptance" && (
        <section className="mb-12" data-testid="acceptance-inbox">
          <div className="mb-5 flex items-center gap-3">
            <Inbox size={18} className="text-[#B8860B]" />
            <h2 className="text-2xl font-serif">Payload Acceptance Inbox</h2>
            {inbox.count > 0 && <span className="text-[10px] bg-[#B8860B]/20 text-[#B8860B] px-2 py-0.5 rounded-full font-mono">{inbox.count}</span>}
            <button onClick={loadInbox} disabled={inboxLoading}
              className="ml-auto flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] border border-[#2A2A22] hover:border-[#B8860B] text-[#B8860B] rounded-md px-3 py-1.5 disabled:opacity-50"
              data-testid="inbox-refresh-btn">
              {inboxLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
            </button>
          </div>
          <div className="text-[12px] text-[#7A7A70] mb-4 max-w-2xl">
            Payloads transmitted to your own hosted endpoint (<span className="font-mono text-[#9A9A90]">/api/inbound/payloads</span>) — HMAC-verified, recorded, and acknowledged with a signed acceptance receipt. No counterparty registration required.
          </div>
          {inbox.payloads.length === 0 ? (
            <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="inbox-empty">No payloads accepted yet. Use “Send to my Acceptance Inbox” in the Secure Payload panel.</div>
          ) : (
            <div className="space-y-2">
              {inbox.payloads.map((p) => (
                <div key={p.id} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`inbox-row-${p.acceptance_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-[13px] flex items-center gap-2">
                      <span className="text-[#22c55e]"><CheckCircle2 size={13} /></span>
                      {p.transaction_label || p.transaction_type || "Payload"} · {p.borrower_name || "—"}
                    </div>
                    <div className="text-[11px] text-[#9A9A90] mt-1 font-mono">{p.acceptance_id} · Collateral {p.collateral_id?.slice(0, 8)} · Face {fmtUSD(p.face_value)}{p.borrower_ssn_masked ? ` · SSN ${p.borrower_ssn_masked}` : ""}</div>
                    <div className="text-[10px] text-[#5C5C5C] mt-1 font-mono truncate">SHA-256 {(p.document_hash || p.payload_sha256 || "").slice(0, 24)}… · {new Date(p.received_at).toLocaleString()}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#22c55e] border border-[#22c55e]/30 bg-[#22c55e]/10 rounded-md px-2 py-1">Accepted · Sig Verified</div>
                </div>
              ))}
            </div>
          )}
        </section>
        )}

        {/* ============ BROKERAGE ACCOUNT — INTERNAL TRANSACTIONS (consolidated hub) ============ */}
        {activeView === "brokerage" && (
        <div id="sec-brokerage" className="scroll-mt-32">
          <BrokerageHub apps={[...bApps.pending, ...bApps.resolved]} brokers={brokers} txns={txns}
            onDisburse={disburseClient} onChargeAutopay={chargeAutopay} onMarkPaid={markFeesPaid} onRunAll={runAllAutodebit} onPayCommission={payCommission} />
        </div>
        )}

        {/* ============ BORROWER DATABASE ============ */}
        {activeView === "database" && (
        <BorrowerDatabase apps={[...bApps.pending, ...bApps.resolved]} token={token} onChanged={reloadBApps} />
        )}

        {/* ============ BORROWER FILES (consolidated per-borrower) ============ */}
        {activeView === "files" && (
        <BorrowerFiles apps={[...bApps.pending, ...bApps.resolved]} token={token} onChanged={reloadBApps} />
        )}

        {/* ============ SEUTRADE BROKER APPLICATIONS ============ */}
        {activeView === "broker-apps" && (
        <div id="sec-broker-apps" className="scroll-mt-32">
          <BrokerApplications brokers={brokers} onDecide={decideBroker} decidingId={brDecidingId} token={token} onDelete={deleteBroker} />
        </div>
        )}

        {/* ============ BORROWER APPLICATIONS ============ */}
        {activeView === "borrower-apps" && (
        <section id="sec-borrower-apps" className="mb-12 scroll-mt-32" data-testid="borrower-applications-admin">
          <LenderSubmissionsPanel client={supportClient} />
          <div className="mb-5 flex items-center gap-3">
            <Percent size={18} className="text-[#B8860B]" />
            <h2 className="text-2xl font-serif">Borrower Applications</h2>
            {bApps.pending_count > 0 && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#B8860B]/15 text-[#B8860B] border border-[#B8860B]/40 rounded-sm">{bApps.pending_count} pending</span>
            )}
          </div>
          {bApps.pending.length === 0 ? (
            <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No pending applications.</div>
          ) : (
            <div className="space-y-3">
              {bApps.pending.map((a) => (
                <div key={a.id} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5 flex items-center justify-between" data-testid={`bapp-${a.id}`}>
                  <div>
                    <div className="font-semibold text-[14px]">{a.borrower_name} · <span className="capitalize">{a.transaction_type.replace("_", " ")}</span> · {a.debt_type}</div>
                    {a.transaction_type === "assumption" && (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[#C8A24B]/60 bg-[#C8A24B]/12 px-2.5 py-1 text-[10.5px] font-semibold text-[#E4C97A]" data-testid={`admin-assumption-chip-${a.id}`}>
                        <Repeat size={11} /> Assumption · subject-to{a.mortgage_position && a.mortgage_position !== "first" ? ` · ${a.mortgage_position} lien` : ""}
                      </div>
                    )}
                    <div className="text-[12px] text-[#9A9A90] mt-1">{a.current_lender} · {a.property_or_collateral || "—"}</div>
                    {a.transaction_type === "assumption" ? (
                      <div className="text-[12px] text-[#8A8A8A] mt-1">Assumes existing {fmtUSD(a.current_balance)} note · {fmtUSD(a.refi?.new_monthly_payment)}/mo @ {Number(a.refi?.new_rate || 0).toFixed(2)}% over {a.refi?.term_months} mo · cash-for-keys {fmtUSD(a.relocation_incentive || 500)}</div>
                    ) : (
                      <div className="text-[12px] text-[#8A8A8A] mt-1">Balance {fmtUSD(a.current_balance)} → New 0% note {fmtUSD(a.refi?.new_principal)} · {fmtUSD(a.refi?.new_monthly_payment)}/mo over {a.refi?.term_months} mo{a.refi?.monthly_savings ? ` · saves ${fmtUSD(a.refi.monthly_savings)}/mo` : ""}</div>
                    )}
                    <div className="text-[11px] text-[#5C5C5C] mt-1">{a.borrower_email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => decideApp(a.id, "approve")} disabled={bDecidingId === a.id} data-testid={`bapp-approve-${a.id}`}
                      className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                      {bDecidingId === a.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={13} />} Approve
                    </button>
                    <button onClick={() => decideApp(a.id, "reject")} disabled={bDecidingId === a.id} data-testid={`bapp-reject-${a.id}`}
                      className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#9E2A2B]/50 text-[#FF8585] hover:bg-[#9E2A2B]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                      <XCircle size={13} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {bApps.resolved.length > 0 && (
            <div className="mt-4 text-[12px] text-[#5C5C5C]">{bApps.resolved.length} resolved application(s) — {bApps.resolved.filter((x) => x.status === "approved").length} approved.</div>
          )}
        </section>
        )}

        {/* ============ SERVICING DASHBOARD ============ */}
        {activeView === "dashboard" && <ServicingOverview />}

        {/* ============ LOAN SERVICING & ACCOUNTING ============ */}
        {activeView === "servicing" && (
        <div id="sec-servicing" className="scroll-mt-32">
          <LoanServicing loans={bApps.resolved.filter((x) => x.status === "approved")} onDisburse={disburseClient} onChargeAutopay={chargeAutopay} onRunAll={runAllAutodebit} onDelete={deleteApp} onEsign={staffEsignApp} onEfax={(id, name, saved) => setAppFax({ appId: id, name, saved })} onSendReminders={sendPaymentReminders} onRunCommissions={runCommissionPayouts} onRebaseline={rebaselineSchedules} onSetDueDates={setAllDueDates} onMarkPaidInFull={markPaidInFull} onRefreshBinders={refreshSavedBinders} onToggleWaiveFees={toggleWaiveFees} onSetDiscounting={setDiscounting} />
        </div>
        )}

        {/* ============ DISCOUNTING PIPELINE BOARD ============ */}
        {activeView === "discounting" && (
        <div id="sec-discounting" className="scroll-mt-32">
          <DiscountingBoard loans={bApps.resolved.filter((x) => x.status === "approved")} onSetDiscounting={setDiscounting} />
        </div>
        )}

        {/* ============ DUPLICATE LOANS ============ */}
        {activeView === "servicing" && isOwner && <DuplicateLoans onChanged={reloadBApps} />}

        {/* ============ COMBINE BORROWER MORTGAGES ============ */}
        {activeView === "servicing" && isOwner && <MergeMortgages onChanged={reloadBApps} />}

        {/* ============ FEE WAIVER REPORT ============ */}
        {activeView === "servicing" && isOwner && <WaiverReport client={supportClient} />}

        {activeView === "servicing" && <TransmitQueue loans={bApps.resolved.filter((x) => x.status === "approved")} onEfax={(id, name, saved) => setAppFax({ appId: id, name, saved })} />}

        {/* ============ eFAX TO COUNTERPARTY ============ */}
        {activeView === "fax" && (
        <section id="sec-fax" className="mb-12 scroll-mt-32" data-testid="admin-fax">
          <div className="mb-5 flex items-center gap-3">
            <Send size={18} className="text-[#0B6B4F]" />
            <h2 className="text-2xl font-serif">Submit a Payload</h2>
            <button
              data-testid="open-counterparty-report-btn"
              onClick={() => window.open("/counterparty-report", "_blank")}
              className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#B8892B] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#a67a24] transition-colors"
              title="Print a directory of every counterparty / lender / servicer — name, mailing address, fax and phone"
            >
              <Printer size={14} /> Directory Report
            </button>
            <button
              data-testid="open-mailing-labels-btn"
              onClick={() => window.open("/mailing-labels", "_blank")}
              className="inline-flex items-center gap-2 rounded-md bg-[#0B6B4F] px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#095a42] transition-colors"
              title="Print Avery 5160 envelope mailing labels — each lender/servicer's Loss Mitigation Department, plus SEUTRADE return labels"
            >
              <Printer size={14} /> Mailing Labels
            </button>
          </div>
          <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Pick a deal, enter the counterparty's fax number, check off which documents to include, and submit the payload. Each payload is transmitted with a Payload Transmittal &amp; Legal Notice cover letter documenting the enclosures, the credit tendered, and the action required, followed by the fully-completed Private Negotiable Instrument. Delivery status updates below.</p>
          <FaxPanel client={supportClient} focusTx={faxFocus} />
        </section>
        )}
        {appFax && <AppFaxModal appFax={appFax} client={supportClient} onClose={() => setAppFax(null)} />}

        {/* ============ SUPPORT INBOX ============ */}
        {activeView === "support" && (
        <section id="sec-support" className="mb-12 scroll-mt-32" data-testid="admin-support">
          <div className="mb-5 flex items-center gap-3">
            <Inbox size={18} className="text-[#B8860B]" />
            <h2 className="text-2xl font-serif">Support Inbox</h2>
          </div>
          <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Messages from borrowers and brokers, plus internal <span className="text-[#9DB4E8]">team DMs</span> to other admins. Replies to clients are signed as <span className="text-[#E4C97A]">SEUTRADE FINANCIAL SERVICES — Support</span>; team messages show your name. Use <span className="text-[#E4C97A]">New message</span> to reach a borrower, broker, or team member.</p>
          <SupportMessaging client={supportClient} isAdmin accent="#B8860B" />
        </section>
        )}

        {/* ============ BORROWER ACCESS ACTIVITY ============ */}
        {activeView === "access" && (
        <section id="sec-access-activity" className="mb-12 scroll-mt-32" data-testid="admin-access-activity">
          <AccessActivity client={supportClient} />
        </section>
        )}


        {/* ============ INTERNAL CREDIT RESERVE ============ */}
        {activeView === "reserve" && (
        <section id="sec-reserve" className="mb-12 scroll-mt-32" data-testid="admin-reserve">
          <div className="mb-5 flex items-center gap-3">
            <Wallet size={18} className="text-[#3DBB8E]" />
            <h2 className="text-2xl font-serif">Internal Credit Reserve</h2>
          </div>
          <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">The network credit reserve that backs issuance of vaulted drafts and medallion stamps. Set the reserve and ratio; outstanding drafts commit capacity and issuance is blocked beyond available headroom.</p>
          <CreditReserve client={supportClient} />
        </section>
        )}

        {/* ============ SECURITIES ACCOUNTS ============ */}
        {activeView === "securities" && (
        <section id="sec-securities" className="mb-12 scroll-mt-32" data-testid="admin-securities-section">
          <div className="mb-5 flex items-center gap-3">
            <Landmark size={18} className="text-[#C9A227]" />
            <h2 className="text-2xl font-serif">Securities Accounts</h2>
          </div>
          <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Approve applications to open <span className="text-[#E4C97A]">SEUTRADE Securities Accounts</span> (Letter of Credit, SBLC, Surety Bond, Proof of Funds). Provisioning mints an account number (STF-SEC-2026-00001) and seeds the opening balance. Maintain each account's running USD margin/securities ledger with credit &amp; debit entries.</p>
          <SecuritiesAdmin client={supportClient} />
        </section>
        )}

        {/* ============ VAULTED DRAFTS REGISTRY ============ */}
        {activeView === "vault" && (
        <section id="sec-vault" className="mb-12 scroll-mt-32" data-testid="vault-registry">
          <div className="mb-5 flex items-center gap-3">
            <ShieldCheck size={18} className="text-[#0B6B4F]" />
            <h2 className="text-2xl font-serif">Vaulted Drafts</h2>
            {vault.drafts.filter((d) => d.status === "vaulted").length > 0 && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold bg-[#0B6B4F] text-white rounded-full" data-testid="vault-outstanding-badge">
                {vault.drafts.filter((d) => d.status === "vaulted").length} outstanding
              </span>
            )}
            <button onClick={loadVault} className="ml-auto flex items-center gap-1.5 text-[11px] text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="vault-refresh-btn">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">
            Live ledger of every Private Negotiable Instrument minted into the vault. <span className="text-[#3DBB8E]">Vaulted</span> drafts are outstanding
            and can still be scanned/withdrawn by a bank; <span className="text-[#E4C97A]">Withdrawn</span> drafts have already been redeemed.
          </p>

          {vaultLoading ? (
            <div className="p-8 text-center text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-md"><Loader2 className="animate-spin inline" /></div>
          ) : vault.drafts.length === 0 ? (
            <div className="p-8 text-center text-[#5C5C5C] text-sm bg-[#13130E] border border-[#2A2A22] rounded-md" data-testid="vault-empty">
              No drafts vaulted yet. Use “Vault &amp; Print” on the Private Negotiable Instrument Package to mint a redeemable draft.
            </div>
          ) : (
            <div className="overflow-x-auto bg-[#13130E] border border-[#2A2A22] rounded-md">
              <table className="w-full text-[12px]" data-testid="vault-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.1em] text-[#8A8A8A] border-b border-[#2A2A22]">
                    <th className="text-left px-4 py-3">Draft No.</th>
                    <th className="text-left px-4 py-3">Drawer → Payee</th>
                    <th className="text-right px-4 py-3">Face Value</th>
                    <th className="text-left px-4 py-3">Medallion</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Vaulted</th>
                    <th className="text-left px-4 py-3">Links</th>
                  </tr>
                </thead>
                <tbody>
                  {vault.drafts.map((d) => {
                    const redeemed = d.status === "redeemed";
                    const voided = d.status === "voided";
                    return (
                      <tr key={d.id} className="border-b border-[#1F1F18] hover:bg-[#181811]" data-testid={`vault-row-${d.id}`}>
                        <td className="px-4 py-3 font-mono text-[#ECECE4]">{d.draft_no || "—"}</td>
                        <td className="px-4 py-3 text-[#C9C9C0]">{d.drawer_name || "—"} <span className="text-[#5C5C5C]">→</span> {d.pay_to || "—"}</td>
                        <td className="px-4 py-3 text-right font-mono text-[#3DBB8E]">{d.amount != null ? `$${Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—"}</td>
                        <td className="px-4 py-3 font-mono text-[#E4C97A]">{d.medallion_no || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.1em] rounded-sm border ${voided ? "bg-[#9E2A2B]/15 text-[#FF8585] border-[#9E2A2B]/40" : redeemed ? "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/40" : "bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40"}`} data-testid={`vault-status-${d.id}`}>
                            {voided ? "Voided" : redeemed ? "Withdrawn" : "Vaulted"}
                          </span>
                          {d.accepted && (
                            <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.1em] rounded-sm border bg-[#0e2a6b]/25 text-[#9DB4E8] border-[#0e2a6b]/50" data-testid={`vault-accepted-${d.id}`} title={d.acceptance ? `Accepted by ${d.acceptance.accepting_bank}` : "Bank-accepted"}>
                              ✓ Accepted
                            </span>
                          )}
                          {d.reserve_backing?.backed && (
                            <span className="ml-1.5 inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.1em] rounded-sm border bg-[#0B6B4F]/15 text-[#3DBB8E] border-[#0B6B4F]/40" data-testid={`vault-backed-${d.id}`} title={`Reserve-backed · ${d.reserve_backing.reserve_no} · ratio ${(d.reserve_backing.ratio * 100).toFixed(0)}%`}>
                              ✓ Backed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#8A8A8A]">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <a href={`/verify/${d.redemption_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#9DB4E8] hover:text-[#c3d3f5]" data-testid={`vault-verify-${d.id}`} title="Open the public medallion verification page">
                              <ShieldCheck size={12} /> Verify
                            </a>
                            {!voided && (
                              <a href={`/redeem/${d.redemption_token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#E4C97A] hover:text-[#f0d99a]" data-testid={`vault-redeem-${d.id}`} title="Open the Withdraw / Redeem page (marks the draft withdrawn)">
                                <ExternalLink size={12} /> Withdraw
                              </a>
                            )}
                            {!redeemed && !voided && (
                              <button onClick={() => voidDraft(d.id)} className="inline-flex items-center gap-1 text-[11px] text-[#FF8585] hover:text-[#ff6060]" data-testid={`vault-void-${d.id}`} title="Void this draft — invalidate its redemption token so it can no longer be withdrawn">
                                <Ban size={12} /> Void
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}

        {/* ============ BROKERAGE — CLOSING COSTS & TITLE ============ */}
        {activeView === "brokerage" && <BrokerageAccount loans={bApps.resolved.filter((x) => x.status === "approved")} onMarkPaid={markFeesPaid} onReconcile={reconcileFees} />}

        {/* ============ PAYMENT RECEIPTS (admin/admin-team copy) ============ */}
        {activeView === "brokerage" && <AdminReceipts receipts={receipts} onDelete={deleteReceipt} />}

        {/* ============ OFFER INBOX ============ */}
        {activeView === "offers" && (
        <section id="sec-offers" className="mb-12 scroll-mt-32" data-testid="offer-inbox">
          <div className="mb-5 flex items-center gap-3">
            <Inbox size={18} className="text-[#B8860B]" />
            <h2 className="text-2xl font-serif">Offer Inbox</h2>
            {offers.pending_count > 0 && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] font-bold bg-[#B8860B] text-[#0A0A0A] rounded-full" data-testid="pending-offer-badge">
                {offers.pending_count} pending
              </span>
            )}
          </div>
          <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">
            Counterparty click-to-buy offers submitted from the private marketplace. Accept to lock the buyer of
            record, or reject to strip their signature and re-list the deal.
          </p>

          {offersLoading ? (
            <div className="p-8 text-center text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-md"><Loader2 className="animate-spin inline" /></div>
          ) : offers.pending.length === 0 ? (
            <div className="p-8 text-center text-[#5C5C5C] text-sm bg-[#13130E] border border-[#2A2A22] rounded-md" data-testid="no-pending-offers">
              No pending offers. New counterparty offers will appear here for review.
            </div>
          ) : (
            <div className="space-y-3">
              {offers.pending.map((o) => (
                <div key={o.tx_id} className="bg-[#13130E] border border-[#B8860B]/40 rounded-md p-5" data-testid={`offer-card-${o.tx_id}`}>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 size={14} className="text-[#B8860B]" />
                        <span className="font-semibold text-[15px]">{o.counterparty.name || "Unnamed counterparty"}</span>
                        {o.counterparty.via_market_token_label && (
                          <span className="text-[10px] text-[#5C5C5C]">· via {o.counterparty.via_market_token_label}</span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#8A8A8A] grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mt-3">
                        <div><span className="text-[#5C5C5C]">Signer:</span> {o.counterparty.signer || "—"}{o.counterparty.title ? `, ${o.counterparty.title}` : ""}</div>
                        <div><span className="text-[#5C5C5C]">Email:</span> {o.counterparty.email || "—"}</div>
                        <div><span className="text-[#5C5C5C]">Face value:</span> {fmtUSD(o.face_value)}</div>
                        <div><span className="text-[#5C5C5C]">Purchase price:</span> {fmtUSD(o.purchase_price)}</div>
                        <div><span className="text-[#5C5C5C]">Bank:</span> {o.counterparty.bank_name || "—"}</div>
                        <div><span className="text-[#5C5C5C]">Routing / Acct:</span> {o.counterparty.routing || "—"} / …{o.counterparty.account_last4 || "----"}</div>
                        <div><span className="text-[#5C5C5C]">Path:</span> {o.path}</div>
                        <div><span className="text-[#5C5C5C]">Maturity:</span> {o.maturity_date ? new Date(o.maturity_date).toLocaleDateString() : "—"}</div>
                        <div><span className="text-[#5C5C5C]">Submitted:</span> {o.submitted_at ? new Date(o.submitted_at).toLocaleString() : "—"}</div>
                      </div>
                      {o.transmission ? (
                        <div
                          className={`inline-flex flex-wrap items-center gap-1.5 mt-3 text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-1 rounded-sm ${o.transmission.ok ? "bg-[#0B6B4F]/15 text-[#7DD3B8] border border-[#0B6B4F]/50" : "bg-[#9E2A2B]/20 text-[#FF8585] border border-[#9E2A2B]/40"}`}
                          data-testid={`offer-transmission-${o.tx_id}`}
                        >
                          <Send size={11} />
                          {o.transmission.ok ? "Binder Transmitted" : "Transmit Failed"}
                          {o.transmission.count > 1 ? ` ×${o.transmission.count}` : ""}
                          {o.transmission.ssn_masked ? ` · SSN ${o.transmission.ssn_masked}` : ""}
                          {o.transmission.mtls ? " · mTLS" : ""}
                          <span className="opacity-70 font-normal normal-case">· {o.transmission.timestamp ? new Date(o.transmission.timestamp).toLocaleString() : ""}</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 mt-3 text-[10px] uppercase tracking-[0.1em] text-[#5C5C5C] border border-[#2A2A22] px-2 py-1 rounded-sm" data-testid={`offer-transmission-${o.tx_id}`}>
                          Binder Not Transmitted
                        </div>
                      )}
                    </div>
                    <div className="flex md:flex-col gap-2 shrink-0">
                      <button
                        onClick={() => decideOffer(o.tx_id, "accept")}
                        disabled={decidingId === o.tx_id}
                        className="flex items-center justify-center gap-1.5 bg-[#0B4636] hover:bg-[#0d5b45] disabled:opacity-50 text-[#7DD3B8] border border-[#0B4636] font-bold uppercase tracking-[0.12em] text-[10px] px-4 py-2 rounded-sm"
                        data-testid={`accept-offer-${o.tx_id}`}
                      >
                        {decidingId === o.tx_id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={13} />} Accept
                      </button>
                      <button
                        onClick={() => decideOffer(o.tx_id, "reject")}
                        disabled={decidingId === o.tx_id}
                        className="flex items-center justify-center gap-1.5 bg-[#9E2A2B]/20 hover:bg-[#9E2A2B]/30 disabled:opacity-50 text-[#FF8585] border border-[#9E2A2B]/40 font-bold uppercase tracking-[0.12em] text-[10px] px-4 py-2 rounded-sm"
                        data-testid={`reject-offer-${o.tx_id}`}
                      >
                        <XCircle size={13} /> Reject
                      </button>
                      <a href={`/shared/${o.tx_id}`} target="_blank" rel="noreferrer"
                        className="flex items-center justify-center gap-1.5 text-[#B8860B] hover:text-[#d6a224] text-[10px] uppercase tracking-[0.12em] px-4 py-2"
                        data-testid={`view-deal-${o.tx_id}`}>
                        <ExternalLink size={12} /> Deal
                      </a>
                      <a href={`/?deliver=${o.tx_id}`}
                        className="flex items-center justify-center gap-1.5 text-[#7DD3B8] hover:text-[#a2e6d0] text-[10px] uppercase tracking-[0.12em] px-4 py-2"
                        data-testid={`deliver-payload-${o.tx_id}`}
                        title="Open the Secure Payload delivery panel for this deal">
                        <Send size={12} /> Deliver
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Resolved offers (recent) */}
          {offers.resolved.length > 0 && (
            <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-md overflow-hidden" data-testid="resolved-offers">
              <div className="px-5 py-3 border-b border-[#2A2A22] text-[10px] uppercase tracking-[0.16em] text-[#5C5C5C] font-bold">Recently resolved</div>
              <table className="w-full text-[12px]">
                <tbody>
                  {offers.resolved.map((o) => (
                    <tr key={o.tx_id} className="border-t border-[#2A2A22] first:border-t-0" data-testid={`resolved-row-${o.tx_id}`}>
                      <td className="px-4 py-3 font-semibold">{o.counterparty.name || "—"}</td>
                      <td className="px-4 py-3 text-[#8A8A8A]">{fmtUSD(o.face_value)}</td>
                      <td className="px-4 py-3">
                        {o.settlement_status === "offer_accepted" ? (
                          <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#0B4636]/20 text-[#7DD3B8] border border-[#0B4636]/60 rounded-sm">Accepted</span>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#9E2A2B]/20 text-[#FF8585] border border-[#9E2A2B]/40 rounded-sm">Rejected</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#5C5C5C] text-right">{o.resolved_at ? new Date(o.resolved_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {o.settlement_status === "offer_accepted" && (
                          <button
                            onClick={() => deliverBinder(o.tx_id)}
                            disabled={decidingId === o.tx_id}
                            className="inline-flex items-center gap-1.5 text-[#B8860B] hover:text-[#d6a224] disabled:opacity-50 text-[10px] uppercase tracking-[0.12em] font-bold"
                            title="Re-send the secure closing-binder link to the counterparty"
                            data-testid={`deliver-binder-${o.tx_id}`}
                          >
                            {decidingId === o.tx_id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Re-send binder
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        )}

        {activeView === "tokens" && (<>
        <div className="mb-8">
          <h2 className="text-2xl font-serif">Marketplace Access Tokens</h2>
          <p className="text-[#8A8A8A] text-sm mt-1 max-w-2xl">
            Issue a per-counterparty access token to the password-gated Secondary Market. Each token carries
            an expiration date, a label (counterparty name), and a single-use revoke control. Tokens never
            collide and can be issued in bulk for cold outreach.
          </p>
        </div>

        {/* New token form */}
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-md p-6 mb-8" data-testid="new-token-card">
          <div className="flex items-center gap-2 mb-4">
            <Plus size={14} className="text-[#B8860B]" />
            <h3 className="text-[11px] uppercase tracking-[0.16em] font-bold">Issue new token</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Counterparty / Label</label>
              <input
                type="text"
                placeholder="Acme Capital Partners, LLC"
                value={newForm.label}
                onChange={(e) => setNewForm(f => ({ ...f, label: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                data-testid="new-token-label"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Expires in (days)</label>
              <input
                type="number" min={1} max={365}
                value={newForm.ttl_days}
                onChange={(e) => setNewForm(f => ({ ...f, ttl_days: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                data-testid="new-token-ttl"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Internal note (optional)</label>
              <input
                type="text"
                placeholder="Source: cold-email Jan 2026 · ROI target 9-12%"
                value={newForm.note}
                onChange={(e) => setNewForm(f => ({ ...f, note: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-sm focus:border-[#B8860B] focus:outline-none"
                data-testid="new-token-note"
              />
            </div>
          </div>
          <button
            onClick={issueToken}
            disabled={creating}
            className="mt-4 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[11px] px-5 py-2 rounded-sm flex items-center gap-2"
            data-testid="issue-token-btn"
          >
            {creating ? <><Loader2 size={12} className="animate-spin" /> Issuing…</> : <><KeyRound size={12} /> Issue & Copy Link</>}
          </button>
          {err && <div className="mt-3 text-[12px] text-[#FF8585]">{err}</div>}
        </div>

        {/* Token list */}
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-hidden" data-testid="token-list">
          <div className="px-5 py-3 border-b border-[#2A2A22] text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] font-bold flex justify-between">
            <span>{tokens.length} token{tokens.length !== 1 ? "s" : ""}</span>
            <span>Sorted by newest</span>
          </div>
          {loading ? (
            <div className="p-6 text-center text-[#5C5C5C]"><Loader2 className="animate-spin inline" /></div>
          ) : tokens.length === 0 ? (
            <div className="p-8 text-center text-[#5C5C5C] text-sm">No tokens issued yet. Use the form above to issue the first counterparty invitation.</div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-[#0A0A0A]">
                <tr className="text-[10px] uppercase tracking-[0.12em] text-[#5C5C5C]">
                  <th className="text-left px-4 py-2.5 font-semibold">Counterparty</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Token</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Expires</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Uses</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className="border-t border-[#2A2A22] hover:bg-[#0A0A0A]/50" data-testid={`token-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold flex items-center gap-2">
                        {t.label}
                        {t.self_requested && <span className="px-1.5 py-0.5 text-[8.5px] uppercase tracking-[0.1em] bg-[#B8860B]/15 text-[#B8860B] border border-[#B8860B]/40 rounded-sm">Self-requested</span>}
                      </div>
                      {t.note && <div className="text-[10.5px] text-[#5C5C5C] mt-0.5">{t.note}</div>}
                      {t.suspended_reason && <div className="text-[10.5px] text-[#FF8585] mt-0.5">⚠ {t.suspended_reason}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-[10.5px] text-[#8A8A8A]">{t.token.slice(0, 12)}…</td>
                    <td className="px-4 py-3 text-[#8A8A8A]">{new Date(t.expires_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-[#8A8A8A]">{t.use_count}</td>
                    <td className="px-4 py-3">
                      {t.revoked ? (
                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#9E2A2B]/20 text-[#FF8585] border border-[#9E2A2B]/40 rounded-sm">{t.suspended_reason ? "Suspended" : "Revoked"}</span>
                      ) : !t.active ? (
                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#5C5C5C]/20 text-[#8A8A8A] border border-[#5C5C5C]/40 rounded-sm">Expired</span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#0B4636]/20 text-[#7DD3B8] border border-[#0B4636]/60 rounded-sm">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end items-center gap-2.5">
                        <button onClick={() => copyUrl(t)} title="Copy share URL"
                          className="text-[#B8860B] hover:text-[#d6a224]" data-testid={`copy-url-${t.id}`}>
                          {copiedId === t.id ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                        <a href={mailtoLink(t)} title="Email invitation to counterparty"
                          className="text-[#B8860B] hover:text-[#d6a224]" data-testid={`email-${t.id}`}>
                          <Mail size={14} />
                        </a>
                        <a href={`/market?market_token=${t.token}`} target="_blank" rel="noreferrer" title="Open marketplace as counterparty"
                          className="text-[#B8860B] hover:text-[#d6a224]" data-testid={`preview-${t.id}`}>
                          <ExternalLink size={14} />
                        </a>
                        {t.revoked ? (
                          <button onClick={() => restore(t.id, t.label)} title="Restore access"
                            className="text-[#7DD3B8] hover:text-[#9ee8cc]" data-testid={`restore-${t.id}`}>
                            <RotateCcw size={14} />
                          </button>
                        ) : (
                          <>
                            <button onClick={() => suspend(t.id, t.label)} title="Suspend / restrict (policy violation)"
                              className="text-[#E0A33E] hover:text-[#f0b54e]" data-testid={`suspend-${t.id}`}>
                              <Ban size={14} />
                            </button>
                            <button onClick={() => revoke(t.id, t.label)} title="Revoke permanently"
                              className="text-[#FF8585] hover:text-[#ff6060]" data-testid={`revoke-${t.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                      {copiedId === t.id && <div className="text-[9px] text-[#7DD3B8] mt-1 uppercase tracking-[0.12em]">Copied!</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-6 text-[11px] text-[#5C5C5C] leading-relaxed bg-[#13130E] border border-[#2A2A22] rounded-md p-4">
          <strong className="text-[#B8860B]">How counterparty access works:</strong> Issue a token, copy the share URL or hit the
          mail icon to draft an invitation. The counterparty clicks the link and is automatically authenticated into the
          private marketplace (no signup required). Revoke at any time — counterparty link stops working immediately.
        </div>
        </>)}
      </main>
      </div>
    </div>
  );
}

const ADMIN_NAV_GROUPS = [
  {
    label: "Operations",
    items: [
      { key: "dashboard", label: "Dashboard", Icon: Activity },
      { key: "broker-apps", label: "Broker Applications", Icon: Handshake },
      { key: "borrower-apps", label: "Borrower Applications", Icon: Percent },
      { key: "servicing", label: "Loan Servicing", Icon: FileCheck2 },
      { key: "discounting", label: "Discounting Pipeline", Icon: Layers },
      { key: "brokerage", label: "Brokerage / Transactions", Icon: Repeat },
      { key: "database", label: "Borrower Database", Icon: Database },
      { key: "files", label: "Borrower Files", Icon: FolderOpen },
    ],
  },
  {
    label: "Instruments & Capital",
    items: [
      { key: "vault", label: "Vaulted Drafts", Icon: ShieldCheck },
      { key: "reserve", label: "Credit Reserve", Icon: Wallet },
      { key: "batch-sale", label: "Batch Sale Packages", Icon: Layers },
      { key: "documents", label: "Document Center", Icon: FileCheck2 },
      { key: "securities", label: "Securities Accounts", Icon: Landmark },
    ],
  },
  {
    label: "Communications",
    items: [
      { key: "support", label: "Support Inbox", Icon: Inbox },
      { key: "offers", label: "Offer Inbox", Icon: Handshake },
      { key: "acceptance", label: "Acceptance Inbox", Icon: CheckCircle2 },
      { key: "access", label: "Access Activity", Icon: UserCheck },
      { key: "fax", label: "Submit a Payload", Icon: Send },
    ],
  },
  {
    label: "Administration",
    items: [
      { key: "tokens", label: "Marketplace Tokens", Icon: KeyRound },
      { key: "permissions", label: "Broker Access", Icon: Network },
      { key: "team", label: "Admin Team", Icon: Users },
    ],
  },
];

// Views a SCOPED (non-owner) admin is allowed to see. Everything else is
// owner-only (company-wide financials, entity management, etc.).
const SCOPED_ALLOWED = new Set([
  "borrower-apps", "servicing", "discounting", "broker-apps", "support", "access", "batch-sale", "documents",
]);

function AdminSidebar({ activeView, setActiveView, counts = {}, isOwner = true }) {
  const groups = ADMIN_NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => isOwner || SCOPED_ALLOWED.has(it.key)) }))
    .filter((g) => g.items.length > 0);
  return (
    <aside
      className="w-60 shrink-0 border-r border-[#2A2A22] bg-[#0F0F0A] self-start sticky top-[73px] h-[calc(100vh-73px)] overflow-y-auto py-5"
      data-testid="admin-sidebar"
    >
      {groups.map((group) => (
        <div key={group.label} className="mb-5 px-3">
          <div className="px-2 mb-1.5 text-[9px] uppercase tracking-[0.18em] text-[#5C5C5C] font-bold">{group.label}</div>
          <div className="space-y-0.5">
            {group.items.map(({ key, label, Icon }) => {
              const active = activeView === key;
              const count = counts[key] || 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveView(key)}
                  data-testid={`admin-nav-${key}`}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[12px] transition-colors ${
                    active
                      ? "bg-[#C9A227]/15 text-[#E4C97A] border border-[#C9A227]/40 font-semibold"
                      : "text-[#C9C9C0] border border-transparent hover:bg-white/5 hover:text-[#ECECE4]"
                  }`}
                >
                  <Icon size={15} className={active ? "text-[#E4C97A]" : "text-[#8A8A8A]"} />
                  <span className="flex-1 text-left truncate">{label}</span>
                  {count > 0 && (
                    <span className="px-1.5 py-px text-[9px] leading-none font-bold bg-[#C9A227] text-[#0A0A0A] rounded-full" data-testid={`admin-nav-count-${key}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </aside>
  );
}


const usd0 = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const usd2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const LOAN_LABELS = { mortgage: "Mortgage", auto: "Auto", student_loan: "Student Loan", other: "Consumer" };

function AppFaxModal({ appFax, client, onClose }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [override, setOverride] = useState(false);

  const closingSaved = !!appFax.saved?.closing?.saved_at;
  const draftSaved = !!appFax.saved?.draft?.saved_at;
  const bothSaved = closingSaved && draftSaved;
  const anyStale = appFax.saved?.closing?.stale || appFax.saved?.draft?.stale;
  const missing = [!closingSaved && "Closing Binder", !draftSaved && "Private Negotiable Instrument"].filter(Boolean);
  const blocked = !bothSaved && !override;

  const download = async () => {
    // Faithful, reliable bundling happens in the admin's own browser: open the
    // combined Closing Binder + completed Private Negotiable Instrument print page and let
    // the print dialog Save-as-PDF a single file (both docs, page-broken).
    window.open(`/print-bundle/${appFax.appId}?auto=1`, "_blank");
    setMsg("✓ Opening the combined bundle — use “Save as PDF” in the print dialog to save one file (Closing Binder + completed Private Negotiable Instrument).");
  };

  const sendFax = async () => {
    if (!to.trim()) { setMsg("Enter the counterparty fax number."); return; }
    setBusy("fax"); setMsg("");
    try {
      const r = await client.post(`/admin/fax/send-app/${appFax.appId}`, { to });
      setMsg(`✓ eFax queued (id ${r.data.fax_id}).`);
    } catch (e) {
      setMsg("⚠ " + (e?.response?.data?.detail || "eFax failed."));
    }
    setBusy("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="app-fax-modal" onClick={onClose}>
      <div className="bg-[#13130E] border border-[#2A2A22] rounded-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1">
          <Send size={16} className="text-[#0B6B4F]" />
          <h3 className="text-lg font-serif text-[#ECECE4]">Submit a Payload / Download Bundle</h3>
          <button onClick={onClose} className="ml-auto text-[#8A8A8A] hover:text-[#ECECE4]" data-testid="app-fax-close"><X size={16} /></button>
        </div>
        <p className="text-[12px] text-[#8A8A8A] mb-4">
          {appFax.name ? <span className="text-[#C9C9C0]">{appFax.name}</span> : "This borrower"} · bundles the <strong className="text-[#C9C9C0]">Closing Binder</strong> + the completed <strong className="text-[#C9C9C0]">Private Negotiable Instrument</strong> into one PDF.
        </p>
        {bothSaved ? (
          <div className="mb-4 rounded-md border border-[#0B6B4F]/50 bg-[#0B6B4F]/10 px-3 py-2 text-[11px] text-[#8FE3C0]" data-testid="app-fax-saved-note">
            ✓ Bundling the <strong>exact archived copies on file</strong> — Closing Binder (saved {new Date(appFax.saved.closing.saved_at).toLocaleDateString()}) + Private Negotiable Instrument (saved {new Date(appFax.saved.draft.saved_at).toLocaleDateString()}).
            {anyStale && <span className="block mt-1 text-[#E4B84A]">⚠ A saved copy is out of date (loan edited after save). Use “Refresh copy” in Loan Servicing, or re-save the binder, to send the latest.</span>}
          </div>
        ) : (
          <div className="mb-4 rounded-md border border-[#E4B84A]/50 bg-[#E4B84A]/10 px-3 py-2 text-[11px] text-[#E4C97A]" data-testid="app-fax-fresh-note">
            <div className="font-semibold uppercase tracking-[0.08em] mb-1">⚠ Missing saved {missing.length > 1 ? "copies" : "copy"}: {missing.join(" + ")}</div>
            <div className="text-[#d8c79a]">For a guaranteed archived bundle, open the {missing.join(" and ")} and click <strong>Save</strong> first. Otherwise the missing document{missing.length > 1 ? "s" : ""} will be generated fresh from current loan data.</div>
            <label className="flex items-center gap-2 mt-2 cursor-pointer" data-testid="app-fax-override-label">
              <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="app-fax-override-check" className="accent-[#B8860B]" />
              <span className="text-[#ECECE4]">Send anyway — generate the missing document{missing.length > 1 ? "s" : ""} fresh</span>
            </label>
          </div>
        )}

        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#8A8A8A] mb-1">Counterparty fax number (E.164)</label>
        <FaxPresetPicker client={client} value={to} onChange={setTo} testidPrefix="app-fax-preset" />
        <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+13125551234" data-testid="app-fax-to-input"
          className="w-full bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] mb-4" />

        <div className="flex gap-2">
          <button onClick={sendFax} disabled={!!busy || blocked} data-testid="app-fax-send-btn"
            title={blocked ? "Save both documents (or tick “Send anyway”) first" : "Submit the payload by eFax"}
            className="inline-flex items-center gap-2 bg-[#0B6B4F] hover:bg-[#0a5a43] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-[12px] uppercase tracking-[0.1em] px-4 py-2 rounded-md">
            {busy === "fax" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit Payload
          </button>
          <button onClick={download} disabled={!!busy || blocked} data-testid="app-fax-download-btn"
            title={blocked ? "Save both documents (or tick “Send anyway”) first" : "Save / print the combined bundle"}
            className="inline-flex items-center gap-2 bg-[#2A2A22] hover:bg-[#3A3A2E] disabled:opacity-50 disabled:cursor-not-allowed text-[#E4C97A] font-semibold text-[12px] uppercase tracking-[0.1em] px-4 py-2 rounded-md border border-[#B8860B]/40">
            <Download size={14} /> Save / Print bundle
          </button>
        </div>
        {msg && <div className="mt-3 text-[12px] text-[#C9C9C0]" data-testid="app-fax-msg">{msg}</div>}
      </div>
    </div>
  );
}

function ServicingOverview() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [audit, setAudit] = useState([]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, ar] = await Promise.all([
        axios.get(`${API}/admin/borrower-applications/servicing-overview`),
        axios.get(`${API}/admin/borrower-applications/payout-audit`).catch(() => ({ data: { events: [] } })),
      ]);
      setD(r.data); setAudit(ar.data?.events || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const approvePayout = async (p) => {
    const msg = p.connect_enabled
      ? `Approve and PAY ${p.broker} now? This sends a REAL Stripe transfer of ${usd0(p.total)} (2% ${usd0(p.commission_outstanding)}${p.admin_fee_due ? ` + $${p.admin_fee_due} admin fee` : ""}) to their connected account.`
      : `Approve payout of ${usd0(p.total)} to ${p.broker}? The broker hasn't connected Stripe yet, so it will queue and transfer automatically once they onboard.`;
    if (!window.confirm(msg)) return;
    setBusyId(p.app_id);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${p.app_id}/approve-payout`, {});
      const cs = r.data.commission_payout?.status, fs = r.data.admin_fee_payout?.status;
      const sent = (cs === "transferred" || fs === "transferred");
      toast.success(sent ? `Approved — funds transferred to ${p.broker} via Stripe.` : `Approved — payout unlocked for ${p.broker}; it will transfer once the broker's Stripe payouts are enabled and platform funds clear.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusyId(null); }
  };
  const cancelPayout = async (p) => {
    if (!window.confirm(`Cancel this pending payout to ${p.broker} (${usd0(p.total)})? It will be dismissed from the dashboard and NEVER transferred. This does not claw back anything already sent.`)) return;
    setBusyId(p.app_id);
    try {
      await axios.post(`${API}/admin/borrower-applications/${p.app_id}/cancel-payout`, {});
      toast.success(`Payout to ${p.broker} cancelled — removed from pending.`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusyId(null); }
  };
  const clearMockPayouts = async () => {
    if (!window.confirm("Clear ALL test/mock broker payouts? Every pending payout EXCEPT the real Trend Realty → Mariama F deal will be dismissed and never transferred. Each removal is recorded in the audit log. This cannot be undone.")) return;
    setBusyId("bulk");
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/cancel-all-mock-payouts`, {});
      toast.success(`Cleared ${r.data.cancelled} test payout${r.data.cancelled !== 1 ? "s" : ""}. Protected ${r.data.protected_real_deals} real deal${r.data.protected_real_deals !== 1 ? "s" : ""} (Trend Realty).`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusyId(null); }
  };
  const reversePayout = async (e) => {
    if (!window.confirm(`Cancel / reverse this payout to ${e.broker || "the broker"} for ${e.borrower || "this client"}? Any recorded (not-yet-transferred) amount is voided and the payout is dismissed. Completed Stripe transfers cannot be undone here.`)) return;
    setBusyId(`rev-${e.app_id}`);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${e.app_id}/reverse-payout`, {});
      const blocked = r.data.transferred_blocked || 0;
      toast.success(`Payout reversed — voided ${usd0(r.data.voided_amount || 0)}.${blocked ? ` ${blocked} completed Stripe transfer(s) could not be clawed back — refund those in Stripe.` : ""}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusyId(null); }
  };
  const restorePayout = async (e) => {
    if (!window.confirm(`Restore this payout for ${e.broker || "the broker"} (${e.borrower || "client"})? It returns to Pending Broker Payouts awaiting your approval.`)) return;
    setBusyId(`res-${e.app_id}`);
    try {
      await axios.post(`${API}/admin/borrower-applications/${e.app_id}/restore-payout`, {});
      toast.success("Payout restored to pending — approve it when ready.");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusyId(null); }
  };
  const t = d?.totals || {};
  const dueColor = (n) => (n <= 3 ? "text-[#FF8585]" : n <= 10 ? "text-[#E4C97A]" : "text-[#9A9A90]");
  return (
    <section id="sec-servicing-overview" className="mb-12 scroll-mt-32" data-testid="admin-servicing-overview">
      <div className="mb-5 flex items-center gap-3">
        <Database size={18} className="text-[#C9A227]" />
        <h2 className="font-serif text-2xl">Servicing Dashboard</h2>
        <button onClick={clearMockPayouts} disabled={busyId === "bulk"} data-testid="svc-clear-mock-payouts" className="ml-auto inline-flex items-center gap-1.5 bg-[#13130E] border border-[#7A3A3A]/60 text-[#FF8585] hover:bg-[#9E2A2B]/20 disabled:opacity-40 px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
          <Trash2 size={13} /> Clear test payouts
        </button>
        <button onClick={load} data-testid="servicing-refresh" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9A9A90] hover:text-[#E4C97A]">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <SvcStat label="Due in next 60 days" count={t.upcoming_count} amount={usd0(t.upcoming_amount)} tone="gold" testid="svc-stat-upcoming" />
        <SvcStat label="Overdue notes" count={t.overdue_count} amount={usd0(t.overdue_amount)} tone={t.overdue_count ? "red" : "muted"} testid="svc-stat-overdue" />
        <SvcStat label="Outstanding pre-closing fees" count={t.outstanding_fee_count} amount={usd0(t.outstanding_fee_amount)} tone={t.outstanding_fee_count ? "gold" : "muted"} testid="svc-stat-fees" />
        <SvcStat label="Pending broker payouts" count={t.pending_payout_count} amount={usd0(t.pending_payout_amount)} tone={t.pending_payout_count ? "green" : "muted"} testid="svc-stat-payouts" />
      </div>
      {t.awaiting_over_7d > 0 && (
        <div className="mb-6 flex items-start gap-2.5 bg-[#B8860B]/12 border border-[#B8860B]/50 rounded-md px-4 py-3" data-testid="svc-approval-reminder">
          <AlertTriangle size={16} className="text-[#E4C97A] mt-0.5 shrink-0" />
          <div className="text-[12px] text-[#E4C97A]">
            <strong>{t.awaiting_over_7d} broker payout{t.awaiting_over_7d !== 1 ? "s have" : " has"} been waiting more than 7 days for your approval.</strong> Review the Pending Broker Payouts below — no funds move until you approve each one.
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Overdue */}
        <SvcPanel title="Overdue Notes" testid="svc-overdue-panel">
          {(d?.overdue || []).length === 0 ? <SvcEmpty text="No overdue notes — every loan is current." /> : (d.overdue).map((o) => (
            <div key={o.app_id} className="py-2.5 border-b border-[#1F1F19] last:border-0" data-testid={`svc-overdue-${o.app_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div><div className="text-[13px] text-[#ECECE4]">{o.borrower}</div>
                  <div className="text-[10.5px] text-[#8A8A8A]">{o.count} missed · oldest {o.oldest_due} · {o.channel === "broker" ? "Broker" : "Direct"}{o.autopay ? " · autopay" : ""}</div></div>
                <div className="text-right"><div className="text-[13px] text-[#FF8585] font-semibold">{usd0(o.amount)}</div>
                  <div className="text-[10px] text-[#FF8585]">{o.days_overdue}d overdue</div></div>
              </div>
            </div>
          ))}
        </SvcPanel>
        {/* Upcoming */}
        <SvcPanel title="Upcoming Payments" testid="svc-upcoming-panel">
          {(d?.upcoming || []).length === 0 ? <SvcEmpty text="No payments due in the next 60 days." /> : (d.upcoming).slice(0, 40).map((u) => (
            <div key={u.app_id} className="py-2.5 border-b border-[#1F1F19] last:border-0" data-testid={`svc-upcoming-${u.app_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div><div className="text-[13px] text-[#ECECE4]">{u.borrower}</div>
                  <div className="text-[10.5px] text-[#8A8A8A]">Payment #{u.n} · {u.due_date}{u.autopay ? " · autopay" : ""}</div></div>
                <div className="text-right"><div className="text-[13px] text-[#E4C97A] font-semibold">{usd0(u.amount)}</div>
                  <div className={`text-[10px] ${dueColor(u.days_until)}`}>in {u.days_until}d</div></div>
              </div>
            </div>
          ))}
        </SvcPanel>
        {/* Pending payouts */}
        <SvcPanel title="Pending Broker Payouts" testid="svc-payouts-panel">
          {(d?.pending_payouts || []).length === 0 ? <SvcEmpty text="No broker payouts waiting." /> : (d.pending_payouts).map((p) => (
            <div key={p.app_id} className="py-2.5 border-b border-[#1F1F19] last:border-0" data-testid={`svc-payout-${p.app_id}`}>
              <div className="flex justify-between items-start gap-2">
                <div><div className="text-[13px] text-[#ECECE4]">{p.broker}</div>
                  <div className="text-[10.5px] text-[#8A8A8A]">for {p.borrower}{p.commission_outstanding > 0 ? ` · 2% ${usd0(p.commission_outstanding)}` : ""}{p.admin_fee_due ? `${p.commission_outstanding > 0 ? " + " : " · "}admin ${usd0(p.admin_fee_due)}` : ""}</div>
                  {!p.connect_enabled && <div className="text-[10px] text-[#FF8585] mt-0.5">⚠ broker not Stripe-connected</div>}
                  {p.payout_approved
                    ? <div className="text-[10px] text-[#3DBB8E] mt-0.5">✓ approved</div>
                    : <div className={`text-[10px] mt-0.5 ${p.needs_reminder ? "text-[#FF8585]" : "text-[#E4C97A]"}`}>{p.needs_reminder ? `⏰ waiting ${p.waiting_days}d — needs approval` : "🔒 awaiting your approval — no funds moved"}</div>}</div>
                <div className="text-right"><div className="text-[13px] text-[#3DBB8E] font-semibold">{usd0(p.total)}</div></div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => approvePayout(p)} disabled={busyId === p.app_id} data-testid={`svc-approve-payout-${p.app_id}`}
                  title="Approve this loan and send the broker payout via Stripe"
                  className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 border border-[#0B6B4F]/60 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 disabled:opacity-40 px-3 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                  <Handshake size={12} /> {p.payout_approved ? `Pay ${usd0(p.total)}` : `Approve & Pay ${usd0(p.total)}`}
                </button>
                <button onClick={() => cancelPayout(p)} disabled={busyId === p.app_id} data-testid={`svc-cancel-payout-${p.app_id}`}
                  title="Cancel this payout — it will be dismissed and never transferred"
                  className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#7A3A3A]/60 text-[#FF8585] hover:bg-[#9E2A2B]/20 disabled:opacity-40 px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </SvcPanel>
      </div>
      {/* Pre-closing fee ledger — outstanding fees per transaction */}
      <div className="mt-6 bg-[#13130E] border border-[#2A2A22] rounded-lg p-5" data-testid="svc-fee-ledger">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-3 flex items-center gap-2">
          <Wallet size={13} /> Outstanding Pre-Closing Fees
          {t.outstanding_fee_count > 0 && <span className="px-2 py-0.5 text-[10px] bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm normal-case tracking-normal">{usd0(t.outstanding_fee_amount)} across {t.outstanding_fee_count} transaction{t.outstanding_fee_count !== 1 ? "s" : ""}</span>}
        </div>
        {(d?.outstanding_fees || []).length === 0 ? (
          <SvcEmpty text="No outstanding pre-closing fees — every approved file has paid or been waived." />
        ) : (
          <div className="max-h-[340px] overflow-y-auto pr-1 divide-y divide-[#1F1F19]">
            {(d.outstanding_fees).map((f) => (
              <div key={f.app_id} className="py-2.5 flex justify-between items-center gap-2" data-testid={`svc-fee-${f.app_id}`}>
                <div>
                  <div className="text-[13px] text-[#ECECE4]">{f.borrower}</div>
                  <div className="text-[10.5px] text-[#8A8A8A]">
                    <span className="uppercase">{f.debt_type}</span>{f.transaction_type ? ` · ${f.transaction_type}` : ""} · {f.channel === "broker" ? "Broker" : "Direct"}
                    {f.closed ? <span className="text-[#3DBB8E]"> · e-signed</span> : <span className="text-[#8A8A8A]"> · pre-closing</span>}
                  </div>
                </div>
                <div className="text-[13px] text-[#E4C97A] font-semibold whitespace-nowrap">{usd0(f.amount)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-6 bg-[#13130E] border border-[#2A2A22] rounded-lg p-5" data-testid="svc-payout-audit">
        <div className="text-[11px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-3 flex items-center gap-2">
          <FileText size={13} /> Broker Payout Audit Log
        </div>
        {audit.length === 0 ? (
          <SvcEmpty text="No payout approvals or cancellations recorded yet." />
        ) : (
          <div className="max-h-[300px] overflow-y-auto pr-1">
            {audit.map((e, i) => {
              const tone = e.action === "approved" || e.action === "restored" ? "text-[#3DBB8E]"
                : e.action === "cancelled" || e.action === "reversed" ? "text-[#FF8585]"
                : e.action === "accrued" ? "text-[#7FB2E5]" : "text-[#E4C97A]";
              return (
                <div key={i} className="py-2 border-b border-[#1F1F19] last:border-0 flex justify-between items-start gap-3" data-testid={`svc-audit-row-${i}`}>
                  <div>
                    <div className="text-[12.5px] text-[#ECECE4]">
                      <span className={`uppercase font-bold ${tone}`}>{e.action}</span>
                      <span className="text-[#8A8A8A]"> · {e.broker || "Broker"} for {e.borrower || "—"}</span>
                    </div>
                    <div className="text-[10.5px] text-[#8A8A8A]">by {e.by || "admin"}{e.amount ? ` · ${usd0(e.amount)}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {e.action === "approved" && (
                      <button onClick={() => reversePayout(e)} disabled={busyId === `rev-${e.app_id}`} data-testid={`svc-audit-reverse-${i}`}
                        title="Cancel / reverse this payout — voids any recorded amount not yet transferred"
                        className="inline-flex items-center gap-1 bg-[#13130E] border border-[#7A3A3A]/60 text-[#FF8585] hover:bg-[#9E2A2B]/20 disabled:opacity-40 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] font-bold rounded-sm">
                        <RotateCcw size={11} /> Cancel
                      </button>
                    )}
                    {(e.action === "cancelled" || e.action === "reversed") && (
                      <button onClick={() => restorePayout(e)} disabled={busyId === `res-${e.app_id}`} data-testid={`svc-audit-restore-${i}`}
                        title="Restore this payout back to pending"
                        className="inline-flex items-center gap-1 bg-[#13130E] border border-[#0B6B4F]/60 text-[#3DBB8E] hover:bg-[#0B6B4F]/20 disabled:opacity-40 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] font-bold rounded-sm">
                        <RotateCcw size={11} /> Restore
                      </button>
                    )}
                    <div className="text-[10px] text-[#8A8A8A] whitespace-nowrap">{e.at ? String(e.at).slice(0, 16).replace("T", " ") : ""}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

const SvcStat = ({ label, count, amount, tone, testid }) => {
  const tones = { gold: "border-[#C9A227]/50 bg-[#C9A227]/8", red: "border-[#9E2A2B]/60 bg-[#9E2A2B]/10", green: "border-[#0B6B4F]/60 bg-[#0B6B4F]/12", muted: "border-[#2A2A22] bg-[#0F0F0F]" };
  const txt = { gold: "text-[#E4C97A]", red: "text-[#FF8585]", green: "text-[#3DBB8E]", muted: "text-[#ECECE4]" };
  return (
    <div className={`rounded-lg p-5 border ${tones[tone] || tones.muted}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A]">{label}</div>
      <div className={`text-3xl font-serif mt-1 ${txt[tone] || txt.muted}`}>{count ?? 0}</div>
      <div className="text-[12px] text-[#9A9A90] mt-1">{amount}</div>
    </div>
  );
};

const SvcPanel = ({ title, children, testid }) => (
  <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-5" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-[0.14em] text-[#C9A227] font-bold mb-2">{title}</div>
    <div className="max-h-[360px] overflow-y-auto pr-1">{children}</div>
  </div>
);

const SvcEmpty = ({ text }) => <div className="text-[12px] text-[#8A8A8A] py-4">{text}</div>;

function MergeMortgages({ onChanged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [keepSel, setKeepSel] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/borrower-applications/mergeable`); setGroups(r.data.groups || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const merge = async (g) => {
    const keepId = keepSel[g.key] || g.records[0].app_id;
    const mergeIds = g.records.map((r) => r.app_id).filter((id) => id !== keepId);
    if (!window.confirm(`Combine ${g.records.length} separate loans for ${g.borrower} into ONE consolidated Seutrade mortgage? Their balances are summed into a single 0% note and the other ${mergeIds.length} record(s) are deleted. This cannot be undone.`)) return;
    setBusy(g.key);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${keepId}/merge-mortgages`, { merge_ids: mergeIds });
      toast.success(`Merged into one loan for ${g.borrower} — ${r.data.mortgage_count} mortgages, consolidated principal ${usd0(r.data.consolidated_principal)}.`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  return (
    <section id="sec-merge" className="mb-12 scroll-mt-32" data-testid="admin-merge-mortgages">
      <div className="mb-5 flex items-center gap-3">
        <Layers size={18} className="text-[#C9A227]" />
        <h2 className="font-serif text-2xl">Combine Borrower Mortgages</h2>
        {groups.length > 0 && <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm" data-testid="merge-count-badge">{groups.length}</span>}
        <button onClick={load} data-testid="merge-refresh" className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9A9A90] hover:text-[#E4C97A]"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh</button>
      </div>
      <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Borrowers who have several separate loan files (one per mortgage). Pick the record to keep, then combine the rest into a single consolidated 0% Seutrade note (all balances summed) that shows as one loan in Loan Servicing.</p>
      {groups.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="merge-empty">No borrowers with multiple separate loans — nothing to combine.</div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-4" data-testid={`merge-group-${g.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] text-[#ECECE4] font-semibold">{g.borrower} <span className="text-[#8A8A8A] font-normal">· {g.count} separate loans</span></div>
                <button onClick={() => merge(g)} disabled={busy === g.key} data-testid={`merge-do-${g.key}`}
                  className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 border border-[#0B6B4F]/60 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 disabled:opacity-40 px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
                  <Layers size={12} /> Combine into one
                </button>
              </div>
              <div className="divide-y divide-[#1F1F19]">
                {g.records.map((r) => (
                  <label key={r.app_id} className="py-2 flex items-center gap-3 cursor-pointer text-[11.5px] text-[#9A9A90]" data-testid={`merge-record-${r.app_id}`}>
                    <input type="radio" name={`keep-${g.key}`} checked={(keepSel[g.key] || g.records[0].app_id) === r.app_id} onChange={() => setKeepSel((s) => ({ ...s, [g.key]: r.app_id }))} data-testid={`merge-keep-${r.app_id}`} />
                    <span className="text-[#C9C9C0]">Keep</span>
                    <span>{r.lender} · Acct {r.account} · balance {usd0(r.balance)} · note {usd0(r.principal)}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DuplicateLoans({ onChanged }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // { title, subtitle, records, onConfirm }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/borrower-applications/duplicates`); setGroups(r.data.groups || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const closePreview = () => { setPreview(null); setConfirmText(""); };
  const keepOne = async (keepId, borrower) => {
    setPreviewLoading(true);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${keepId}/dedupe-group?dry_run=true`, {});
      const recs = r.data.would_delete || [];
      if (recs.length === 0) { toast.message("Nothing to delete — no other records match this one."); return; }
      setConfirmText("");
      setPreview({
        title: `Delete ${recs.length} duplicate record${recs.length !== 1 ? "s" : ""} for ${borrower}?`,
        subtitle: "The record you chose will be KEPT. The following will be permanently deleted. This cannot be undone.",
        records: recs,
        onConfirm: async () => {
          setBusy(keepId);
          try {
            const rr = await axios.post(`${API}/admin/borrower-applications/${keepId}/dedupe-group`, {});
            toast.success(`Removed ${rr.data.deleted_count} duplicate${rr.data.deleted_count !== 1 ? "s" : ""} for ${borrower}.`);
            closePreview(); await load(); onChanged && onChanged();
          } catch (e) { toast.error(e.response?.data?.detail || e.message); }
          finally { setBusy(null); }
        },
      });
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setPreviewLoading(false); }
  };
  const delOne = async (id, borrower) => {
    if (!window.confirm(`Permanently delete this single duplicate loan record for ${borrower}? This cannot be undone.`)) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/admin/borrower-applications/${id}`);
      toast.success("Duplicate loan deleted.");
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const removeAll = async () => {
    setPreviewLoading(true);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/dedupe-all?dry_run=true`, {});
      const groupsP = r.data.preview || [];
      const flat = [];
      groupsP.forEach((g) => (g.delete || []).forEach((d) => flat.push({ ...d, borrower: g.borrower })));
      if (flat.length === 0) { toast.message("No duplicates to remove."); return; }
      setConfirmText("");
      setPreview({
        title: `Remove ALL duplicates across ${r.data.groups_affected} borrower${r.data.groups_affected !== 1 ? "s" : ""}?`,
        subtitle: `For each group the best record is kept and the rest deleted — ${flat.length} record${flat.length !== 1 ? "s" : ""} in total. This cannot be undone. Type DELETE to confirm.`,
        requireType: true,
        records: flat,
        onConfirm: async () => {
          setBusy("all");
          try {
            const rr = await axios.post(`${API}/admin/borrower-applications/dedupe-all`, {});
            toast.success(`Removed ${rr.data.deleted_count} duplicate loan${rr.data.deleted_count !== 1 ? "s" : ""} across ${rr.data.groups_cleaned} borrower${rr.data.groups_cleaned !== 1 ? "s" : ""}.`);
            closePreview(); await load(); onChanged && onChanged();
          } catch (e) { toast.error(e.response?.data?.detail || e.message); }
          finally { setBusy(null); }
        },
      });
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setPreviewLoading(false); }
  };
  return (
    <section id="sec-duplicates" className="mb-12 scroll-mt-32" data-testid="admin-duplicate-loans">
      <div className="mb-5 flex items-center gap-3">
        <Copy size={18} className="text-[#C9A227]" />
        <h2 className="font-serif text-2xl">Duplicate Loans</h2>
        {groups.length > 0 && <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm" data-testid="dup-count-badge">{groups.length} group{groups.length !== 1 ? "s" : ""}</span>}
        <div className="ml-auto flex items-center gap-3">
          {groups.length > 0 && (
            <button onClick={removeAll} disabled={busy === "all"} data-testid="dup-remove-all"
              className="inline-flex items-center gap-1.5 bg-[#9E2A2B]/20 border border-[#7A3A3A]/70 text-[#FF8585] hover:bg-[#9E2A2B]/40 disabled:opacity-40 px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
              <Trash2 size={13} /> Remove all duplicates
            </button>
          )}
          <button onClick={load} data-testid="dup-refresh" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9A9A90] hover:text-[#E4C97A]">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Loans that share the same borrower name and financed amount — likely duplicate records. Keep one and remove the rest, or delete individual copies. Once cleaned up, the borrower's account reflects a single loan.</p>
      {groups.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="dup-empty">No duplicate loans found — every borrower has a single record per loan.</div>
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <div key={g.key} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-4" data-testid={`dup-group-${g.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[13px] text-[#ECECE4] font-semibold">{g.borrower} <span className="text-[#8A8A8A] font-normal">· {usd0(g.principal)} financed · {g.count} records</span></div>
              </div>
              <div className="divide-y divide-[#1F1F19]">
                {g.records.map((r) => (
                  <div key={r.app_id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`dup-record-${r.app_id}`}>
                    <div className="text-[11.5px] text-[#9A9A90]">
                      <span className="text-[#C9C9C0]">{r.borrower_email || "—"}</span>
                      {" · "}{r.status}{" · "}created {r.created_at ? String(r.created_at).slice(0, 10) : "—"}
                      {" · balance "}{usd0(r.remaining_balance)}
                      {r.paid_in_full && <span className="text-[#3DBB8E]"> · paid in full</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => keepOne(r.app_id, g.borrower)} disabled={busy === r.app_id || previewLoading} data-testid={`dup-keep-${r.app_id}`}
                        className="inline-flex items-center gap-1 bg-[#0B6B4F]/20 border border-[#0B6B4F]/60 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 disabled:opacity-40 px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                        <Check size={11} /> Keep this, remove others
                      </button>
                      <button onClick={() => delOne(r.app_id, g.borrower)} disabled={busy === r.app_id} data-testid={`dup-del-${r.app_id}`}
                        className="inline-flex items-center gap-1 bg-[#13130E] border border-[#7A3A3A]/60 text-[#FF8585] hover:bg-[#9E2A2B]/20 disabled:opacity-40 px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="dedupe-preview-modal" onClick={closePreview}>
          <div className="bg-[#13130E] border border-[#7A3A3A]/60 rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-[#2A2A22]">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={18} className="text-[#FF8585] mt-0.5 shrink-0" />
                <div>
                  <h3 className="text-[15px] font-serif text-[#ECECE4]" data-testid="dedupe-preview-title">{preview.title}</h3>
                  <p className="text-[12px] text-[#9A9A90] mt-1">{preview.subtitle}</p>
                </div>
              </div>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#FF8585] font-bold mb-2">Will be permanently deleted ({preview.records.length})</div>
              <div className="divide-y divide-[#1F1F19] border border-[#2A2A22] rounded-md">
                {preview.records.map((rec) => (
                  <div key={rec.app_id} className="px-3 py-2.5 flex items-center justify-between gap-3 text-[11.5px]" data-testid={`dedupe-preview-row-${rec.app_id}`}>
                    <div className="text-[#C9C9C0]">
                      {rec.borrower ? <span className="text-[#ECECE4] font-semibold">{rec.borrower} · </span> : null}
                      <span className="uppercase">{rec.debt_type}</span>{rec.transaction_type ? ` · ${rec.transaction_type}` : ""} · {rec.status}
                      <span className="text-[#8A8A8A]"> · created {rec.created_at ? String(rec.created_at).slice(0, 10) : "—"}</span>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <span className="text-[#E4C97A]">{usd0(rec.remaining_balance)}</span>
                      {rec.paid_in_full && <span className="text-[#3DBB8E]"> · paid</span>}
                    </div>
                  </div>
                ))}
              </div>
              {preview.requireType && (
                <div className="mt-4">
                  <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Type <span className="text-[#FF8585] font-bold">DELETE</span> to confirm</label>
                  <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="dedupe-confirm-input"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#FF8585] focus:outline-none" placeholder="DELETE" />
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#2A2A22] flex items-center justify-end gap-2">
              <button onClick={closePreview} data-testid="dedupe-preview-cancel"
                className="px-4 py-2 text-[11px] uppercase tracking-[0.1em] font-bold text-[#C9C9C0] border border-[#2A2A22] hover:bg-white/5 rounded-sm">Cancel</button>
              <button onClick={preview.onConfirm}
                disabled={busy !== null || (preview.requireType && confirmText.trim().toUpperCase() !== "DELETE")}
                data-testid="dedupe-preview-confirm"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] uppercase tracking-[0.1em] font-bold bg-[#9E2A2B]/30 border border-[#7A3A3A] text-[#FF8585] hover:bg-[#9E2A2B]/50 disabled:opacity-40 disabled:cursor-not-allowed rounded-sm">
                {busy !== null ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Permanently delete {preview.records.length}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function TransmitQueue({ loans, onEfax }) {
  const [open, setOpen] = useState(true);
  const rows = (loans || []).filter((l) => l.payload_transmit && l.payload_transmit.status);
  const rank = { queued: 0, blocked_awaiting_signature: 1, transmitted: 2 };
  rows.sort((a, b) => (rank[a.payload_transmit.status] ?? 9) - (rank[b.payload_transmit.status] ?? 9));
  const label = (s) => (s === "transmitted" ? "Transmitted" : s === "queued" ? "Ready to transmit" : "Awaiting e-signature");
  const nm = (l) => l.borrower_name || (l.binder || {}).borrowerName || l.id;
  const readyCount = rows.filter((r) => r.payload_transmit.status === "queued").length;
  return (
    <div className="mb-6 bg-[#13130E] border border-[#2A2A22] rounded-lg" data-testid="transmit-queue">
      <button type="button" onClick={() => setOpen(!open)} data-testid="transmit-queue-toggle" className="w-full flex items-center gap-2.5 px-4 py-3 text-left">
        <Send size={16} className="text-[#E4C97A]" />
        <span className="text-[13px] font-serif text-[#ECECE4] flex-1">Transmit Queue — Payload eFax status</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/40" data-testid="transmit-queue-count">{readyCount} ready</span>
        <ChevronDown size={15} className={`text-[#8A8A8A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#2A2A22] p-4">
          {rows.length === 0 ? (
            <div className="text-[12px] text-[#6f6f66] py-3 text-center" data-testid="transmit-queue-empty">No deals awaiting transmit. Items appear here once closing costs clear.</div>
          ) : (
            <div className="space-y-2">
              {rows.map((l) => {
                const t = l.payload_transmit; const s = t.status;
                const c = s === "transmitted" ? "#3DBB8E" : s === "queued" ? "#E4C97A" : "#FF9E9E";
                return (
                  <div key={l.id} className="flex items-center gap-3 rounded-md border border-[#2A2A22] bg-[#0F0F0A] px-3 py-2" data-testid={`transmit-row-${l.id}`} style={{ borderLeft: `3px solid ${c}` }}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-[#ECECE4] truncate">{nm(l)} <span className="text-[10px] text-[#7f7f76]">· {t.lender || "—"}</span></div>
                      <div className="text-[10px] text-[#8A8A8A] truncate">{t.reason || ""}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.08em] font-bold whitespace-nowrap" style={{ color: c }}>{label(s)}</span>
                    {s !== "transmitted" && (
                      <button type="button" onClick={() => onEfax(l.id, nm(l), !!(l.closing_binder || l.draft_package))} data-testid={`transmit-send-${l.id}`}
                        disabled={s === "blocked_awaiting_signature"}
                        title={s === "blocked_awaiting_signature" ? "Blocked until the borrower e-signs the closing binder" : "Open eFax to send the payload to the lender"}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/15 disabled:opacity-40 rounded px-2.5 py-1 whitespace-nowrap">
                        <Send size={11} /> Send eFax
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function WaiverReport({ client }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await client.get("/admin/borrower-applications/waiver-report"); setRows(r.data.accounts || []); setLoaded(true); }
    catch { /* noop */ }
    setLoading(false);
  };
  const toggle = () => { const n = !open; setOpen(n); if (n && !loaded) load(); };
  const csv = () => {
    const head = "Borrower,Email,Loan,Amount,Waived by,Waived at,Status";
    const lines = rows.map((r) => [r.borrower_name, r.email, r.loan_type, r.amount, r.waived_by, r.waived_at, r.status]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const blob = new Blob([[head, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `waiver_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  return (
    <div className="mb-6 bg-[#13130E] border border-[#2A2A22] rounded-lg" data-testid="waiver-report">
      <button type="button" onClick={toggle} data-testid="waiver-report-toggle"
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left">
        <Percent size={16} className="text-[#5fd3a8]" />
        <span className="text-[13px] font-serif text-[#ECECE4] flex-1">Fee Waiver Report</span>
        {loaded && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#0B6B4F]/20 text-[#5fd3a8] border border-[#0B6B4F]/40" data-testid="waiver-report-count">{rows.length} waived</span>}
        <ChevronDown size={15} className={`text-[#8A8A8A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[#2A2A22] p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] text-[#8A8A8A]">Every account currently set to No Cost — who waived it and when.</p>
            <div className="flex items-center gap-2">
              <button onClick={load} className="p-1.5 rounded hover:bg-white/10 text-[#9A9A90]" data-testid="waiver-report-refresh"><RefreshCw size={13} /></button>
              {rows.length > 0 && <button onClick={csv} data-testid="waiver-report-csv" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] border border-[#B8860B]/40 text-[#E4C97A] hover:bg-[#B8860B]/10 rounded px-2.5 py-1"><Download size={12} /> CSV</button>}
            </div>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-[12px] text-[#8A8A8A] py-4"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-[12px] text-[#6f6f66] py-4 text-center" data-testid="waiver-report-empty">No accounts are currently fee-waived.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11.5px]" data-testid="waiver-report-table">
                <thead><tr className="text-[9px] uppercase tracking-[0.12em] text-[#8A6D1B] border-b border-[#2A2A22]">
                  <th className="text-left py-1.5 pr-3">Borrower</th><th className="text-left pr-3">Loan</th>
                  <th className="text-right pr-3">Amount</th><th className="text-left pr-3">Waived by</th><th className="text-left">When</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-[#1F1F18]" data-testid={`waiver-row-${r.id}`}>
                      <td className="py-2 pr-3 text-[#ECECE4]">{r.borrower_name}<div className="text-[10px] text-[#7f7f76]">{r.email}</div></td>
                      <td className="pr-3 text-[#C9C9C0] capitalize">{r.loan_type}</td>
                      <td className="pr-3 text-right text-[#C9C9C0] font-mono">{usd0(r.amount)}</td>
                      <td className="pr-3 text-[#5fd3a8]">{r.waived_by}</td>
                      <td className="text-[#9A9A90]">{r.waived_at ? new Date(r.waived_at).toLocaleString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



const DISCOUNT_STAGES = [
  { k: "not_started", t: "Not Started", c: "#8A8A82" },
  { k: "prepared", t: "Prepared", c: "#C9A227" },
  { k: "submitted_to_bank", t: "Submitted to Bank", c: "#4FA3E3" },
  { k: "accepted", t: "Accepted", c: "#7C6BE0" },
  { k: "funded", t: "Funded", c: "#3DBB8E" },
  { k: "settled", t: "Settled", c: "#2E8B57" },
  { k: "declined", t: "Declined", c: "#E4646E" },
];

function DiscountingBoard({ loans, onSetDiscounting }) {
  // Approved loans auto-start in the pipeline: no explicit stage → "Prepared"
  // (or "Submitted to Bank" if the loan was already batch-sold/faxed).
  const stageOf = (l) => l.discounting_status || (l.batch_sold ? "submitted_to_bank" : "prepared");
  const byStage = (k) => loans.filter((l) => stageOf(l) === k);
  const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  const amt = (l) => l.refi?.total_debt || l.current_balance || l.binder?.newPrincipal || 0;
  return (
    <div id="discounting-board" data-testid="discounting-board">
      <h2 className="text-2xl font-serif mb-1">Discounting Pipeline</h2>
      <p className="text-[#8A8A8A] text-sm mb-5 max-w-2xl">Every approved loan auto-enters the pipeline at <span className="text-[#C9A227] font-semibold">Prepared</span> the moment its intake is submitted, then advances to <span className="text-[#4FA3E3] font-semibold">Submitted to Bank</span> when the batch is faxed, and on to Accepted/Funded as the bank responds. Override any card with its selector.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {DISCOUNT_STAGES.map((s) => {
          const items = byStage(s.k);
          const total = items.reduce((a, l) => a + amt(l), 0);
          return (
            <div key={s.k} className="rounded-lg border border-[#2A2A22] bg-[#13130E] p-3" data-testid={`discount-col-${s.k}`}>
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#2A2A22]">
                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: s.c }} /><span className="text-[11px] uppercase tracking-[0.12em] font-bold" style={{ color: s.c }}>{s.t}</span></div>
                <span className="text-[11px] text-[#8A8A82]">{items.length} · {money(total)}</span>
              </div>
              <div className="space-y-2 min-h-[40px]">
                {items.map((l) => (
                  <div key={l.id} className="rounded-md bg-[#0F0F0A] border border-[#2A2A22] p-2.5" data-testid={`discount-card-${l.id}`}>
                    <div className="text-[13px] text-[#ECECE4] font-medium truncate">{l.borrower_name || l.binder?.borrowerName || "Borrower"}</div>
                    <div className="text-[11px] text-[#8A8A82]">{money(amt(l))}{l.discounting_bank ? ` · ${l.discounting_bank}` : ""}</div>
                    <select value={stageOf(l)} onChange={(e) => onSetDiscounting(l.id, e.target.value)}
                      data-testid={`discount-card-select-${l.id}`}
                      className="mt-2 w-full bg-[#13130E] border border-[#2A2A22] text-[#ECECE4] text-[11px] rounded-sm px-1.5 py-1">
                      {DISCOUNT_STAGES.map((x) => <option key={x.k} value={x.k}>{x.t}</option>)}
                    </select>
                  </div>
                ))}
                {items.length === 0 && <div className="text-[11px] text-[#4C4C44] italic py-2">None</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function LoanServicing({ loans, onDisburse, onChargeAutopay, onRunAll, onDelete, onEsign, onEfax, onSendReminders, onRunCommissions, onRebaseline, onSetDueDates, onMarkPaidInFull, onRefreshBinders, onToggleWaiveFees, onSetDiscounting }) {
  const pastDue = loans.filter((l) => l.accounting?.status_label === "past_due");
  const totalOutstanding = loans.reduce((s, l) => s + Number(l.accounting?.remaining_balance || 0), 0);
  const totalCollected = loans.reduce((s, l) => s + Number(l.accounting?.paid_amount || 0), 0);
  return (
    <section className="mb-12" data-testid="loan-servicing-admin">
      <div className="mb-5 flex items-center gap-3">
        <Wallet size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Loan Servicing & Accounting</h2>
        {pastDue.length > 0 && (
          <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#9E2A2B]/20 text-[#FF8585] border border-[#9E2A2B]/50 rounded-sm" data-testid="ls-pastdue-badge">{pastDue.length} past due</span>
        )}
        <button onClick={onSendReminders} data-testid="ls-send-reminders"
          className="ml-auto inline-flex items-center gap-1.5 bg-[#13130E] border border-[#0B6B4F]/60 text-[#7DD3B8] hover:bg-[#0B6B4F]/15 px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
          <Mail size={13} /> Send due-payment reminders
        </button>
        <button onClick={onRunCommissions} data-testid="ls-run-commissions"
          className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/15 px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
          <Handshake size={13} /> Run broker payouts
        </button>
        <button onClick={onRebaseline} data-testid="ls-rebaseline-schedules"
          className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#5C6B9E]/60 text-[#9DB4E8] hover:bg-[#5C6B9E]/15 px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
          <RefreshCw size={13} /> Re-baseline 45-day schedules
        </button>
        <button onClick={onSetDueDates} data-testid="ls-set-due-dates"
          className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/15 px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
          <CalendarClock size={13} /> Set payment due date
        </button>
        <button onClick={onRunAll} data-testid="ls-run-autodebit-all"
          className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-3.5 py-2 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
          <CreditCard size={13} /> Run monthly auto-debit
        </button>
      </div>

      {loans.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No active notes yet. Approved applications appear here for servicing.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="ls-summary">
            <Stat label="Active notes" value={loans.length} />
            <Stat label="Collected to date" value={usd0(totalCollected)} good />
            <Stat label="Outstanding balance" value={usd0(totalOutstanding)} />
            <Stat label="Past-due notes" value={pastDue.length} danger={pastDue.length > 0} />
          </div>
          <div className="flex flex-wrap items-center gap-4 mb-2 text-[10px] text-[#8A8A8A]" data-testid="ls-legend">
            <span className="uppercase tracking-[0.12em] text-[#6C6C64] font-bold">Account status:</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#C9A227" }} /> Current</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#FF6B6B" }} /> Past due</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#3DBB8E" }} /> Paid in full</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#5fd3a8" }} /> Fee-waived (No Cost)</span>
          </div>
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
            <style>{`[data-testid="ls-table"]{font-size:10px}[data-testid="ls-table"] th,[data-testid="ls-table"] td{padding:6px 7px}[data-testid="ls-table"] button,[data-testid="ls-table"] a{padding:3px 6px}`}</style>
            <table className="w-full text-[11px]" data-testid="ls-table">
              <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]">
                <tr>
                  <th className="px-4 py-3 text-left">Borrower</th>
                  <th className="px-4 py-3 text-left">Channel</th>
                  <th className="px-4 py-3 text-left">Loan Type</th>
                  <th className="px-4 py-3 text-right">Financed</th>
                  <th className="px-4 py-3 text-right">Monthly</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                  <th className="px-4 py-3 text-left">Next Due</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Disbursement</th>
                  <th className="px-4 py-3 text-left">Auto-Debit</th>
                  <th className="px-4 py-3 text-left">Binder</th>
                </tr>
              </thead>
              <tbody>
                {loans.map((l) => {
                  const ac = l.accounting || {};
                  const st = ac.status_label;
                  const accent = l.fee_waived ? "#5fd3a8" : st === "past_due" ? "#FF6B6B" : st === "paid_in_full" ? "#3DBB8E" : "#C9A227";
                  return (
                    <tr key={l.id} className="border-t border-[#2A2A22] hover:bg-[#0A0A0A]/50 align-top" data-testid={`ls-row-${l.id}`}
                      style={{ borderLeft: `4px solid ${accent}`, backgroundColor: `${accent}0D` }}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#ECECE4]">{l.borrower_name}</div>
                        <div className="text-[10.5px] text-[#5C5C5C]">{l.borrower_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {l.channel === "broker" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] bg-[#C9A227]/15 text-[#C9A227] border border-[#C9A227]/40 rounded-sm" title={l.broker_name} data-testid={`ls-channel-${l.id}`}><Handshake size={10} /> Broker</span>
                        ) : (
                          <span className="text-[10.5px] text-[#5C5C5C]" data-testid={`ls-channel-${l.id}`}>Direct</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#C9C9C0]">{LOAN_LABELS[ac.loan_type] || ac.loan_type || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#C9C9C0]">{usd0(ac.amount_financed)}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#C9C9C0]">{usd2(ac.monthly_payment)}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#3DBB8E]">{usd0(ac.paid_amount)} <span className="text-[#5C5C5C]">({ac.paid_count}/{ac.total_installments})</span></td>
                      <td className="px-4 py-3 text-right font-mono text-[#E4C97A]">{usd0(ac.remaining_balance)}</td>
                      <td className="px-4 py-3 text-[#9A9A90]">{ac.next_due ? `${ac.next_due.due_date} · ${usd2(ac.next_due.amount)}` : "—"}</td>
                      <td className="px-4 py-3">
                        {st === "past_due" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#9E2A2B]/20 text-[#FF8585] border border-[#9E2A2B]/50 rounded-sm" data-testid={`ls-status-${l.id}`}>
                            <AlertTriangle size={11} /> Past due {usd0(ac.past_due_amount)}
                          </span>
                        ) : st === "paid_in_full" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/20 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm" data-testid={`ls-status-${l.id}`}>
                            <CheckCircle2 size={11} /> Paid in full
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#B8860B]/15 text-[#B8860B] border border-[#B8860B]/40 rounded-sm" data-testid={`ls-status-${l.id}`}>Current</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {l.pipeline?.disbursement === "success" ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#3DBB8E]" data-testid={`ls-disb-${l.id}`}><CheckCircle2 size={11} /> Disbursed</span>
                        ) : (
                          <button onClick={() => onDisburse(l.id)} data-testid={`ls-disburse-btn-${l.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/20 hover:bg-[#0B6B4F]/40 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm">
                            <Truck size={11} /> Mark disbursed
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {l.autopay?.card_last4 ? (
                          ac.next_due ? (
                            <button onClick={() => onChargeAutopay(l.id)} data-testid={`ls-autodebit-btn-${l.id}`}
                              title={`${String(l.autopay.card_brand || "card").toUpperCase()} ••••${l.autopay.card_last4}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#B8860B]/15 hover:bg-[#B8860B]/30 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm">
                              <CreditCard size={11} /> Debit ••••{l.autopay.card_last4}
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#3DBB8E]">Paid in full</span>
                          )
                        ) : (
                          <span className="text-[10px] text-[#5C5C5C]" data-testid={`ls-nocard-${l.id}`}>No card on file</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-start gap-1.5 max-w-[360px]">
                          {l.binder_audit?.complete ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.1em] bg-[#0B6B4F]/15 text-[#3DBB8E] border border-[#0B6B4F]/40 rounded-sm" data-testid={`ls-audit-${l.id}`}>
                              <CheckCircle2 size={10} /> Complete
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[9.5px] uppercase tracking-[0.1em] bg-[#9E2A2B]/15 text-[#FF8585] border border-[#9E2A2B]/40 rounded-sm cursor-help"
                              title={`Missing: ${(l.binder_audit?.missing || []).join(", ")}`}
                              data-testid={`ls-audit-${l.id}`}
                            >
                              <AlertTriangle size={10} /> {l.binder_audit?.filled || 0}/{l.binder_audit?.total || 0} fields
                            </span>
                          )}
                          <a href={`/admin/binder/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#B8860B]/15 hover:bg-[#B8860B]/30 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm"
                            title="Open the borrower's closing binder — Print / Save as PDF"
                            data-testid={`ls-binder-btn-${l.id}`}>
                            <Printer size={11} /> Print / PDF
                          </a>
                          <a href={`/closing-binder/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm"
                            title="Open the editable Closing Binder auto-filled with this borrower's data"
                            data-testid={`ls-closing-btn-${l.id}`}>
                            <FileText size={11} /> Closing Binder
                          </a>
                          <a href={`/draft-package/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0e2a6b]/20 hover:bg-[#0e2a6b]/40 text-[#9DB4E8] border border-[#0e2a6b]/50 rounded-sm"
                            title="Open the Private Negotiable Instrument auto-filled with this borrower's data"
                            data-testid={`ls-draft-btn-${l.id}`}>
                            <FileText size={11} /> Private Negotiable Instrument
                          </a>
                          <a href={`/respa-notice/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B4636]/25 hover:bg-[#0B4636]/45 text-[#8FD3B6] border border-[#0B4636]/60 rounded-sm"
                            title="Open a RESPA / Reg X Notice of Error & Request for Information auto-filled with this borrower's data"
                            data-testid={`ls-respa-btn-${l.id}`}>
                            <Scale size={11} /> RESPA Notice
                          </a>
                          <a href={`/funding-commitment/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#B8860B]/15 hover:bg-[#B8860B]/30 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm"
                            title="Open a Funding Commitment / Term Sheet auto-filled with this borrower's data"
                            data-testid={`ls-funding-btn-${l.id}`}>
                            <Landmark size={11} /> Funding Commitment
                          </a>
                          <a href={`/master-funding/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm"
                            title="Open the consolidated Master Funding & Settlement Agreement auto-filled with this borrower's data"
                            data-testid={`ls-master-funding-btn-${l.id}`}>
                            <FileText size={11} /> Master Funding
                          </a>
                          <a href={`/mcc/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm"
                            title="Open a Mortgage Credit Certificate (MCC / IRC §25) document auto-filled with this borrower's data"
                            data-testid={`ls-mcc-btn-${l.id}`}>
                            <FileText size={11} /> Tax Credit (MCC)
                          </a>
                          <a href={`/forbearance/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm"
                            title="Open the Final Forbearance & Conditional Performance Undertaking auto-filled with this borrower's data"
                            data-testid={`ls-forbearance-btn-${l.id}`}>
                            <FileText size={11} /> Forbearance
                          </a>
                          <a href={`/tro-motion/${l.id}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#7A1420]/20 hover:bg-[#7A1420]/40 text-[#E48A94] border border-[#7A1420]/50 rounded-sm"
                            title="Open a state-adaptable Foreclosure TRO & Injunction Motion auto-filled with this borrower's data"
                            data-testid={`ls-tro-btn-${l.id}`}>
                            <FileText size={11} /> TRO Motion
                          </a>
                          <div className="inline-flex items-center gap-1 ml-1" title="Discounting-bank pipeline status" data-testid={`ls-discounting-${l.id}`}>
                            <span className="text-[9px] uppercase tracking-[0.12em] text-[#3DBB8E] font-bold">Discount:</span>
                            <select value={l.discounting_status || "not_started"} onChange={(e) => onSetDiscounting(l.id, e.target.value)}
                              data-testid={`ls-discounting-select-${l.id}`}
                              className="bg-[#0F0F0A] border border-[#0B6B4F]/60 text-[#8FD3B6] text-[10px] rounded-sm px-1.5 py-0.5">
                              {["not_started", "prepared", "submitted_to_bank", "accepted", "funded", "settled", "declined"].map((s) => (
                                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                              ))}
                            </select>
                            {l.discounting_bank ? <span className="text-[9px] text-[#8A8A82]">· {l.discounting_bank}</span> : null}
                          </div>
                          {Array.isArray(l.binder?.mortgages) && l.binder.mortgages.length > 1 && (
                            <div className="w-full mt-1.5 pt-1.5 border-t border-[#2A2A22]" data-testid={`ls-perlien-${l.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="text-[9px] uppercase tracking-[0.14em] text-[#8A8A82] font-bold">Per-lien files (each lender its own closing + funding binder)</div>
                                <button
                                  onClick={() => { (l.binder.mortgages || []).forEach((m, i) => { window.open(`/closing-binder/${l.id}?lien=${i}`, "_blank"); window.open(`/draft-package/${l.id}?lien=${i}`, "_blank"); }); }}
                                  data-testid={`ls-print-all-liens-${l.id}`}
                                  title="Open every lien's Closing Binder + Private Negotiable Instrument in new tabs — Print / Save each as PDF"
                                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.08em] bg-[#B8860B]/15 hover:bg-[#B8860B]/30 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm font-bold">
                                  <Printer size={10} /> Print all lien files
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {l.binder.mortgages.map((m, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 rounded-sm border border-[#3A3A2E] bg-[#161610] px-1.5 py-1">
                                    <span className="text-[9px] uppercase tracking-[0.08em] text-[#C9A227] font-bold pr-0.5" title={m.lender}>{m.position?.[0]?.toUpperCase() || (i + 1)}· {(m.lender || "Lender").slice(0, 16)}</span>
                                    <a href={`/closing-binder/${l.id}?lien=${i}`} target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.06em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#B8C4B8] border border-[#3A3A2E] rounded-sm"
                                      data-testid={`ls-closing-lien-${l.id}-${i}`}>
                                      <FileText size={10} /> Closing
                                    </a>
                                    <a href={`/draft-package/${l.id}?lien=${i}`} target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] uppercase tracking-[0.06em] bg-[#0e2a6b]/20 hover:bg-[#0e2a6b]/40 text-[#9DB4E8] border border-[#0e2a6b]/50 rounded-sm"
                                      data-testid={`ls-draft-lien-${l.id}-${i}`}>
                                      <FileText size={10} /> Draft
                                    </a>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {l.fee_waived && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/25 text-[#5fd3a8] border border-[#0B6B4F]/60 rounded-sm"
                              title="VIP/insider waiver — all fees, balance and commission on this account are $0 (No Cost)."
                              data-testid={`ls-fee-waived-${l.id}`}>
                              ★ No Cost / Waived
                            </span>
                          )}
                          {l.payload_transmit?.status && (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${
                              l.payload_transmit.status === "transmitted" ? "bg-[#0B6B4F]/25 text-[#3DBB8E] border-[#0B6B4F]/60"
                              : l.payload_transmit.status === "queued" ? "bg-[#B8860B]/15 text-[#E4C97A] border-[#B8860B]/50"
                              : "bg-[#9E2A2B]/15 text-[#FF9E9E] border-[#9E2A2B]/50"}`}
                              title={l.payload_transmit.reason || ""}
                              data-testid={`ls-transmit-status-${l.id}`}>
                              <Send size={11} /> {l.payload_transmit.status === "transmitted" ? "Payload transmitted" : l.payload_transmit.status === "queued" ? "Ready to transmit" : "Awaiting e-signature"}
                            </span>
                          )}
                          {l.last_login_at && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#1a1a12] text-[#C9C9B8] border border-[#3a3a2e] rounded-sm"
                              title={`Borrower last signed in ${new Date(l.last_login_at).toLocaleString()}${l.login_count ? ` · ${l.login_count} total sign-ins` : ""}`}
                              data-testid={`ls-last-login-${l.id}`}>
                              <Activity size={11} /> Last login {new Date(l.last_login_at).toLocaleDateString()}{l.login_count ? ` · ${l.login_count}×` : ""}
                            </span>
                          )}
                          {l.draft_package?.saved_at && (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${l.draft_package.stale ? "bg-[#E4B84A]/15 text-[#E4B84A] border-[#E4B84A]/50" : "bg-[#0e2a6b]/25 text-[#9DB4E8] border-[#0e2a6b]/50"}`}
                              title={l.draft_package.stale ? `Draft on file is OUT OF DATE — loan edited after save (${new Date(l.draft_package.saved_at).toLocaleString()}). Re-open the Private Negotiable Instrument and re-save.` : `Completed Private Negotiable Instrument / Funding Binder saved ${new Date(l.draft_package.saved_at).toLocaleString()}`}
                              data-testid={`ls-binder-saved-${l.id}`}>
                              <CheckCircle2 size={11} /> {l.draft_package.stale ? "Draft (stale)" : "Draft on file"}
                            </span>
                          )}
                          {l.closing_binder?.saved_at && (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${l.closing_binder.stale ? "bg-[#E4B84A]/15 text-[#E4B84A] border-[#E4B84A]/50" : "bg-[#0B6B4F]/25 text-[#3DBB8E] border-[#0B6B4F]/50"}`}
                              title={l.closing_binder.stale ? `Closing copy on file is OUT OF DATE — loan edited after save (${new Date(l.closing_binder.saved_at).toLocaleString()}). Re-open the Closing Binder and re-save.` : `Completed Closing Binder (internal copy) saved ${new Date(l.closing_binder.saved_at).toLocaleString()}`}
                              data-testid={`ls-closing-saved-${l.id}`}>
                              <CheckCircle2 size={11} /> {l.closing_binder.stale ? "Closing (stale)" : "Closing on file"}
                            </span>
                          )}
                          {onToggleWaiveFees && (
                            <button onClick={() => onToggleWaiveFees(l.id, l.borrower_name, !!l.fee_waived)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] rounded-sm border ${l.fee_waived ? "bg-[#0B6B4F]/20 text-[#5fd3a8] border-[#0B6B4F]/60" : "bg-[#1a1a12] text-[#C9C9B8] border-[#3a3a2e] hover:bg-white/5"}`}
                              title={l.fee_waived ? "Remove the VIP/insider fee waiver" : "Waive all fees (VIP/insider) — closing costs, balance & commission become $0"}
                              data-testid={`ls-waive-fees-btn-${l.id}`}>
                              {l.fee_waived ? "Un-waive fees" : "Waive fees"}
                            </button>
                          )}
                          {onRefreshBinders && (l.closing_binder?.stale || l.draft_package?.stale) && (
                            <button onClick={() => onRefreshBinders(l.id, l.borrower_name)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#E4B84A]/15 hover:bg-[#E4B84A]/30 text-[#E4B84A] border border-[#E4B84A]/50 rounded-sm"
                              title="Refresh the archived saved copy with the latest loan data and clear the out-of-date flag — without opening the binder"
                              data-testid={`ls-refresh-binder-btn-${l.id}`}>
                              <RefreshCw size={11} /> Refresh copy
                            </button>
                          )}
                          {onEfax && (
                            <button onClick={() => onEfax(l.id, l.borrower_name, { closing: l.closing_binder || null, draft: l.draft_package || null })}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/20 hover:bg-[#0B6B4F]/40 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm"
                              title="eFax / download this deal's Closing Binder + completed Private Negotiable Instrument"
                              data-testid={`ls-efax-btn-${l.id}`}>
                              <Send size={11} /> Submit Payload
                            </button>
                          )}
                          {onMarkPaidInFull && st !== "paid_in_full" && (
                            <button onClick={() => onMarkPaidInFull(l.id, l.borrower_name)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/20 hover:bg-[#0B6B4F]/40 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm"
                              title="Mark every remaining installment paid — borrower's account reads PAID IN FULL with $0.00 owed"
                              data-testid={`ls-mark-paid-btn-${l.id}`}>
                              <CheckCircle2 size={11} /> Mark paid in full
                            </button>
                          )}
                          {onEsign && !(l.signatures && l.signatures.length) && (
                            <button onClick={() => onEsign(l.id, l.borrower_name)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#5C6B9E]/15 hover:bg-[#5C6B9E]/30 text-[#9DB4E8] border border-[#5C6B9E]/40 rounded-sm"
                              title="E-sign the borrower's package on their behalf (final)"
                              data-testid={`ls-esign-btn-${l.id}`}>
                              <Pen size={11} /> E-sign
                            </button>
                          )}
                          {onDelete && (
                            <button onClick={() => onDelete(l.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#9E2A2B]/15 hover:bg-[#9E2A2B]/35 text-[#FF8585] border border-[#9E2A2B]/40 rounded-sm"
                              title="Delete (archive) this transaction — recoverable"
                              data-testid={`ls-delete-btn-${l.id}`}>
                              <Trash2 size={11} /> Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

const Stat = ({ label, value, good, danger }) => (
  <div className="bg-[#13130E] border border-[#2A2A22] rounded-md p-4">
    <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A]">{label}</div>
    <div className={`text-xl font-serif mt-1 ${danger ? "text-[#FF8585]" : good ? "text-[#3DBB8E]" : "text-[#ECECE4]"}`}>{value}</div>
  </div>
);

function printAdminReceipt(r) {
  const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const kindLabel = r.kind === "closing_fees" ? "Closing Costs" : (r.installment_n ? `Monthly Payment #${r.installment_n}` : "Payment");
  const row = (k, v) => (v ? `<tr><td class="k">${k}</td><td>${v}</td></tr>` : "");
  const rows = (r.items || []).map((it) => `<tr><td>${it.label}</td><td style="text-align:right">${money(it.amount)}</td></tr>`).join("");
  const w = window.open("", "_blank", "width=760,height=900");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Receipt ${r.receipt_no}</title>
<style>@page{size:letter;margin:.7in}body{font-family:Georgia,serif;color:#141410;padding:24px 30px}
.wm{letter-spacing:.22em;font-weight:800;color:#0B4636;font-size:15px}.eb{font-size:10px;letter-spacing:.14em;text-transform:uppercase;color:#8a6a10;font-weight:700;margin-top:2px}
h1{font-size:24px;margin:10px 0 4px}hr{border:none;border-top:2px solid #0B4636;margin:8px 0 16px}
table{width:100%;border-collapse:collapse;font-size:13px;margin:6px 0}td{border:1px solid #e2e2da;padding:7px 10px}.k{background:#f6f6f1;font-weight:600;width:42%}
.amt{text-align:right;font-size:17px;font-weight:800;margin-top:12px}.note{font-size:11px;color:#6c6c64;margin-top:18px;line-height:1.5}
.btn{margin:16px 0}@media print{.btn{display:none}}</style></head><body>
<div class="btn"><button onclick="window.print()">Print / Save PDF</button></div>
<div class="wm">SEUTRADE FINANCIAL SERVICES</div><div class="eb">Private Lender Services · Payment Receipt</div>
<h1>Payment Receipt</h1><hr/>
<table>${row("Receipt No.", r.receipt_no)}${row("Date", r.issued_at)}${row("Seutrade Account No.", r.seutrade_account_no)}${row("Payment For", kindLabel)}${row("Borrower", r.borrower_name)}${row("Email", r.borrower_email)}${row("Phone", r.borrower_phone)}${row("Address", r.borrower_address)}${row("Property / Collateral", r.property)}${row("Payment Method", r.payment_method)}${row("Broker", r.broker_name)}<tr><td class="k">Reference</td><td style="font-family:monospace;font-size:11px">${String(r.ref || "").slice(0, 60)}</td></tr></table>
${rows ? `<table>${rows}</table>` : ""}
<div class="amt">Amount Paid: ${money(r.amount)} USD</div>
<p class="note">This receipt confirms funds received by Seutrade Financial Services for the item(s) above. Official electronic receipt — no signature required.</p>
</body></html>`);
  w.document.close();
}

function AdminReceipts({ receipts, onDelete }) {
  const rows = receipts || [];
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const installmentCount = rows.filter((r) => r.installment_n).length;
  return (
    <section className="mb-12" data-testid="admin-receipts">
      <div className="mb-5 flex items-center gap-3">
        <FileCheck2 size={18} className="text-[#C9A227]" />
        <h2 className="text-2xl font-serif">Payment Receipts</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Stat label="Receipts issued" value={rows.length} />
        <Stat label="Total received" value={usd0(total)} good />
        <Stat label="Monthly payments received" value={installmentCount} />
      </div>
      <div className="text-[11px] text-[#8A8A82] mb-3">A receipt appears here only when a payment actually clears (closing costs or a monthly installment). If no monthly payments have been made, only closing-cost receipts (if any) should be listed. Use Delete to remove any receipt issued in error.</div>
      {rows.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No payment receipts yet. They appear here when a borrower or broker pays closing costs or a monthly installment.</div>
      ) : (
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="admin-receipts-table">
            <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]">
              <tr>
                <th className="px-4 py-3 text-left">Receipt No.</th>
                <th className="px-4 py-3 text-left">Seutrade Acct</th>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Borrower</th>
                <th className="px-4 py-3 text-left">Mailing Address</th>
                <th className="px-4 py-3 text-left">For</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-left">Emailed</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#2A2A22] hover:bg-[#0A0A0A]/50" data-testid={`admin-receipt-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-[10.5px] text-[#9DB4E8]">{r.receipt_no}</td>
                  <td className="px-4 py-3 font-mono text-[10.5px] text-[#C9A227]">{r.seutrade_account_no || "—"}</td>
                  <td className="px-4 py-3 text-[#9A9A90]">{r.issued_at}</td>
                  <td className="px-4 py-3"><div className="text-[#ECECE4]">{r.borrower_name}</div>{r.borrower_email && <div className="text-[10px] text-[#5C5C5C]">{r.borrower_email}</div>}{r.broker_name && <div className="text-[10px] text-[#5C5C5C]">via {r.broker_name}</div>}</td>
                  <td className="px-4 py-3 text-[#9A9A90] max-w-[220px]">{r.borrower_address || "—"}</td>
                  <td className="px-4 py-3 text-[#9A9A90]">{r.kind === "closing_fees" ? "Closing Costs" : (r.installment_n ? `Payment #${r.installment_n}` : "Payment")}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#E4C97A]">{usd0(r.amount)}</td>
                  <td className="px-4 py-3 text-[10px]">{r.emailed ? <span className="text-[#3DBB8E]">✉ sent</span> : <span className="text-[#5C5C5C]">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => printAdminReceipt(r)} data-testid={`admin-receipt-view-${r.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#2A2A22] hover:bg-[#3A3A2E] text-[#E4C97A] border border-[#3A3A2E] rounded-sm">View / Print</button>
                      <button onClick={() => onDelete && onDelete(r)} data-testid={`admin-receipt-delete-${r.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#3a1414] hover:bg-[#5a1c1c] text-[#FF8585] border border-[#7a2a2a] rounded-sm"><Trash2 size={11} /> Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BrokerageAccount({ loans, onMarkPaid, onReconcile }) {
  const rows = loans.filter((l) => (l.closing_fees?.total || 0) > 0);
  const collected = rows.filter((l) => l.closing_fees?.paid).reduce((s, l) => s + Number(l.closing_fees.total || 0), 0);
  const outstanding = rows.filter((l) => !l.closing_fees?.paid).reduce((s, l) => s + Number(l.closing_fees.total || 0), 0);
  return (
    <section className="mb-12" data-testid="brokerage-account-admin">
      <div className="mb-5 flex items-center gap-3">
        <Building2 size={18} className="text-[#C9A227]" />
        <h2 className="text-2xl font-serif">Brokerage Account — Closing Costs & Title</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <Stat label="Fee income collected" value={usd0(collected)} good />
        <Stat label="Closing costs outstanding" value={usd0(outstanding)} />
        <Stat label="Files at closing" value={rows.length} />
      </div>
      {rows.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No approved files with closing costs yet.</div>
      ) : (
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="brokerage-table">
            <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]">
              <tr>
                <th className="px-4 py-3 text-left">Borrower</th>
                <th className="px-4 py-3 text-left">Fee breakdown</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-left">Title Ins.</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => {
                const cf = l.closing_fees || {};
                return (
                  <tr key={l.id} className="border-t border-[#2A2A22] hover:bg-[#0A0A0A]/50" data-testid={`brokerage-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#ECECE4]">{l.borrower_name}</div>
                      <div className="text-[10.5px] text-[#5C5C5C]">{l.borrower_email}</div>
                    </td>
                    <td className="px-4 py-3 text-[10.5px] text-[#9A9A90]">{(cf.items || []).map((i) => `${i.label} $${i.amount}`).join(" · ")}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#E4C97A]">{usd0(cf.total)}</td>
                    <td className="px-4 py-3">{cf.title_applies ? (cf.title_insurance_accepted ? <span className="text-[#3DBB8E] text-[10px]">✓ Accepted</span> : <span className="text-[#8A8A8A] text-[10px]">Pending</span>) : <span className="text-[#5C5C5C] text-[10px]">N/A</span>}</td>
                    <td className="px-4 py-3">{cf.paid
                      ? <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/20 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm" data-testid={`brokerage-paid-${l.id}`}><CheckCircle2 size={11} /> Collected</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#B8860B]/15 text-[#B8860B] border border-[#B8860B]/40 rounded-sm">Due at closing</span>}</td>
                    <td className="px-4 py-3">{cf.paid
                      ? <span className="text-[10px] text-[#5C5C5C]">—</span>
                      : <div className="flex items-center gap-1.5">
                          <button onClick={() => onMarkPaid(l.id)} data-testid={`brokerage-mark-paid-${l.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0B6B4F]/20 hover:bg-[#0B6B4F]/40 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm">Process / Mark collected</button>
                          <button onClick={() => onReconcile(l.id)} data-testid={`brokerage-reconcile-${l.id}`} title="Check Stripe for a completed payment and mark it collected"
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] bg-[#0e2a6b]/30 hover:bg-[#0e2a6b]/50 text-[#9DB4E8] border border-[#0e2a6b]/60 rounded-sm">Verify w/ Stripe</button>
                        </div>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}


const STATUS_CLS = {
  submitted: "text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10",
  approved: "text-[#3DBB8E] border-[#0B6B4F]/60 bg-[#0B6B4F]/15",
  rejected: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15",
};

const cfoTip = { backgroundColor: "#0A0A0A", border: "1px solid #2A2A22", borderRadius: 6, fontSize: 11, color: "#ECECE4" };
const usdK = (n) => { const v = Number(n || 0); return v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${Math.round(v)}`; };

function CFOAnalytics({ apps, txns, brokers, onPayCommission }) {
  const approved = apps.filter((a) => a.status === "approved");
  const pending = apps.filter((a) => a.status === "submitted" || a.status === "pending");
  const tFace = txns.reduce((s, t) => s + Number(t.face_value || 0), 0);
  const tPurchase = txns.reduce((s, t) => s + Number(t.purchase_price || 0), 0);
  const tDiscount = tFace - tPurchase;
  const financed = approved.reduce((s, a) => s + Number(a.accounting?.amount_financed || a.current_balance || 0), 0);
  const collected = approved.reduce((s, a) => s + Number(a.accounting?.paid_amount || 0), 0);
  const outstanding = approved.reduce((s, a) => s + Number(a.accounting?.remaining_balance || 0), 0);
  const pastDue = approved.filter((a) => a.accounting?.status_label === "past_due");
  const pastDueAmt = pastDue.reduce((s, a) => s + Number(a.accounting?.past_due_amount || 0), 0);
  const brokerApproved = approved.filter((a) => a.channel === "broker");
  const directApproved = approved.filter((a) => a.channel !== "broker");
  const commAccrued = brokerApproved.reduce((s, a) => a.payout_cancelled ? s : s + Number(a.commission?.accrued ?? (Number(a.accounting?.amount_financed || a.current_balance || 0) * 0.02)), 0);
  const commPaid = brokerApproved.reduce((s, a) => s + Number(a.commission?.paid || 0), 0);
  const commPayables = brokerApproved.filter((a) => !a.payout_cancelled && Number(a.commission?.outstanding || 0) > 0);
  const bookValue = tFace + financed;
  const collectRate = financed > 0 ? (collected / financed) * 100 : 0;

  const now = new Date();
  const months = [];
  for (let i = 7; i >= 0; i--) { const d = new Date(now.getFullYear(), now.getMonth() - i, 1); months.push({ key: d.toISOString().slice(0, 7), label: d.toLocaleString("en-US", { month: "short" }) }); }
  const mkey = (s) => { try { return new Date(s).toISOString().slice(0, 7); } catch { return ""; } };
  const volume = months.map((m) => {
    const deals = txns.filter((t) => mkey(t.execution_date || t.created_at) === m.key);
    const loans = approved.filter((a) => mkey(a.created_at) === m.key);
    return { label: m.label, "Note deals": Math.round(deals.reduce((s, t) => s + Number(t.face_value || 0), 0)), "0% loans": Math.round(loans.reduce((s, a) => s + Number(a.accounting?.amount_financed || a.current_balance || 0), 0)) };
  });
  const discountTrend = months.map((m) => {
    const deals = txns.filter((t) => mkey(t.execution_date || t.created_at) === m.key && Number(t.face_value) > 0);
    const avg = deals.length ? deals.reduce((s, t) => s + ((Number(t.face_value) - Number(t.purchase_price || 0)) / Number(t.face_value)) * 100, 0) / deals.length : 0;
    return { label: m.label, "Avg discount %": Number(avg.toFixed(2)) };
  });
  const collVsOut = [{ name: "Collected", value: Math.round(collected) }, { name: "Outstanding", value: Math.round(outstanding) }, { name: "Past-due", value: Math.round(pastDueAmt) }];
  const paidPipeline = [{ name: "Funded", value: approved.length }, { name: "Pipeline", value: pending.length }];
  const channelSplit = [{ name: "Direct", value: directApproved.length }, { name: "Broker", value: brokerApproved.length }];
  const commData = [{ name: "Accrued", value: Math.round(commAccrued) }, { name: "Paid", value: Math.round(commPaid) }];

  const Panel = ({ title, children, testid }) => (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-md p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-3">{title}</div>
      {children}
    </div>
  );

  return (
    <div data-testid="cfo-analytics" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Stat label="Total book value" value={usdK(bookValue)} />
        <Stat label="Note face notional" value={usdK(tFace)} />
        <Stat label="Discount volume" value={usdK(tDiscount)} good />
        <Stat label="0% loans financed" value={usdK(financed)} />
        <Stat label="Collections" value={usdK(collected)} good />
        <Stat label="Outstanding" value={usdK(outstanding)} />
        <Stat label="Past-due exposure" value={usdK(pastDueAmt)} danger={pastDueAmt > 0} />
        <Stat label="Broker comm. accrued" value={usdK(commAccrued)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Panel title="Monthly volume trend (note face + loans financed)" testid="cfo-volume-chart">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={volume} margin={{ top: 6, right: 8, left: -6, bottom: 0 }}>
              <defs>
                <linearGradient id="gNotes" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C9A227" stopOpacity={0.5} /><stop offset="100%" stopColor="#C9A227" stopOpacity={0.03} /></linearGradient>
                <linearGradient id="gLoans" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3DBB8E" stopOpacity={0.5} /><stop offset="100%" stopColor="#3DBB8E" stopOpacity={0.03} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#22221B" />
              <XAxis dataKey="label" tick={{ fill: "#8A8A8A", fontSize: 11 }} stroke="#2A2A22" />
              <YAxis tick={{ fill: "#8A8A8A", fontSize: 10 }} stroke="#2A2A22" tickFormatter={usdK} width={48} />
              <Tooltip contentStyle={cfoTip} formatter={(v) => usdK(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="Note deals" stroke="#C9A227" fill="url(#gNotes)" strokeWidth={2} />
              <Area type="monotone" dataKey="0% loans" stroke="#3DBB8E" fill="url(#gLoans)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Average discount-rate trend (note sales)" testid="cfo-discount-chart">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={discountTrend} margin={{ top: 6, right: 8, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#22221B" />
              <XAxis dataKey="label" tick={{ fill: "#8A8A8A", fontSize: 11 }} stroke="#2A2A22" />
              <YAxis tick={{ fill: "#8A8A8A", fontSize: 10 }} stroke="#2A2A22" width={40} tickFormatter={(v) => `${v}%`} />
              <Tooltip contentStyle={cfoTip} formatter={(v) => `${v}%`} />
              <Line type="monotone" dataKey="Avg discount %" stroke="#6C8CD5" strokeWidth={2.5} dot={{ r: 3, fill: "#6C8CD5" }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Panel title="Collections vs outstanding vs past-due" testid="cfo-collections-chart">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={collVsOut} margin={{ top: 6, right: 8, left: -6, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#22221B" />
              <XAxis dataKey="name" tick={{ fill: "#8A8A8A", fontSize: 10 }} stroke="#2A2A22" />
              <YAxis tick={{ fill: "#8A8A8A", fontSize: 10 }} stroke="#2A2A22" width={48} tickFormatter={usdK} />
              <Tooltip contentStyle={cfoTip} formatter={(v) => usdK(v)} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {collVsOut.map((e, i) => <Cell key={i} fill={["#3DBB8E", "#C9A227", "#FF8585"][i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-[10.5px] text-[#8A8A8A] mt-2">Collection rate: <span className="text-[#3DBB8E] font-mono">{collectRate.toFixed(1)}%</span></div>
        </Panel>
        <Panel title="Funded vs pipeline" testid="cfo-pipeline-chart">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={paidPipeline} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3}>
                {paidPipeline.map((e, i) => <Cell key={i} fill={["#3DBB8E", "#C9A227"][i]} />)}
              </Pie>
              <Tooltip contentStyle={cfoTip} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Broker vs direct (funded)" testid="cfo-channel-chart">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={channelSplit} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3}>
                {channelSplit.map((e, i) => <Cell key={i} fill={["#6C8CD5", "#C9A227"][i]} />)}
              </Pie>
              <Tooltip contentStyle={cfoTip} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="Broker commissions — accrued vs paid" testid="cfo-commissions-chart">
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={commData} layout="vertical" margin={{ top: 4, right: 16, left: 20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#22221B" horizontal={false} />
            <XAxis type="number" tick={{ fill: "#8A8A8A", fontSize: 10 }} stroke="#2A2A22" tickFormatter={usdK} />
            <YAxis type="category" dataKey="name" tick={{ fill: "#C9C9C0", fontSize: 11 }} stroke="#2A2A22" width={64} />
            <Tooltip contentStyle={cfoTip} formatter={(v) => usdK(v)} cursor={{ fill: "#ffffff08" }} />
            <Bar dataKey="value" radius={[0, 3, 3, 0]}>
              {commData.map((e, i) => <Cell key={i} fill={["#C9A227", "#3DBB8E"][i]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <div className="text-[10px] text-[#5C5C5C] mt-1">Yr-1 accrual estimated at 2% of financed principal for broker-channel notes. "Paid" reflects recorded commission disbursements.</div>
      </Panel>

      <Panel title="Broker commission payables — pay accrued commissions" testid="cfo-commission-payables">
        {commPayables.length === 0 ? (
          <div className="text-[12px] text-[#5C5C5C]">No broker commissions currently outstanding. Accrued commissions appear here as broker-channel notes age.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="cfo-payables-table">
              <thead className="text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]"><tr>
                <th className="text-left py-2">Borrower · Broker</th>
                <th className="text-right py-2">Note / mo</th>
                <th className="text-right py-2">Comm / mo</th>
                <th className="text-right py-2">Comm / yr</th>
                <th className="text-right py-2">Accrued</th>
                <th className="text-right py-2">Paid</th>
                <th className="text-right py-2">Outstanding</th>
                <th className="text-right py-2">Action</th>
              </tr></thead>
              <tbody>
                {commPayables.map((a) => {
                  const c = a.commission || {};
                  const bp = a.broker_payout || {};
                  const canAuto = bp.payouts_enabled;
                  return (
                    <tr key={a.id} className="border-t border-[#2A2A22]" data-testid={`cfo-payable-row-${a.id}`}>
                      <td className="py-2">
                        <div className="text-[#ECECE4] font-semibold">{a.borrower_name}</div>
                        <div className="text-[10px] text-[#5C5C5C]">{c.broker_name || a.broker_name || "Broker"} · {c.paid_installments ?? c.elapsed_months ?? 0} payment{(c.paid_installments ?? c.elapsed_months ?? 0) !== 1 ? "s" : ""} · {Math.round((c.current_rate || 0.02) * 100)}% tier</div>
                        <div className="mt-1">
                          {canAuto ? (
                            <span data-testid={`cfo-payout-badge-${a.id}`} className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.08em] font-bold text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-full px-2 py-0.5">✓ Payouts connected</span>
                          ) : bp.connected ? (
                            <span data-testid={`cfo-payout-badge-${a.id}`} title="Broker started Stripe onboarding but payouts aren't enabled yet" className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.08em] font-bold text-[#E4C97A] border border-[#B8860B]/40 rounded-full px-2 py-0.5">⏳ Onboarding pending</span>
                          ) : (
                            <span data-testid={`cfo-payout-badge-${a.id}`} title="Broker has not connected a Stripe payout account — payment will be recorded, not auto-transferred" className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.08em] font-bold text-[#C77] border border-[#7A3A3A]/50 rounded-full px-2 py-0.5">⚠ Not onboarded</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 text-right font-mono text-[#C9C9C0]">{usd0(c.monthly_payment)}</td>
                      <td className="py-2 text-right font-mono text-[#C9A227]" data-testid={`cfo-comm-monthly-${a.id}`}>{usd2(c.monthly_commission)}</td>
                      <td className="py-2 text-right font-mono text-[#C9A227]" data-testid={`cfo-comm-annual-${a.id}`}>{usd0(c.annual_commission)}</td>
                      <td className="py-2 text-right font-mono text-[#C9A227]">{usd0(c.accrued)}</td>
                      <td className="py-2 text-right font-mono text-[#3DBB8E]">{usd0(c.paid)}</td>
                      <td className="py-2 text-right font-mono text-[#E4C97A] font-bold">{usd0(c.outstanding)}</td>
                      <td className="py-2 text-right">
                        <button onClick={() => onPayCommission && onPayCommission(a.id, canAuto)} data-testid={`cfo-pay-commission-${a.id}`}
                          title={canAuto ? "Sends a real Stripe transfer to the broker's connected account" : "Records the payment — broker hasn't connected Stripe payouts yet"}
                          className={`px-3 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm border ${canAuto ? "bg-[#0B6B4F]/20 border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/40" : "bg-[#2A2A22] border-[#3A3A30] text-[#C9A227] hover:bg-[#33332A]"}`}>{canAuto ? `Pay & Transfer ${usd0(c.outstanding)}` : `Record ${usd0(c.outstanding)}`}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function BrokerageHub({ apps, brokers, txns, onDisburse, onChargeAutopay, onMarkPaid, onRunAll, onPayCommission }) {
  const [tab, setTab] = useState("transactions");
  const approved = apps.filter((a) => a.status === "approved");
  const financed = approved.reduce((s, a) => s + Number(a.accounting?.amount_financed || a.current_balance || 0), 0);
  const collectedInstallments = approved.reduce((s, a) => s + Number(a.accounting?.paid_amount || 0), 0);
  const feeCollected = approved.filter((a) => a.closing_fees?.paid).reduce((s, a) => s + Number(a.closing_fees.total || 0), 0);
  const feeOutstanding = approved.filter((a) => !a.closing_fees?.paid).reduce((s, a) => s + Number(a.closing_fees?.total || 0), 0);
  const remaining = approved.reduce((s, a) => s + Number(a.accounting?.remaining_balance || 0), 0);
  const brokerApproved = approved.filter((a) => a.channel === "broker");
  const commissionsYr1 = brokerApproved.reduce((s, a) => s + Number(a.accounting?.amount_financed || a.current_balance || 0) * 0.02, 0);
  const activeBrokers = (brokers?.resolved || []).filter((b) => b.status === "approved").length;
  const brokerList = [...(brokers?.pending || []), ...(brokers?.resolved || [])];

  const TabBtn = ({ k, label }) => (
    <button onClick={() => setTab(k)} data-testid={`brokerage-tab-${k}`}
      className={`px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm border transition-colors ${tab === k ? "bg-[#C9A227] text-[#0A0A0A] border-[#C9A227]" : "bg-[#0F0F0F] text-[#C9C9C0] border-[#2A2A22] hover:border-[#C9A227]/60"}`}>{label}</button>
  );

  return (
    <section className="mb-12" data-testid="brokerage-hub-admin">
      <div className="mb-5 flex items-center gap-3 flex-wrap">
        <Building2 size={18} className="text-[#C9A227]" />
        <h2 className="text-2xl font-serif">Brokerage Account — Internal Transactions</h2>
        <span className="text-[11px] text-[#5C5C5C]">brokers · borrowers · accounting · process payments in one place</span>
        <button onClick={onRunAll} data-testid="brokerage-run-all-autodebit"
          className="ml-auto inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 hover:bg-[#0B6B4F]/40 text-[#3DBB8E] border border-[#0B6B4F]/50 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"><Percent size={12} /> Run monthly auto-debit batch</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
        <Stat label="Active brokers" value={activeBrokers} />
        <Stat label="Borrowers" value={apps.length} />
        <Stat label="Funded" value={approved.length} good />
        <Stat label="Portfolio financed" value={usd0(financed)} />
        <Stat label="Installments collected" value={usd0(collectedInstallments)} good />
        <Stat label="Fees collected / due" value={`${usd0(feeCollected)} / ${usd0(feeOutstanding)}`} />
        <Stat label="Broker comm. (Yr1 @2%)" value={usd0(commissionsYr1)} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <TabBtn k="transactions" label="Internal Transactions" />
        <TabBtn k="brokers" label="Brokers" />
        <TabBtn k="accounting" label="Accounting" />
        <TabBtn k="cfo" label="CFO Analytics" />
      </div>

      {tab === "cfo" && <CFOAnalytics apps={apps} txns={txns || []} brokers={brokers} onPayCommission={onPayCommission} />}

      {tab === "transactions" && (
        approved.length === 0 ? (
          <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No funded loans to service yet.</div>
        ) : (
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="brokerage-tx-table">
              <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]">
                <tr>
                  <th className="px-4 py-3 text-left">Borrower</th>
                  <th className="px-4 py-3 text-left">Channel</th>
                  <th className="px-4 py-3 text-right">Financed</th>
                  <th className="px-4 py-3 text-right">Remaining</th>
                  <th className="px-4 py-3 text-left">Closing fees</th>
                  <th className="px-4 py-3 text-left">Process internal transaction</th>
                </tr>
              </thead>
              <tbody>
                {approved.map((a) => {
                  const ac = a.accounting || {}; const cf = a.closing_fees || {};
                  return (
                    <tr key={a.id} className="border-t border-[#2A2A22] hover:bg-[#0A0A0A]/50" data-testid={`brokerage-tx-row-${a.id}`}>
                      <td className="px-4 py-3"><div className="font-semibold text-[#ECECE4]">{a.borrower_name}</div><div className="text-[10.5px] text-[#5C5C5C]">{a.borrower_email}</div></td>
                      <td className="px-4 py-3">{a.channel === "broker" ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] bg-[#C9A227]/15 text-[#C9A227] border border-[#C9A227]/40 rounded-sm" title={a.broker_name}><Handshake size={10} /> Broker</span> : <span className="text-[10.5px] text-[#5C5C5C]">Direct</span>}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#C9C9C0]">{usd0(ac.amount_financed || a.current_balance)}</td>
                      <td className="px-4 py-3 text-right font-mono text-[#E4C97A]">{usd0(ac.remaining_balance)}</td>
                      <td className="px-4 py-3">{cf.waived ? <span className="inline-flex items-center gap-1 text-[#5fd3a8] text-[10px]" data-testid={`brokerage-fees-waived-${a.id}`}>★ Waived · No Cost</span> : cf.paid ? <span className="text-[#3DBB8E] text-[10px]">✓ {usd0(cf.total)} collected</span> : <span className="text-[#B8860B] text-[10px]">{usd0(cf.total)} due</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {!a.disbursed && <button onClick={() => onDisburse(a.id)} data-testid={`brokerage-disburse-${a.id}`} className="px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] bg-[#13130E] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/10 rounded-sm">Disburse payoff</button>}
                          {a.autopay?.enrolled && a.autopay?.stripe_payment_method_id && <button onClick={() => onChargeAutopay(a.id)} data-testid={`brokerage-autodebit-${a.id}`} className="px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] bg-[#0B6B4F]/20 border border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 rounded-sm">Run auto-debit</button>}
                          {!cf.paid && !cf.waived && (cf.total > 0) && <button onClick={() => onMarkPaid(a.id)} data-testid={`brokerage-markfees-${a.id}`} className="px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] bg-[#0B6B4F]/20 border border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 rounded-sm">Collect fees</button>}
                          {a.disbursed && (cf.paid || cf.waived) && <span className="text-[10px] text-[#5C5C5C]">✓ processed</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "brokers" && (
        brokerList.length === 0 ? (
          <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No brokers registered yet.</div>
        ) : (
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="brokerage-brokers-table">
              <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]"><tr>
                <th className="px-4 py-3 text-left">Broker / Officer</th><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3 text-right">Clients</th></tr></thead>
              <tbody>
                {brokerList.map((b) => (
                  <tr key={b.id} className="border-t border-[#2A2A22]" data-testid={`brokerage-broker-row-${b.id}`}>
                    <td className="px-4 py-3"><div className="font-semibold text-[#ECECE4]">{b.company}</div><div className="text-[10.5px] text-[#5C5C5C]">{b.contact_name} · {b.email}</div></td>
                    <td className="px-4 py-3 text-[#C9C9C0]">{b.applicant_type === "loan_officer" ? "Loan Officer" : "Broker / Firm"}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${brStatusCls(b.status)}`}>{b.status}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-[#C9C9C0]">{b.client_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === "accounting" && (
        approved.length === 0 ? (
          <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No accounting activity yet.</div>
        ) : (
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="brokerage-accounting-table">
              <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]"><tr>
                <th className="px-4 py-3 text-left">Borrower</th><th className="px-4 py-3 text-right">Financed</th><th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-right">Remaining</th><th className="px-4 py-3 text-left">Next due</th><th className="px-4 py-3 text-left">Status</th></tr></thead>
              <tbody>
                {approved.map((a) => { const ac = a.accounting || {}; return (
                  <tr key={a.id} className="border-t border-[#2A2A22]" data-testid={`brokerage-acct-row-${a.id}`}>
                    <td className="px-4 py-3"><div className="font-semibold text-[#ECECE4]">{a.borrower_name}</div></td>
                    <td className="px-4 py-3 text-right font-mono text-[#C9C9C0]">{usd0(ac.amount_financed || a.current_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#3DBB8E]">{usd0(ac.paid_amount)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#E4C97A]">{usd0(ac.remaining_balance)}</td>
                    <td className="px-4 py-3 text-[#9A9A90]">{ac.next_due ? `${ac.next_due.due_date} · ${usd0(ac.next_due.amount)}` : "—"}</td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-[0.1em] text-[#9A9A90]">{ac.status_label || "current"}</span></td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}

function BorrowerDatabase({ apps, token, onChanged }) {
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("all");
  const [editApp, setEditApp] = useState(null);
  const standstillInfo = (a) => {
    if (a.transaction_type !== "foreclosure") return null;
    const b = a.binder || {};
    const eff = b.coverDate || b.closingDate || a.approved_at || a.created_at;
    if (!eff) return null;
    const start = new Date(String(eff).slice(0, 10) + "T00:00:00");
    const exp = new Date(start); exp.setDate(exp.getDate() + 180);
    const days = Math.ceil((exp - new Date()) / 86400000);
    let cls = "bg-[#123c2b] text-[#6fe0b0] border-[#1f6b4d]";
    if (days <= 7) cls = "bg-[#4a1414] text-[#ff9a9a] border-[#7a2020]";
    else if (days <= 30) cls = "bg-[#4a3a10] text-[#f0d070] border-[#8a6a1a]";
    const label = days < 0 ? `Standstill expired ${-days}d ago` : days === 0 ? "Standstill expires today" : `Standstill: ${days}d left`;
    return { days, exp, cls, label };
  };
  const rows = apps
    .filter((a) => channel === "all" ? true : channel === "broker" ? a.channel === "broker" : a.channel !== "broker")
    .filter((a) => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (a.borrower_name || "").toLowerCase().includes(s) || (a.borrower_email || "").toLowerCase().includes(s) || (a.broker_name || "").toLowerCase().includes(s);
    });
  const approvedCount = apps.filter((a) => a.status === "approved").length;
  const brokerCount = apps.filter((a) => a.channel === "broker").length;

  return (
    <section className="mb-12" data-testid="borrower-database-admin">
      <div className="mb-4 flex items-center gap-3">
        <Database size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Borrower Database</h2>
        <span className="text-[11px] text-[#5C5C5C]">every borrower &amp; broker-onboarded client · one record store</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Stat label="Total borrowers" value={apps.length} />
        <Stat label="Approved / funded" value={approvedCount} good />
        <Stat label="Via broker channel" value={brokerCount} />
        <Stat label="In pipeline" value={apps.filter((a) => a.status === "submitted").length} />
      </div>

      {(() => {
        const alerts = rows.map((a) => ({ a, si: standstillInfo(a) })).filter((x) => x.si && x.si.days <= 30).sort((x, y) => x.si.days - y.si.days);
        if (alerts.length === 0) return null;
        return (
          <div className="mb-4 rounded-lg border border-[#7a2020]/60 bg-[#2a1010]/50 p-3" data-testid="standstill-alert-banner">
            <div className="flex items-center gap-2 mb-2 text-[#ff9a9a] text-[11px] uppercase tracking-[0.12em] font-bold">
              <Clock size={13} /> Standstill deadlines approaching ({alerts.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {alerts.map(({ a, si }) => (
                <button key={a.id} onClick={() => setEditApp(a)} className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] border rounded-sm ${si.cls}`} data-testid={`standstill-alert-${a.id}`}>
                  <span className="font-semibold">{a.borrower_name}</span> · {si.label}
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by borrower, email, or broker…" data-testid="borrower-db-search"
            className="w-full bg-[#0F0F0F] border border-[#2A2A22] rounded-md pl-9 pr-3 py-2 text-[13px]" />
        </div>
        <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="borrower-db-channel"
          className="bg-[#0F0F0F] border border-[#2A2A22] rounded-md px-3 py-2 text-[12px]">
          <option value="all">All channels</option>
          <option value="direct">Direct</option>
          <option value="broker">Broker</option>
        </select>
      </div>

      {rows.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No borrower records{q ? " match your search" : " yet"}.</div>
      ) : (
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-md overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="borrower-db-table">
            <thead className="bg-[#0A0A0A] text-[#8A8A8A] uppercase tracking-[0.1em] text-[10px]">
              <tr>
                <th className="px-4 py-3 text-left">Borrower</th>
                <th className="px-4 py-3 text-left">Channel</th>
                <th className="px-4 py-3 text-left">Loan Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Financed</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-left">Next Due</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const ac = a.accounting || {};
                return (
                  <tr key={a.id} className="border-t border-[#2A2A22] hover:bg-[#0A0A0A]/50" data-testid={`borrower-db-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#ECECE4]">{a.borrower_name}</div>
                      <div className="text-[10.5px] text-[#5C5C5C]">{a.borrower_email}</div>
                      {(() => { const si = standstillInfo(a); return si ? <div className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.06em] border rounded-sm ${si.cls}`} data-testid={`standstill-badge-${a.id}`} title={`180-day standstill expires ${si.exp.toLocaleDateString()}`}><Clock size={10} /> {si.label}</div> : null; })()}
                    </td>
                    <td className="px-4 py-3">
                      {a.channel === "broker"
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] bg-[#C9A227]/15 text-[#C9A227] border border-[#C9A227]/40 rounded-sm" title={a.broker_name}><Handshake size={10} /> Broker</span>
                        : <span className="text-[10.5px] text-[#5C5C5C]">Direct</span>}
                    </td>
                    <td className="px-4 py-3 text-[#C9C9C0]">{LOAN_LABELS[a.debt_type] || a.debt_type || "—"}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${STATUS_CLS[a.status] || "text-[#8A8A8A] border-[#2A2A22]"}`}>{a.status}</span></td>
                    <td className="px-4 py-3 text-right font-mono text-[#C9C9C0]">{usd0(ac.amount_financed || a.current_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#E4C97A]">{usd0(ac.remaining_balance)}</td>
                    <td className="px-4 py-3 text-right font-mono text-[#3DBB8E]">{usd0(ac.paid_amount)}</td>
                    <td className="px-4 py-3 text-[#9A9A90]">{ac.next_due ? ac.next_due.due_date : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditApp(a)} data-testid={`admin-app-edit-${a.id}`}
                        className="inline-flex items-center gap-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#E4C97A] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] font-bold rounded-sm">
                        <FileText size={12} /> Edit / Files
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editApp && (
        <AdminAppEditor app={editApp} token={token}
          onClose={() => setEditApp(null)}
          onSaved={() => { setEditApp(null); onChanged && onChanged(); }} />
      )}
    </section>
  );
}

function BorrowerFiles({ apps, token, onChanged }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [editApp, setEditApp] = useState(null);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [faxStatuses, setFaxStatuses] = useState({});
  const [faxJobs, setFaxJobs] = useState([]);
  const [faxJobsOpen, setFaxJobsOpen] = useState(false);
  const loadFaxStatuses = useCallback(async (fileRows) => {
    const ids = (fileRows || []).flatMap((f) => (f.transactions || []).map((t) => t.app_id));
    if (!ids.length) return;
    try {
      const r = await axios.get(`${API}/admin/treasury/fax-status?ids=${ids.join(",")}`);
      setFaxStatuses(r.data.statuses || {});
    } catch { /* ignore */ }
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/borrower-applications/borrower-files`);
      const rows = r.data.files || [];
      setFiles(rows);
      setTotalOutstanding(r.data.total_outstanding_fees || 0);
      loadFaxStatuses(rows);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [loadFaxStatuses]);
  useEffect(() => { load(); }, [load]);
  const loadFaxJobs = async () => {
    try {
      const r = await axios.get(`${API}/admin/fax/jobs`);
      setFaxJobs((r.data.jobs || r.data || []).filter((j) => (j.meta || {}).kind === "treasury_package"));
    } catch { /* ignore */ }
  };
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedRows, setArchivedRows] = useState([]);
  const [archLoading, setArchLoading] = useState(false);
  const [archQuery, setArchQuery] = useState("");
  const loadArchived = async () => {
    setArchLoading(true);
    try { const r = await axios.get(`${API}/admin/borrower-applications/archived-files`); setArchivedRows(r.data.archived || []); }
    catch { /* ignore */ } finally { setArchLoading(false); }
  };
  const restoreApp = async (appId, who) => {
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/unarchive`, {});
      toast.success(`Restored — ${who || "file"} is back in Borrower Files.`);
      await loadArchived(); await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const refreshFaxJob = async (faxId) => {
    try { await axios.post(`${API}/admin/fax/${faxId}/refresh`, {}); await loadFaxJobs(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not refresh"); }
  };
  const openCertificate = async (faxId) => {
    try {
      const token = localStorage.getItem("th_admin_token");
      const r = await axios.get(`${API}/admin/fax/certificate/${faxId}`, { responseType: "blob", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error(e.response?.data?.detail || "Certificate not available yet."); }
  };
  const FAX_UI = {
    delivered: { c: "#3DBB8E", t: "Delivered ✓" }, sent: { c: "#3DBB8E", t: "Sent ✓" }, success: { c: "#3DBB8E", t: "Delivered ✓" },
    sending: { c: "#E4C97A", t: "Sending…" }, queued: { c: "#E4C97A", t: "Queued…" },
    failed: { c: "#FF8585", t: "Failed" }, delivery_failed: { c: "#FF8585", t: "Failed" }, expired: { c: "#FF8585", t: "Expired" },
  };
  const markPaid = async (appId, borrower) => {
    if (!window.confirm(`Record this transaction's pre-closing fee as PAID for ${borrower}? Use this only after the fee has actually been collected.`)) return;
    setBusy(appId);
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/mark-closing-fees-paid`, {});
      toast.success(`Pre-closing fee recorded as paid for ${borrower}.`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const staffEsign = async (appId, borrower) => {
    const name = window.prompt(`E-SIGN BORROWER'S PACKAGE\n\nType the borrower's full legal name to apply their e-signature to every document in this file. This signature is FINAL.`, borrower !== "—" ? borrower : "");
    if (!name || !name.trim()) return;
    const co = window.prompt("Co-borrower's full legal name (optional — leave blank if none):", "") || "";
    setBusy(appId);
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/staff-esign`, { signer_name: name.trim(), co_signer_name: co.trim() || null });
      toast.success(`Package e-signed for ${name.trim()}.`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const archiveApp = async (appId, borrower, label) => {
    if (!window.confirm(`Delete this ${label} transaction for ${borrower}?\n\nIt will be archived (hidden from all lists) and can be restored later by an owner. Its documents and payment records are preserved.`)) return;
    setBusy(appId);
    try {
      await axios.post(`${API}/admin/borrower-applications/${appId}/archive`, {});
      toast.success(`Transaction archived for ${borrower}.`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await axios.post(`${API}/admin/borrower-applications/${appId}/unarchive`, {});
              toast.success(`Restored — ${borrower}'s ${label} transaction is back.`);
              await load(); onChanged && onChanged();
            } catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
          },
        },
      });
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  const openEditor = (appId) => {
    const full = apps.find((a) => a.id === appId);
    if (full) setEditApp(full);
    else toast.message("Open this file from the Borrower Database to edit.");
  };
  const rows = files.filter((f) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (f.borrower || "").toLowerCase().includes(s) || (f.borrower_email || "").toLowerCase().includes(s);
  });
  const allIds = rows.flatMap((f) => f.transactions.map((t) => t.app_id));
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  // Ids for a bulk action: checked rows if any, else all visible rows.
  const bulkIds = () => (selected.size ? allIds.filter((id) => selected.has(id)) : allIds);
  const openBulk = (base, extra) => {
    const ids = bulkIds();
    if (!ids.length) { toast.message("No files to generate."); return; }
    const CAP = 25;
    const use = ids.slice(0, CAP);
    if (ids.length > CAP) toast.message(`Building for the first ${CAP} of ${ids.length} files. Select fewer rows for a specific batch.`);
    const qs = new URLSearchParams({ ids: use.join(","), ...(extra || {}) }).toString();
    window.open(`${base}?${qs}`, "_blank");
  };

  const openDeedsBulk = () => {
    const ids = bulkIds();
    if (!ids.length) { toast.message("No files selected."); return; }
    const CAP = 15;
    const use = ids.slice(0, CAP);
    if (ids.length > CAP) toast.message(`Opening the first ${CAP} of ${ids.length} deeds. Select fewer rows for a specific batch.`);
    use.forEach((id) => window.open(`/closing-binder/${id}?deed=1`, "_blank"));
  };
  const feeBadge = (t) => t.fee_waived
    ? <span className="px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] bg-[#0B6B4F]/20 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm">Waived · No Cost</span>
    : t.fee_paid
      ? <span className="px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] bg-[#0B6B4F]/20 text-[#3DBB8E] border border-[#0B6B4F]/50 rounded-sm">Fee Paid</span>
      : <span className="px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm">{usd0(t.fee_total)} due</span>;
  return (
    <section className="mb-12" data-testid="borrower-files-admin">
      <div className="mb-4 flex items-center gap-3">
        <FolderOpen size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Borrower Files</h2>
        <span className="text-[11px] text-[#5C5C5C]">all transactions per borrower — nothing merged</span>
        {totalOutstanding > 0 && <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#B8860B]/15 text-[#E4C97A] border border-[#B8860B]/40 rounded-sm" data-testid="bf-total-outstanding">{usd0(totalOutstanding)} fees outstanding</span>}
        <span className="ml-auto text-[10px] uppercase tracking-[0.1em] text-[#7DD3B8]" data-testid="bf-sel-count">{selected.size ? `${selected.size} selected` : "all files"}</span>
        <button onClick={toggleSelectAll} data-testid="bf-select-all" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9A9A90] hover:text-[#ECECE4] border border-[#2A2A22] hover:border-[#3A3A2A] px-2.5 py-1 rounded-sm">
          {allSelected ? "Clear all" : "Select all"}
        </button>
        <button onClick={load} data-testid="bf-refresh" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9A9A90] hover:text-[#E4C97A]">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button onClick={() => openBulk("/collateral-motion")} data-testid="bf-bulk-motion" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9DB4E8] border border-[#5C6B9E]/50 hover:border-[#5C6B9E] px-2.5 py-1 rounded-sm">
          <Gavel size={12} /> Motions (bulk)
        </button>
        <button onClick={() => openBulk("/motion-packet")} data-testid="bf-bulk-packet" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#E4C97A] border border-[#B8860B]/50 hover:border-[#B8860B] px-2.5 py-1 rounded-sm">
          <FolderKanban size={12} /> Packets (bulk)
        </button>
        <button onClick={() => openBulk("/treasury-repo")} data-testid="bf-bulk-treasury" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#5FBFA0] border border-[#2E6F5E]/60 hover:border-[#2E6F5E] px-2.5 py-1 rounded-sm">
          <Landmark size={12} /> Treasury (bulk)
        </button>
        <button onClick={() => openBulk("/federal-assistance")} data-testid="bf-bulk-federal" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9DB4E8] border border-[#5C6B9E]/60 hover:border-[#5C6B9E] px-2.5 py-1 rounded-sm">
          <Landmark size={12} /> Fed Assistance (bulk)
        </button>
        <button onClick={() => openBulk("/gsa-bond")} data-testid="bf-bulk-gsa" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#B9C6EC] border border-[#3a4a86]/70 hover:border-[#3a4a86] px-2.5 py-1 rounded-sm">
          <ShieldCheck size={12} /> GSA Bonds (bulk)
        </button>
        <button onClick={() => openBulk("/gsa-bond", { claim: "1" })} data-testid="bf-bulk-claim" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#E4C97A] border border-[#B8860B]/60 hover:border-[#B8860B] px-2.5 py-1 rounded-sm">
          <FileText size={12} /> Bond Claim Forms (bulk)
        </button>
        <select data-testid="bf-bulk-gsaform" defaultValue="" onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) openBulk("/gsa-bond", { form: v }); }}
          className="text-[10px] uppercase tracking-[0.12em] text-[#B9C6EC] bg-[#0F0F0A] border border-[#3a4a86]/70 hover:border-[#3a4a86] px-2.5 py-1 rounded-sm cursor-pointer">
          <option value="">GSA Single Form (bulk)…</option>
          {GSA_FORMS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <button onClick={openDeedsBulk} data-testid="bf-bulk-deeds" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#7FD8BC] border border-[#5FBFA0]/50 hover:border-[#5FBFA0] px-2.5 py-1 rounded-sm">
          <FileText size={12} /> State Deeds (bulk)
        </button>
        <button onClick={() => openBulk("/loss-mitigation")} data-testid="bf-bulk-lossmit" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#7FD3B4] border border-[#2E6F5E]/70 hover:border-[#2E6F5E] px-2.5 py-1 rounded-sm">
          <Handshake size={12} /> Loss Mitigation (bulk)
        </button>
        <button onClick={() => openBulk("/mortgage-replacement")} data-testid="bf-bulk-mortrepl" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#D8A77A] border border-[#5A2E12]/70 hover:border-[#B8860B] px-2.5 py-1 rounded-sm">
          <Repeat size={12} /> Mtg Replacement (bulk)
        </button>
        <button onClick={() => openBulk("/tax-credit-transfer")} data-testid="bf-bulk-taxcredit" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#6FD3AE] border border-[#0B3D2E]/80 hover:border-[#6FD3AE] px-2.5 py-1 rounded-sm">
          <Landmark size={12} /> Tax Credit / IRS Release (bulk)
        </button>
        <button onClick={() => { const nx = !faxJobsOpen; setFaxJobsOpen(nx); if (nx) loadFaxJobs(); }} data-testid="bf-faxjobs-toggle" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#9DB4E8] border border-[#5C6B9E]/50 hover:border-[#5C6B9E] px-2.5 py-1 rounded-sm">
          <Send size={12} /> Fax Jobs
        </button>
        <button onClick={() => { const nx = !archivedOpen; setArchivedOpen(nx); if (nx) loadArchived(); }} data-testid="bf-archived-toggle" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#C99A9A] border border-[#7A3A3A]/50 hover:border-[#9E2A2B] px-2.5 py-1 rounded-sm">
          <Trash2 size={12} /> Archived / Restore
        </button>
      </div>
      {archivedOpen && (
        <div className="mb-4 rounded-lg border border-[#7A3A3A]/40 bg-[#1A1010] p-3" data-testid="bf-archived-panel">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#FF9E9E] font-bold flex items-center gap-2"><Trash2 size={12} /> Archived (deleted) files — restore any hidden transaction</span>
            <button onClick={loadArchived} className="text-[10px] text-[#9A9A90] hover:text-[#E4C97A] inline-flex items-center gap-1"><RefreshCw size={11} className={archLoading ? "animate-spin" : ""} /> Reload</button>
            <span className="ml-auto text-[10px] text-[#6A6A62]">{(() => { const q = archQuery.trim().toLowerCase(); const n = q ? archivedRows.filter((a) => [a.borrower, a.borrower_email, a.debt_type, a.mortgage_position, a.collateral, a.archived_by].filter(Boolean).join(" ").toLowerCase().includes(q)).length : archivedRows.length; return q ? `${n} of ${archivedRows.length} archived` : `${archivedRows.length} archived`; })()}</span>
          </div>
          {archivedRows.length > 0 && (
            <div className="relative mb-2">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7A6A6A]" />
              <input
                type="text"
                value={archQuery}
                onChange={(e) => setArchQuery(e.target.value)}
                data-testid="bf-archived-search"
                placeholder="Search archived by borrower, email, type, collateral or who archived…"
                className="w-full bg-[#120A0A] border border-[#3A2222] focus:border-[#9E2A2B] rounded-sm pl-7 pr-7 py-1.5 text-[11px] text-[#E9D9D9] placeholder-[#6A5A5A] outline-none"
              />
              {archQuery && (
                <button onClick={() => setArchQuery("")} data-testid="bf-archived-search-clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A7A7A] hover:text-[#E4C97A]"><X size={12} /></button>
              )}
            </div>
          )}
          {archivedRows.length === 0 ? (
            <div className="text-[11px] text-[#8A8A8A] py-3 text-center">{archLoading ? "Loading…" : "No archived files. If a file is missing and not here, it was never archived (it may be live-production data not present in this preview)."}</div>
          ) : (() => {
            const q = archQuery.trim().toLowerCase();
            const rows = q ? archivedRows.filter((a) => [a.borrower, a.borrower_email, a.debt_type, a.mortgage_position, a.collateral, a.archived_by].filter(Boolean).join(" ").toLowerCase().includes(q)) : archivedRows;
            if (rows.length === 0) return (
              <div className="text-[11px] text-[#8A8A8A] py-3 text-center" data-testid="bf-archived-noresults">No archived files match "{archQuery}".</div>
            );
            return (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr className="text-[#7A7A72] border-b border-[#2A2A22]">
                  <th className="px-2 py-1.5 text-left">Borrower</th><th className="px-2 py-1.5 text-left">Type</th>
                  <th className="px-2 py-1.5 text-left">Collateral</th><th className="px-2 py-1.5 text-left">Archived</th>
                  <th className="px-2 py-1.5 text-left">By</th><th className="px-2 py-1.5 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.app_id} className="border-b border-[#231414] last:border-0" data-testid={`bf-archived-${a.app_id}`}>
                      <td className="px-2 py-1.5 text-[#E9D9D9] font-semibold">{a.borrower}<div className="text-[9px] text-[#7A6A6A]">{a.borrower_email}</div></td>
                      <td className="px-2 py-1.5 text-[#C9C9C1] capitalize">{a.mortgage_position ? `${a.mortgage_position} ` : ""}{a.debt_type}</td>
                      <td className="px-2 py-1.5 text-[#C9C9C1] max-w-[220px] truncate">{a.collateral || "—"}</td>
                      <td className="px-2 py-1.5 text-[#9A9A90]">{(a.archived_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-2 py-1.5 text-[#9A9A90]">{a.archived_by || "—"}</td>
                      <td className="px-2 py-1.5 text-right"><button onClick={() => restoreApp(a.app_id, a.borrower)} data-testid={`bf-restore-${a.app_id}`} className="inline-flex items-center gap-1 border border-[#2E6F5E]/60 hover:border-[#2E6F5E] text-[#5FBFA0] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm"><RotateCcw size={11} /> Restore</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>
      )}
      {faxJobsOpen && (
        <div className="mb-4 rounded-lg border border-[#5C6B9E]/40 bg-[#0E1320] p-3" data-testid="bf-faxjobs-panel">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] uppercase tracking-[0.12em] text-[#9DB4E8] font-bold flex items-center gap-2"><Send size={12} /> Treasury eFax Jobs</span>
            <button onClick={loadFaxJobs} className="text-[10px] text-[#9A9A90] hover:text-[#E4C97A] inline-flex items-center gap-1"><RefreshCw size={11} /> Reload</button>
            <span className="ml-auto text-[10px] text-[#6A6A62]">{faxJobs.length} sent</span>
          </div>
          {faxJobs.length === 0 ? (
            <div className="text-[11px] text-[#8A8A8A] py-3 text-center">No Treasury eFax jobs yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead><tr className="text-[#7A7A72] border-b border-[#2A2A22]">
                  <th className="px-2 py-1.5 text-left">Sent</th><th className="px-2 py-1.5 text-left">To</th>
                  <th className="px-2 py-1.5 text-center">Borrowers</th><th className="px-2 py-1.5 text-left">Telnyx ID</th>
                  <th className="px-2 py-1.5 text-left">Status</th><th className="px-2 py-1.5 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {faxJobs.map((j) => { const s = (j.status || "").toLowerCase(); const ui = FAX_UI[s] || { c: "#9A9A90", t: j.status || "—" }; return (
                    <tr key={j.id} className="border-b border-[#1A1A22] last:border-0" data-testid={`bf-faxjob-${j.id}`}>
                      <td className="px-2 py-1.5 text-[#C9C9C1]">{(j.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-2 py-1.5 text-[#C9C9C1]">{j.to}</td>
                      <td className="px-2 py-1.5 text-center text-[#C9C9C1]">{(j.meta || {}).count || ((j.meta || {}).app_ids || []).length}</td>
                      <td className="px-2 py-1.5 text-[#8A8A8A] font-mono text-[10px]">{String(j.id).slice(0, 12)}…</td>
                      <td className="px-2 py-1.5"><span style={{ color: ui.c }} className="font-bold">{ui.t}</span>{j.failure_reason ? <span className="text-[#FF8585]"> · {j.failure_reason}</span> : null}</td>
                      <td className="px-2 py-1.5 text-right"><button onClick={() => refreshFaxJob(j.id)} className="text-[#9DB4E8] hover:text-[#E4C97A] inline-flex items-center gap-1 text-[10px]"><RefreshCw size={10} /> Refresh</button>{["delivered", "sent", "success"].includes(s) && <button onClick={() => openCertificate(j.id)} data-testid={`bf-faxcert-${j.id}`} className="ml-2 text-[#3DBB8E] hover:text-[#5FE0B0] inline-flex items-center gap-1 text-[10px]"><FileCheck2 size={10} /> Certificate</button>}</td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="relative mb-4 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by borrower or email…" data-testid="bf-search"
          className="w-full bg-[#0F0F0F] border border-[#2A2A22] rounded-md pl-9 pr-3 py-2 text-[13px]" />
      </div>
      {rows.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="bf-empty">No borrower files{q ? " match your search" : " yet"}.</div>
      ) : (
        <div className="space-y-4">
          {rows.map((f) => (
            <div key={f.key} className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-4" data-testid={`bf-card-${f.key}`}>
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                  <div className="text-[14px] text-[#ECECE4] font-semibold">{f.borrower}</div>
                  <div className="text-[10.5px] text-[#8A8A8A]">{f.borrower_email || "—"} · {f.transaction_count} transaction{f.transaction_count !== 1 ? "s" : ""}</div>
                </div>
                <div className="flex items-center gap-4 text-right">
                  <div><div className="text-[9.5px] uppercase tracking-[0.1em] text-[#8A8A8A]">Outstanding fees</div><div className={`text-[14px] font-semibold ${f.total_outstanding_fees > 0 ? "text-[#E4C97A]" : "text-[#3DBB8E]"}`}>{usd0(f.total_outstanding_fees)}</div></div>
                  <div><div className="text-[9.5px] uppercase tracking-[0.1em] text-[#8A8A8A]">Total balance</div><div className="text-[14px] font-semibold text-[#C9C9C0]">{usd0(f.total_remaining_balance)}</div></div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid={`bf-table-${f.key}`}>
                  <thead className="text-[#8A8A8A] uppercase tracking-[0.1em] text-[9.5px]">
                    <tr className="border-b border-[#2A2A22]">
                      <th className="px-2 py-2 text-center w-8"><input type="checkbox" checked={f.transactions.length > 0 && f.transactions.every((t) => selected.has(t.app_id))} onChange={() => { const ids = f.transactions.map((t) => t.app_id); const allOn = ids.every((id) => selected.has(id)); setSelected((prev) => { const n = new Set(prev); ids.forEach((id) => allOn ? n.delete(id) : n.add(id)); return n; }); }} data-testid={`bf-selrow-${f.key}`} /></th>
                      <th className="px-2 py-2 text-left">Transaction</th>
                      <th className="px-2 py-2 text-left">Status</th>
                      <th className="px-2 py-2 text-left">E-Signature</th>
                      <th className="px-2 py-2 text-right">Financed</th>
                      <th className="px-2 py-2 text-right">Balance</th>
                      <th className="px-2 py-2 text-left">Next Due</th>
                      <th className="px-2 py-2 text-left">Pre-Closing Fee</th>
                      <th className="px-2 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {f.transactions.map((t) => (
                      <tr key={t.app_id} className="border-b border-[#1F1F19] last:border-0" data-testid={`bf-tx-${t.app_id}`}>
                        <td className="px-2 py-2.5 text-center"><input type="checkbox" checked={selected.has(t.app_id)} onChange={() => toggleSel(t.app_id)} data-testid={`bf-sel-${t.app_id}`} /></td>
                        <td className="px-2 py-2.5">
                          <div className="text-[#ECECE4]">{LOAN_LABELS[t.debt_type] || t.debt_type}{t.mortgage_position ? ` · ${t.mortgage_position} lien` : ""}</div>
                          <div className="text-[10px] text-[#8A8A8A] capitalize">{(t.transaction_type || "").replace(/_/g, " ")}{t.channel === "broker" ? ` · ${t.broker_name || "Broker"}` : " · Direct"}</div>
                        </td>
                        <td className="px-2 py-2.5"><span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border rounded-sm ${STATUS_CLS[t.status] || "text-[#8A8A8A] border-[#2A2A22]"}`}>{t.status}</span></td>
                        <td className="px-2 py-2.5">
                          {t.signed
                            ? (t.esign_pending_confirmation
                                ? <span className="px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] border rounded-sm text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10" data-testid={`bf-sig-chip-${t.app_id}`}>Signed · unconfirmed</span>
                                : <span className="px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] border rounded-sm text-[#3DBB8E] border-[#0B6B4F]/60 bg-[#0B6B4F]/12" data-testid={`bf-sig-chip-${t.app_id}`}>Signed · confirmed</span>)
                            : <span className="px-2 py-0.5 text-[9.5px] uppercase tracking-[0.08em] border rounded-sm text-[#8A8A8A] border-[#2A2A22]" data-testid={`bf-sig-chip-${t.app_id}`}>Unsigned</span>}
                        </td>
                        <td className="px-2 py-2.5 text-right font-mono text-[#C9C9C0]">{usd0(t.principal)}</td>
                        <td className="px-2 py-2.5 text-right font-mono text-[#E4C97A]">{usd0(t.remaining_balance)}</td>
                        <td className="px-2 py-2.5 text-[#9A9A90]">{t.next_due ? t.next_due.due_date : "—"}</td>
                        <td className="px-2 py-2.5">{feeBadge(t)}</td>
                        <td className="px-2 py-2.5 text-right whitespace-nowrap">
                          {!t.fee_paid && !t.fee_waived && (
                            <button onClick={() => markPaid(t.app_id, f.borrower)} disabled={busy === t.app_id} data-testid={`bf-mark-paid-${t.app_id}`}
                              className="inline-flex items-center gap-1 bg-[#0B6B4F]/20 border border-[#0B6B4F]/60 text-[#3DBB8E] hover:bg-[#0B6B4F]/40 disabled:opacity-40 px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm mr-1.5">
                              <CreditCard size={11} /> Mark fee paid
                            </button>
                          )}
                          <button onClick={() => openEditor(t.app_id)} data-testid={`bf-open-${t.app_id}`}
                            className="inline-flex items-center gap-1 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#E4C97A] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <FileText size={11} /> Open
                          </button>
                          <button onClick={() => window.open(`/collateral-motion/${t.app_id}`, "_blank")} data-testid={`bf-motion-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#5C6B9E]/50 hover:border-[#5C6B9E] text-[#9DB4E8] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Gavel size={11} /> Motion
                          </button>
                          <button onClick={() => window.open(`/motion-packet/${t.app_id}`, "_blank")} data-testid={`bf-packet-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#B8860B]/40 hover:border-[#B8860B] text-[#E4C97A] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <FolderKanban size={11} /> Packet
                          </button>
                          <button onClick={() => window.open(`/treasury-repo/${t.app_id}`, "_blank")} data-testid={`bf-treasury-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#2E6F5E]/60 hover:border-[#2E6F5E] text-[#5FBFA0] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Landmark size={11} /> Treasury
                          </button>
                          <button onClick={() => window.open(`/federal-assistance/${t.app_id}`, "_blank")} data-testid={`bf-federal-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#5C6B9E]/60 hover:border-[#5C6B9E] text-[#9DB4E8] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Landmark size={11} /> Fed Assist
                          </button>
                          <button onClick={() => window.open(`/gsa-bond/${t.app_id}`, "_blank")} data-testid={`bf-gsa-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#3a4a86]/70 hover:border-[#3a4a86] text-[#B9C6EC] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <ShieldCheck size={11} /> GSA Bond
                          </button>
                          <select data-testid={`bf-gsaform-${t.app_id}`} defaultValue="" onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v) window.open(`/gsa-bond/${t.app_id}?form=${v}`, "_blank"); }}
                            className="ml-1.5 border border-[#3a4a86]/70 hover:border-[#3a4a86] text-[#B9C6EC] bg-[#0F0F0A] px-2 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm cursor-pointer">
                            <option value="">Single Form…</option>
                            {GSA_FORMS.map((f) => <option key={f.key} value={f.key}>{f.short}</option>)}
                          </select>
                          <button onClick={() => window.open(`/gsa-bond/${t.app_id}?claim=1`, "_blank")} data-testid={`bf-claim-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#B8860B]/60 hover:border-[#B8860B] text-[#E4C97A] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <FileText size={11} /> Bond Claim
                          </button>
                          <button onClick={() => window.open(`/closing-binder/${t.app_id}?deed=1`, "_blank")} data-testid={`bf-deed-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#5FBFA0]/50 hover:border-[#5FBFA0] text-[#7FD8BC] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <FileText size={11} /> Deed
                          </button>
                          <button onClick={() => window.open(`/loss-mitigation/${t.app_id}`, "_blank")} data-testid={`bf-lossmit-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#2E6F5E]/70 hover:border-[#2E6F5E] text-[#7FD3B4] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Handshake size={11} /> Loss Mit
                          </button>
                          <button onClick={() => window.open(`/mortgage-replacement/${t.app_id}`, "_blank")} data-testid={`bf-mortrepl-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#5A2E12]/70 hover:border-[#B8860B] text-[#D8A77A] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Repeat size={11} /> Mtg Replace
                          </button>
                          <button onClick={() => window.open(`/mortgage-replacement/${t.app_id}?form=coupon`, "_blank")} data-testid={`bf-mrcoupon-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#2E6F5E]/60 hover:border-[#2E6F5E] text-[#7FD8BC] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <FileText size={11} /> Pay Coupon
                          </button>
                          <button onClick={() => window.open(`/tax-credit-transfer/${t.app_id}`, "_blank")} data-testid={`bf-taxcredit-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#0B3D2E]/80 hover:border-[#6FD3AE] text-[#6FD3AE] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Landmark size={11} /> Tax Credit
                          </button>
                          {faxStatuses[t.app_id] && (() => { const s = (faxStatuses[t.app_id].status || "").toLowerCase(); const ui = FAX_UI[s] || { c: "#9A9A90", t: s || "—" }; return (
                            <span data-testid={`bf-faxchip-${t.app_id}`} title={`Treasury eFax ${faxStatuses[t.app_id].fax_id} → ${faxStatuses[t.app_id].to || ""}`} className="inline-flex items-center gap-1 ml-1.5 px-2 py-1 text-[9px] uppercase tracking-[0.06em] font-bold rounded-sm whitespace-nowrap" style={{ color: ui.c, border: `1px solid ${ui.c}55` }}>eFax: {ui.t}</span>
                          ); })()}
                          {!t.signed && (
                            <button onClick={() => staffEsign(t.app_id, f.borrower)} disabled={busy === t.app_id} data-testid={`bf-esign-${t.app_id}`}
                              className="inline-flex items-center gap-1 ml-1.5 border border-[#5C6B9E]/50 hover:border-[#5C6B9E] text-[#9DB4E8] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                              <Pen size={11} /> E-sign package
                            </button>
                          )}
                          <button onClick={() => archiveApp(t.app_id, f.borrower, LOAN_LABELS[t.debt_type] || t.debt_type)} disabled={busy === t.app_id} data-testid={`bf-delete-${t.app_id}`}
                            className="inline-flex items-center gap-1 ml-1.5 border border-[#7A3A3A]/50 hover:border-[#9E2A2B] text-[#FF8585] px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                            <Trash2 size={11} /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
      {editApp && (
        <AdminAppEditor app={editApp} token={token}
          onClose={() => setEditApp(null)}
          onSaved={() => { setEditApp(null); load(); onChanged && onChanged(); }} />
      )}
    </section>
  );
}


const ADMIN_TX_TYPES = [
  ["refinance", "Refinance"], ["finance", "Asset Financing"], ["debt_swap", "Swap"],
  ["debt_settlement", "Settlement"], ["consolidation", "Consolidation"],
  ["assumption", "Mortgage Assumption (subject-to)"],
  ["foreclosure", "Foreclosure / Standstill (180-day)"],
];
const adminErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return d || e.message || "Something went wrong.";
};
const AL = ({ children }) => <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A8A] mb-1 mt-2">{children}</div>;
const AInp = ({ v, set, type = "text", testid }) => (
  <input type={type} value={v} onChange={(e) => set(e.target.value)} data-testid={testid}
    className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px] text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
);

function AdminAppEditor({ app, token, onClose, onSaved }) {
  const [f, setF] = useState({
    transaction_type: app.transaction_type || "refinance", debt_type: app.debt_type || "mortgage",
    vehicle_vin: app.vehicle_vin || "", vehicle_year: app.vehicle_year || "", vehicle_make: app.vehicle_make || "",
    vehicle_model: app.vehicle_model || "", vehicle_trim: app.vehicle_trim || "",
    vehicle_value: app.vehicle_value ?? "", vehicle_purchase_price: app.vehicle_purchase_price ?? "",
    vehicle_title_state: app.vehicle_title_state || "", vehicle_title_status: app.vehicle_title_status || "clean",
    property_or_collateral: app.property_or_collateral || "", property_state: app.property_state || "",
    property_county: app.property_county || "", legal_description: app.legal_description || "",
    tax_parcel_id: app.tax_parcel_id || "",
    note_structure: app.note_structure || "", property_use: app.property_use || "residential",
    occupancy: app.occupancy || "owner_occupied",
    current_lender: app.current_lender || "", current_lender_address: app.current_lender_address || "",
    account_number: app.account_number || "", current_balance: app.current_balance ?? "",
    counterparty_bank_name: app.counterparty_bank_name || "", counterparty_bank_aba: app.counterparty_bank_aba || "",
    counterparty_bank_account: app.counterparty_bank_account || "",
    current_rate: app.current_rate ?? "", current_payment: app.current_payment ?? "",
    term_months: app.term_months || 360,
    borrower_full_name: app.borrower_full_name || app.borrower_name || "", borrower_phone: app.borrower_phone || "",
    borrower_address: app.borrower_address || "", borrower_ssn: "", borrower_dob: app.borrower_dob || "",
    co_borrower_name: app.co_borrower_name || "", employer: app.employer || "", annual_income: app.annual_income ?? "",
    mortgage_position: app.mortgage_position || "first",
    assumption_existing_rate: app.assumption_existing_rate ?? "",
    assumption_monthly_payment: app.assumption_monthly_payment ?? "",
    assumption_remaining_term: app.assumption_remaining_term ?? "",
    assumption_next_due_date: app.assumption_next_due_date || "",
    relocation_incentive: app.relocation_incentive ?? 500,
    vacate_date: app.vacate_date || "",
    deed_type: app.deed_type || "warranty",
    trust_name: app.trust_name || "",
    additional_debts: Array.isArray(app.additional_debts)
      ? app.additional_debts.map((d) => ({
          current_lender: d.current_lender || "", current_lender_address: d.current_lender_address || "",
          account_number: d.account_number || "", current_balance: d.current_balance ?? "", debt_type: d.debt_type || "mortgage",
        }))
      : [],
  });
  const [docs, setDocs] = useState(app.documents || []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [brokers, setBrokers] = useState([]);
  const [brokerId, setBrokerId] = useState(app.broker_id || "");
  useEffect(() => {
    axios.get(`${API}/admin/broker-access/brokers`).then((r) => setBrokers(r.data.brokers || [])).catch(() => {});
  }, []);
  const [docType, setDocType] = useState("government_id");
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const addDebt = () => setF((s) => ({ ...s, additional_debts: [...(s.additional_debts || []), { current_lender: "", current_lender_address: "", account_number: "", current_balance: "", debt_type: "mortgage" }] }));
  const rmDebt = (i) => setF((s) => ({ ...s, additional_debts: s.additional_debts.filter((_, x) => x !== i) }));
  const upDebt = (i, k, v) => setF((s) => ({ ...s, additional_debts: s.additional_debts.map((d, x) => x === i ? { ...d, [k]: v } : d) }));

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await axios.put(`${API}/admin/borrower-applications/${app.id}`, {
        ...f,
        broker_id: brokerId || null,
        current_balance: Number((f.debt_type === "auto" && !f.current_balance ? f.vehicle_purchase_price : f.current_balance) || 0),
        vehicle_value: f.debt_type === "auto" && f.vehicle_value ? Number(f.vehicle_value) : null,
        vehicle_purchase_price: f.debt_type === "auto" && f.vehicle_purchase_price ? Number(f.vehicle_purchase_price) : null,
        current_rate: f.current_rate ? Number(f.current_rate) : null,
        current_payment: f.current_payment ? Number(f.current_payment) : null,
        term_months: Number(f.term_months || 360),
        annual_income: f.annual_income ? Number(f.annual_income) : null,
        mortgage_position: f.mortgage_position || null,
        assumption_existing_rate: f.transaction_type === "assumption" && f.assumption_existing_rate ? Number(f.assumption_existing_rate) : null,
        assumption_monthly_payment: f.transaction_type === "assumption" && f.assumption_monthly_payment ? Number(f.assumption_monthly_payment) : null,
        assumption_remaining_term: f.transaction_type === "assumption" && f.assumption_remaining_term ? Number(f.assumption_remaining_term) : null,
        assumption_next_due_date: f.transaction_type === "assumption" ? (f.assumption_next_due_date || null) : null,
        relocation_incentive: f.transaction_type === "assumption" ? Number(f.relocation_incentive || 500) : null,
        vacate_date: f.transaction_type === "assumption" ? (f.vacate_date || null) : null,
        deed_type: f.transaction_type === "assumption" ? (f.deed_type || "warranty") : null,
        trust_name: f.trust_name || "",
        additional_debts: f.transaction_type === "assumption" ? [] : (f.additional_debts || [])
          .filter((d) => d.current_lender || d.current_balance)
          .map((d) => ({ ...d, current_balance: Number(d.current_balance || 0), debt_type: d.debt_type || "mortgage" })),
      });
      setDocs(r.data.application?.documents || docs);
      toast.success("Application updated — binder & schedule regenerated.");
      onSaved && onSaved();
    } catch (e) { setMsg(adminErr(e)); setBusy(false); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const r = await axios.post(`${API}/admin/borrower-applications/${app.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, r.data.document]);
      toast.success("Document uploaded.");
    } catch (err) { toast.error(adminErr(err)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const delDoc = async (fileId) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await axios.delete(`${API}/admin/borrower-applications/${app.id}/documents/${fileId}`);
      setDocs((d) => d.filter((x) => x.id !== fileId));
      toast.success("Document removed.");
    } catch (err) { toast.error(adminErr(err)); }
  };
  const reviewDoc = async (fileId, status) => {
    try {
      await axios.post(`${API}/admin/borrower-applications/${app.id}/documents/${fileId}/review`, { status });
      setDocs((d) => d.map((x) => x.id === fileId ? { ...x, review_status: status } : x));
      toast.success(status === "verified" ? "Marked verified." : status === "needs_attention" ? "Flagged for attention." : "Reset to pending.");
    } catch (err) { toast.error(adminErr(err)); }
  };
  const reviewBadge = (s) => s === "verified"
    ? "text-[#3DBB8E] border-[#0B6B4F]/50 bg-[#0B6B4F]/15"
    : s === "needs_attention" ? "text-[#FF8585] border-[#9E2A2B]/50 bg-[#9E2A2B]/15"
    : "text-[#8A8A8A] border-[#2A2A22] bg-[#0A0A0A]";
  const [accessCred, setAccessCred] = useState(null);
  const [accessBusy, setAccessBusy] = useState(false);
  const resetAccess = async () => {
    if (accessCred && !window.confirm("Generate a NEW temporary password? The borrower's current password will stop working.")) return;
    setAccessBusy(true);
    try {
      const r = await axios.post(`${API}/admin/borrower-applications/${app.id}/borrower-access`);
      setAccessCred(r.data.invite);
      toast.success("Borrower login ready — share the credentials.");
    } catch (e) { toast.error(adminErr(e)); }
    setAccessBusy(false);
  };
  const docUrl = (d) => {
    const base = (d.source || "borrower") === "broker" ? "broker/documents" : "borrower/documents";
    return `${API}/${base}/${d.id}?auth=${token}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="admin-app-editor">
      <div className="bg-[#0F0F0A] border border-[#2A2A22] rounded-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2A2A22] sticky top-0 bg-[#0F0F0A] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#B8860B] font-bold">Edit Application · {app.status}{app.channel === "broker" ? " · Broker" : ""}</div>
            <div className="text-[14px] text-[#ECECE4]">{app.borrower_name} · {app.borrower_email}</div>
            {app.last_edited_by && <div className="text-[10.5px] text-[#8A8A8A] mt-0.5" data-testid="aae-last-edited">Last edited by {app.last_edited_by} ({app.last_edited_role || "user"}) · {new Date(app.last_edited_at).toLocaleString()}</div>}
          </div>
          <div className="flex items-center gap-2">
            {!(app.signatures && app.signatures.length) && (
              <button data-testid="aae-esign" onClick={async () => {
                const name = window.prompt("E-SIGN BORROWER'S PACKAGE\n\nType the borrower's full legal name to apply their e-signature to every document. This signature is FINAL.", app.borrower_name || "");
                if (!name || !name.trim()) return;
                const co = window.prompt("Co-borrower's full legal name (optional):", "") || "";
                try { await axios.post(`${API}/admin/borrower-applications/${app.id}/staff-esign`, { signer_name: name.trim(), co_signer_name: co.trim() || null }); toast.success(`Package e-signed for ${name.trim()}.`); onSaved && onSaved(); }
                catch (e) { toast.error(e.response?.data?.detail || e.message); }
              }} className="inline-flex items-center gap-1 border border-[#5C6B9E]/50 hover:border-[#5C6B9E] text-[#9DB4E8] px-2.5 py-1.5 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
                <Pen size={12} /> E-sign package
              </button>
            )}
            <button data-testid="aae-delete" onClick={async () => {
              if (!window.confirm("Delete this borrower file? It will be archived (hidden from all lists) and can be restored later. Documents and payment records are preserved.")) return;
              try {
                await axios.post(`${API}/admin/borrower-applications/${app.id}/archive`, {});
                toast.success("File archived.", {
                  action: {
                    label: "Undo",
                    onClick: async () => {
                      try { await axios.post(`${API}/admin/borrower-applications/${app.id}/unarchive`, {}); toast.success("Restored — file is back."); onSaved && onSaved(); }
                      catch (e) { toast.error(e.response?.data?.detail || "Undo failed"); }
                    },
                  },
                });
                onSaved && onSaved();
              }
              catch (e) { toast.error(e.response?.data?.detail || e.message); }
            }} className="inline-flex items-center gap-1 border border-[#7A3A3A]/50 hover:border-[#9E2A2B] text-[#FF8585] px-2.5 py-1.5 text-[10px] uppercase tracking-[0.08em] font-bold rounded-sm">
              <Trash2 size={12} /> Delete
            </button>
            <button onClick={onClose} data-testid="admin-app-editor-close" className="text-[#8A8A8A] hover:text-[#ECECE4]"><X size={18} /></button>
          </div>
        </div>
        <div className="px-6 py-4">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-1">Application Fields — saving rebuilds the binder &amp; schedule</div>
          <AL>Full legal name</AL>
          <AInp v={f.borrower_full_name} set={(v) => up("borrower_full_name", v)} testid="aae-name" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><AL>Phone</AL><AInp v={f.borrower_phone} set={(v) => up("borrower_phone", v)} testid="aae-phone" /></div>
            <div><AL>Mailing address</AL><AInp v={f.borrower_address} set={(v) => up("borrower_address", v)} testid="aae-address" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <AL>Transaction type</AL>
              <select value={f.transaction_type} onChange={(e) => up("transaction_type", e.target.value)} data-testid="aae-tx-type"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                {ADMIN_TX_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><AL>Property / collateral</AL><AInp v={f.property_or_collateral} set={(v) => up("property_or_collateral", v)} testid="aae-collateral" /></div>
          </div>
          <div className="mb-1" data-testid="aae-broker-assign-wrap">
            <AL>Broker assignment (which broker's portal this file belongs to)</AL>
            <select value={brokerId} onChange={(e) => setBrokerId(e.target.value)} data-testid="aae-broker-assign"
              className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
              <option value="">— No broker (direct borrower file) —</option>
              {brokers.map((b) => (
                <option key={b.id} value={b.id}>
                  {(b.company || b.contact_name || b.id.slice(0, 8))}{b.email ? ` · ${b.email}` : ""}{typeof b.client_count === "number" ? ` · ${b.client_count} clients` : ""}
                </option>
              ))}
            </select>
            <div className="text-[10.5px] text-[#8A8A8A] mt-1">Assign this file to a broker so it appears in their broker portal and their granted admins can manage it. Owner only.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-1">
            <div>
              <AL>Obligation type</AL>
              <select value={f.debt_type} onChange={(e) => up("debt_type", e.target.value)} data-testid="aae-debt-type"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                <option value="mortgage">Mortgage (real property)</option>
                <option value="auto">Auto / Vehicle loan</option>
              </select>
            </div>
            <div>
              <AL>Pre-closing costs</AL>
              <div className="bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]" data-testid="aae-fee-preview">
                <span className="text-[#E4C97A] font-semibold">{f.debt_type === "auto" ? "$850.00 / vehicle" : "$1,800.00"}</span>
                <span className="text-[#6f6f66]"> {f.debt_type === "auto" ? "· auto — per vehicle" : "· mortgage flat fee"}</span>
              </div>
            </div>
          </div>
          {f.debt_type === "auto" && (
            <div className="mb-2 rounded-sm border border-[#2A2A22] bg-[#0A0A0A] p-3" data-testid="aae-vehicle-section">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2">Vehicle &amp; Title — drives the auto-loan settlement documents</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><AL>Year</AL><AInp v={f.vehicle_year} set={(v) => up("vehicle_year", v)} testid="aae-vehicle-year" /></div>
                <div><AL>Make</AL><AInp v={f.vehicle_make} set={(v) => up("vehicle_make", v)} testid="aae-vehicle-make" /></div>
                <div><AL>Model</AL><AInp v={f.vehicle_model} set={(v) => up("vehicle_model", v)} testid="aae-vehicle-model" /></div>
                <div><AL>Trim</AL><AInp v={f.vehicle_trim} set={(v) => up("vehicle_trim", v)} testid="aae-vehicle-trim" /></div>
              </div>
              <AL>VIN</AL>
              <AInp v={f.vehicle_vin} set={(v) => up("vehicle_vin", v)} testid="aae-vehicle-vin" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><AL>Vehicle value (NADA/KBB)</AL><AInp v={f.vehicle_value} set={(v) => up("vehicle_value", v)} testid="aae-vehicle-value" /></div>
                <div><AL>Purchase price</AL><AInp v={f.vehicle_purchase_price} set={(v) => up("vehicle_purchase_price", v)} testid="aae-vehicle-purchase-price" /></div>
                <div>
                  <AL>Title status</AL>
                  <select value={f.vehicle_title_status} onChange={(e) => up("vehicle_title_status", e.target.value)} data-testid="aae-vehicle-title-status"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                    <option value="clean">Clean title</option>
                    <option value="lien">Title with existing lien</option>
                    <option value="salvage">Salvage</option>
                    <option value="rebuilt">Rebuilt</option>
                  </select>
                </div>
              </div>
              <AL>Title state</AL>
              <select value={f.vehicle_title_state} onChange={(e) => up("vehicle_title_state", e.target.value)} data-testid="aae-vehicle-title-state"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                <option value="">Select state…</option>
                {US_STATES.map(([ab, nm]) => <option key={ab} value={ab}>{nm}</option>)}
              </select>
              <div className="text-[10.5px] text-[#8A8A8A] mt-1">Leave "Property / collateral" blank above — it will be built from these vehicle details (e.g. "2022 Honda Accord EX — VIN …").</div>
            </div>
          )}
          {f.debt_type === "mortgage" && (<>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <AL>Mortgage lien position</AL>
              <select value={f.mortgage_position} onChange={(e) => up("mortgage_position", e.target.value)} data-testid="aae-mortgage-position"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                <option value="first">First (1st)</option>
                <option value="second">Second (2nd)</option>
                <option value="third">Third (3rd)</option>
              </select>
            </div>
          </div>
          {f.transaction_type === "assumption" && (
            <div className="mt-3 rounded-md border border-[#B8860B]/40 bg-[#1A1606]/60 p-3" data-testid="aae-assumption-fields">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2">Mortgage Assumption (subject-to) — existing note terms</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><AL>Existing rate (%)</AL><AInp v={f.assumption_existing_rate} set={(v) => up("assumption_existing_rate", v)} type="number" testid="aae-asm-rate" /></div>
                <div><AL>Existing monthly payment ($)</AL><AInp v={f.assumption_monthly_payment} set={(v) => up("assumption_monthly_payment", v)} type="number" testid="aae-asm-monthly" /></div>
                <div><AL>Remaining term (months)</AL><AInp v={f.assumption_remaining_term} set={(v) => up("assumption_remaining_term", v)} type="number" testid="aae-asm-term" /></div>
                <div><AL>Next payment due</AL><AInp v={f.assumption_next_due_date} set={(v) => up("assumption_next_due_date", v)} type="date" testid="aae-asm-nextdue" /></div>
                <div><AL>Cash-for-keys / relocation ($)</AL><AInp v={f.relocation_incentive} set={(v) => up("relocation_incentive", v)} type="number" testid="aae-asm-relocation" /></div>
                <div><AL>Vacate date</AL><AInp v={f.vacate_date} set={(v) => up("vacate_date", v)} type="date" testid="aae-asm-vacate" /></div>
                <div>
                  <AL>Deed type</AL>
                  <select value={f.deed_type} onChange={(e) => up("deed_type", e.target.value)} data-testid="aae-asm-deed"
                    className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                    <option value="warranty">Warranty Deed</option>
                    <option value="quitclaim">Quitclaim Deed</option>
                  </select>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <AL>Property state</AL>
              <select value={f.property_state} onChange={(e) => { up("property_state", e.target.value); up("note_structure", recommendedNoteStructure(e.target.value)); }} data-testid="aae-property-state"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[13px]">
                <option value="">Select state…</option>
                {US_STATES.map(([ab, nm]) => <option key={ab} value={ab}>{nm}</option>)}
              </select>
            </div>
            <div><AL>Property county</AL><AInp v={f.property_county} set={(v) => up("property_county", v)} testid="aae-property-county" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><AL>Tax Parcel / APN</AL><AInp v={f.tax_parcel_id} set={(v) => up("tax_parcel_id", v)} testid="aae-parcel" /></div>
          </div>
          <AL>Legal Description (Exhibit A — Land Lot / District / Lot / Block / Plat Book &amp; Page)</AL>
          <textarea value={f.legal_description} onChange={(e) => up("legal_description", e.target.value)} data-testid="aae-legal" rows={3}
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[12px] text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
          </>)}
          <AL>{f.debt_type === "auto" ? (f.transaction_type === "finance" ? "Lender / dealer" : "Current lienholder") : "Current lender"}</AL>
          <AInp v={f.current_lender} set={(v) => up("current_lender", v)} testid="aae-lender" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><AL>Lender address</AL><AInp v={f.current_lender_address} set={(v) => up("current_lender_address", v)} testid="aae-lender-address" /></div>
            <div><AL>Account / loan no.</AL><AInp v={f.account_number} set={(v) => up("account_number", v)} testid="aae-account" /></div>
          </div>
          <div className="mt-2 rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-3" data-testid="aae-counterparty-bank">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8A80] font-bold mb-2">Counterparty bank — for Fedwire payoff by a discounting bank (optional)</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><AL>Counterparty bank name</AL><AInp v={f.counterparty_bank_name} set={(v) => up("counterparty_bank_name", v)} testid="aae-cp-bank-name" /></div>
              <div><AL>ABA / routing</AL><AInp v={f.counterparty_bank_aba} set={(v) => up("counterparty_bank_aba", v)} testid="aae-cp-bank-aba" /></div>
              <div><AL>Account no.</AL><AInp v={f.counterparty_bank_account} set={(v) => up("counterparty_bank_account", v)} testid="aae-cp-bank-account" /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><AL>Balance</AL><AInp v={f.current_balance} set={(v) => up("current_balance", v)} type="number" testid="aae-balance" /></div>
            <div><AL>Term (mo)</AL><AInp v={f.term_months} set={(v) => up("term_months", v)} type="number" testid="aae-term" /></div>
            <div><AL>Rate %</AL><AInp v={f.current_rate} set={(v) => up("current_rate", v)} type="number" testid="aae-rate" /></div>
            <div><AL>Payment</AL><AInp v={f.current_payment} set={(v) => up("current_payment", v)} type="number" testid="aae-curpay" /></div>
          </div>

          {f.transaction_type !== "assumption" && (
            <div className="mt-4" data-testid="aae-trust-name-wrap">
              <AL>Seutrade Complex Trust Name (borrower/broker may name it)</AL>
              <AInp v={f.trust_name} set={(v) => up("trust_name", v)} testid="aae-trust-name" />
            </div>
          )}
          {f.debt_type === "mortgage" && f.transaction_type !== "assumption" && (
            <div className="mt-4 border border-[#B8860B]/30 bg-[#1A1606]/40 rounded-md p-3" data-testid="aae-additional-mortgages">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#C9A227] font-bold">Additional mortgages (2nd / 3rd lien) — consolidated into ONE Seutrade note</div>
                {(f.additional_debts || []).length < 2 && (
                  <button type="button" onClick={addDebt} data-testid="aae-add-mortgage" className="text-[10px] uppercase tracking-[0.1em] text-[#3DBB8E] hover:text-[#6fe0b6] font-bold">+ Add mortgage</button>
                )}
              </div>
              {(f.additional_debts || []).length === 0 && <div className="text-[11px] text-[#6C6C64] italic">No additional mortgages — single mortgage.</div>}
              {(f.additional_debts || []).map((d, i) => (
                <div key={i} className="mb-2 border-t border-[#2A2A22] pt-2" data-testid={`aae-mortgage-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-[#C9C9C0] font-semibold">{i === 0 ? "Second (2nd)" : "Third (3rd)"} mortgage</span>
                    <button type="button" onClick={() => rmDebt(i)} data-testid={`aae-remove-mortgage-${i}`} className="text-[10px] uppercase tracking-[0.1em] text-[#FF8585] hover:text-[#ffb3b3] font-bold">Remove</button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><AL>Lender / servicer</AL><AInp v={d.current_lender} set={(v) => upDebt(i, "current_lender", v)} testid={`aae-mortgage-lender-${i}`} /></div>
                    <div><AL>Account / loan no.</AL><AInp v={d.account_number} set={(v) => upDebt(i, "account_number", v)} testid={`aae-mortgage-account-${i}`} /></div>
                    <div><AL>Lender address</AL><AInp v={d.current_lender_address} set={(v) => upDebt(i, "current_lender_address", v)} testid={`aae-mortgage-address-${i}`} /></div>
                    <div><AL>Payoff balance ($)</AL><AInp v={d.current_balance} set={(v) => upDebt(i, "current_balance", v)} type="number" testid={`aae-mortgage-balance-${i}`} /></div>
                  </div>
                </div>
              ))}
              {(f.additional_debts || []).length > 0 && (
                <div className="text-[11px] text-[#3DBB8E] font-semibold mt-1" data-testid="aae-consolidated-total">
                  Total to consolidate into one Seutrade note: ${((Number(f.current_balance || 0)) + (f.additional_debts || []).reduce((s, d) => s + Number(d.current_balance || 0), 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mt-4">
            <button onClick={save} disabled={busy || !f.current_lender || !f.current_balance || !f.borrower_full_name} data-testid="aae-save"
              className="inline-flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-40 text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.16em] font-bold rounded-sm">
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save Changes
            </button>
            {msg && <span className="text-[11px] text-[#FF8585]" data-testid="aae-error">✗ {msg}</span>}
          </div>

          <div className="mt-6 border-t border-[#2A2A22] pt-4">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#3DBB8E] font-bold mb-2">Borrower Login Access</div>
            <div className="text-[11px] text-[#8A8A8A] mb-2">Generate a temporary password so <span className="text-[#B8C4B8]">{app.borrower_email}</span> can sign in at /borrower and e-sign their binder.</div>
            {accessCred && (
              <div className="mb-2 bg-[#0B1A14] border border-[#2A6B4F]/50 rounded-sm p-2.5 text-[12px]" data-testid="aae-access-cred">
                <div>Email: <strong className="text-[#ECECE4]" data-testid="aae-access-email">{accessCred.email}</strong></div>
                <div>Temporary password: <strong className="text-[#3DBB8E]" data-testid="aae-access-pw">{accessCred.temp_password}</strong> <span className="text-[10px] text-[#8A8A8A]">(shown once — copy now)</span></div>
              </div>
            )}
            <button onClick={resetAccess} disabled={accessBusy} data-testid="aae-access-reset"
              className="inline-flex items-center gap-1.5 bg-[#2A6B4F] hover:bg-[#22593f] disabled:opacity-50 text-[#ECECE4] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
              {accessBusy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} {accessCred ? "Reset Borrower Password" : "Generate Borrower Password"}
            </button>
          </div>

          <div className="mt-6 border-t border-[#2A2A22] pt-4">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#B8860B] font-bold mb-2">Documents ({docs.length})</div>
            <div className="space-y-1 mb-3">
              {docs.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-[12px]" data-testid={`aae-doc-${d.id}`}>
                  <a href={docUrl(d)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#7DD3B8] hover:text-[#3DBB8E]">
                    <FileText size={12} /> {d.original_filename} <span className="text-[#5C5C5C]">· {d.doc_type}{d.uploaded_by === "admin" ? " · admin" : ""}</span>
                  </a>
                  <span className={`px-1.5 py-0.5 text-[8.5px] uppercase tracking-[0.08em] border rounded-sm ${reviewBadge(d.review_status)}`} data-testid={`aae-doc-status-${d.id}`}>
                    {d.review_status === "needs_attention" ? "needs attention" : (d.review_status || "pending")}
                  </span>
                  <button onClick={() => reviewDoc(d.id, "verified")} data-testid={`aae-doc-verify-${d.id}`} className="text-[#3DBB8E] hover:text-[#5fe0b3]" title="Mark verified"><CheckCircle2 size={13} /></button>
                  <button onClick={() => reviewDoc(d.id, "needs_attention")} data-testid={`aae-doc-flag-${d.id}`} className="text-[#E4C97A] hover:text-[#ffe08a]" title="Flag needs attention"><AlertTriangle size={13} /></button>
                  <button onClick={() => delDoc(d.id)} data-testid={`aae-doc-del-${d.id}`} className="text-[#FF8585] hover:text-[#ff5252]" title="Remove"><Trash2 size={12} /></button>
                </div>
              ))}
              {docs.length === 0 && <div className="text-[12px] text-[#5C5C5C]">No documents.</div>}
            </div>
            <div className="flex items-center gap-2">
              <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="aae-doctype"
                className="bg-[#0A0A0A] border border-[#2A2A22] rounded-sm text-[12px] px-2 py-1.5">
                <option value="government_id">Gov ID</option>
                <option value="income">Income</option>
                <option value="mortgage_statement">Mortgage stmt</option>
                <option value="property_deed">Deed/Title</option>
                <option value="other">Other</option>
              </select>
              <input ref={fileRef} type="file" onChange={onFile} className="hidden" data-testid="aae-file-input"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="aae-upload"
                className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] px-3 py-1.5 text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm">
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} Upload
              </button>
            </div>
          </div>

          {/* Account details & internal notes */}
          <div className="mt-6">
            <AccountDetailsPanel application={app} editable showNotes theme="dark" actorRole="admin"
              onSaveProfile={async (profile) => { await axios.put(`${API}/admin/borrower-applications/${app.id}/account-profile`, { profile }); }}
              onAddNote={async (text) => { const r = await axios.post(`${API}/admin/borrower-applications/${app.id}/account-notes`, { text }); return r.data.application?.account_notes || []; }}
              onEditNote={async (noteId, text) => { const r = await axios.put(`${API}/admin/borrower-applications/${app.id}/account-notes/${noteId}`, { text }); return r.data.application?.account_notes || []; }}
              onDeleteNote={async (noteId) => { const r = await axios.delete(`${API}/admin/borrower-applications/${app.id}/account-notes/${noteId}`); return r.data.application?.account_notes || []; }}
              onAttach={async (noteId, file) => { const fd = new FormData(); fd.append("file", file); const r = await axios.post(`${API}/admin/borrower-applications/${app.id}/account-notes/${noteId}/attachment`, fd); return r.data.application?.account_notes || []; }}
              attachmentUrl={(noteId, attId) => `${API}/admin/borrower-applications/${app.id}/account-notes/${noteId}/attachment/${attId}?auth=${encodeURIComponent(localStorage.getItem("th_admin_token") || "")}`} />
            {app.borrower_id && (
              <div className="mt-4">
                <BorrowerAccessHistory apiBase={API} token={localStorage.getItem("th_admin_token") || ""} borrowerId={app.borrower_id} />
              </div>
            )}
            <DeliveryCertificates apiBase={API} appId={app.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DeliveryCertificates({ apiBase, appId }) {
  const [certs, setCerts] = useState([]);
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${apiBase}/admin/borrower-applications/${appId}/certificates`); setCerts(r.data.certificates || []); } catch { /* noop */ }
    })();
  }, [apiBase, appId]);
  const download = async (fid) => {
    const win = window.open("", "_blank");
    try {
      const r = await axios.get(`${apiBase}/admin/fax/certificate/${fid}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      if (win && !win.closed) win.location = url;
      else { const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { if (win && !win.closed) win.close(); toast.error("Could not open certificate."); }
  };
  if (!certs.length) return null;
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  return (
    <div className="mt-4 border border-[#2A2A22] rounded-md p-3" data-testid="deal-delivery-certificates">
      <h4 className="text-[13px] font-semibold text-[#ECECE4] mb-1 flex items-center gap-2">
        <ShieldCheck size={14} className="text-[#3DBB8E]" /> Delivery Certificates
        <span className="text-[10px] font-normal text-[#8A8A8A]">· proof of transmission + electronic-closing notice</span>
      </h4>
      <div className="divide-y divide-[#1F1F18]">
        {certs.map((c) => (
          <div key={c.id} data-testid={`deal-cert-${c.id}`} className="flex items-center gap-2 py-1.5 text-[12px]">
            <span className="text-[#3DBB8E]">✓</span>
            <span className="flex-1 truncate text-[#C9C9C0]">{c.deal_label || "Payload"} → <span className="font-mono">{c.to}</span> · {fmt(c.certificate_filed_at)} · {(c.status || "").toUpperCase()}</span>
            <button type="button" onClick={() => download(c.id)} data-testid={`deal-cert-dl-${c.id}`}
              className="inline-flex items-center gap-1 text-[10px] text-[#C9A227] hover:text-[#E4C97A] border border-[#B8860B]/30 hover:border-[#B8860B]/60 rounded px-2 py-0.5">
              <Download size={11} /> Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function BrokerApplications({ brokers, onDecide, decidingId, token, onDelete }) {
  return (
    <section className="mb-12" data-testid="broker-applications-admin">
      <div className="mb-5 flex items-center gap-3">
        <Handshake size={18} className="text-[#C9A227]" />
        <h2 className="text-2xl font-serif">Seutrade Broker Applications</h2>
        {brokers.pending_count > 0 && (
          <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] bg-[#C9A227]/15 text-[#C9A227] border border-[#C9A227]/40 rounded-sm">{brokers.pending_count} pending</span>
        )}
      </div>
      {brokers.pending.length === 0 && brokers.resolved.length === 0 ? (
        <div className="text-[13px] text-[#5C5C5C] bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">No broker applications yet.</div>
      ) : (
        <div className="space-y-3">
          {brokers.pending.map((b) => <BrokerRow key={b.id} b={b} onDecide={onDecide} decidingId={decidingId} token={token} onDelete={onDelete} />)}
          {brokers.resolved.map((b) => <BrokerRow key={b.id} b={b} onDecide={onDecide} decidingId={decidingId} token={token} onDelete={onDelete} />)}
        </div>
      )}
    </section>
  );
}

const brStatusCls = (s) => ({ approved: "text-[#3DBB8E] border-[#0B6B4F]/60 bg-[#0B6B4F]/15", pending: "text-[#E4C97A] border-[#B8860B]/50 bg-[#B8860B]/10", rejected: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15", suspended: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/15", terminated: "text-[#FF8585] border-[#9E2A2B]/60 bg-[#9E2A2B]/25" }[s] || "");

function BrokerRow({ b, onDecide, decidingId, token, onDelete }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState(null);
  const [loading, setLoading] = useState(false);
  const askReject = () => { const n = window.prompt("Reason for rejecting this broker (optional):", ""); if (n === null) return; onDecide(b.id, "reject", n); };
  const askSuspend = () => { const n = window.prompt("Reason for suspending this broker:", "Policy violation"); if (n === null) return; onDecide(b.id, "suspend", n); };
  const askTerminate = () => { if (!window.confirm(`TERMINATE broker "${b.company}"? This permanently disables client onboarding.`)) return; const n = window.prompt("Reason for termination:", "Account terminated for cause"); if (n === null) return; onDecide(b.id, "terminate", n); };

  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && clients === null) {
      setLoading(true);
      try { const r = await axios.get(`${API}/admin/broker/${b.id}/clients`); setClients(r.data.clients || []); }
      catch { setClients([]); }
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg" data-testid={`broker-app-${b.id}`}>
      <div className="p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-semibold text-[14px] flex items-center gap-2"><Building2 size={14} className="text-[#C9A227]" /> {b.company} <span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] border border-[#C9A227]/40 text-[#C9A227] rounded-sm" data-testid={`broker-type-badge-${b.id}`}>{b.applicant_type === "loan_officer" ? "Loan Officer" : "Broker / Firm"}</span><span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] border rounded-sm ${brStatusCls(b.status)}`}>{b.status}</span></div>
          <div className="text-[12px] text-[#9A9A90] mt-1">{b.contact_name} · {b.email}{b.phone ? ` · ${b.phone}` : ""}</div>
          <div className="text-[11px] text-[#5C5C5C] mt-1">{b.nmls_no ? `NMLS ${b.nmls_no}` : (b.license_no ? `Lic ${b.license_no}` : "No NMLS on file")}{b.license_no && b.nmls_no ? ` · Lic ${b.license_no}` : ""}{b.ein ? ` · EIN ${b.ein}` : ""} · {b.client_count || 0} client(s)</div>
          {b.review_note && <div className="text-[11px] text-[#8A8A8A] mt-1 italic">Note: {b.review_note}</div>}
          <button onClick={toggle} data-testid={`broker-view-clients-${b.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#C9A227] hover:text-[#e0b62e]">
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} View clients & documents ({b.client_count || 0})
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {b.status === "pending" && (
            <>
              <button onClick={() => onDecide(b.id, "approve")} disabled={decidingId === b.id} data-testid={`broker-approve-${b.id}`}
                className="inline-flex items-center gap-1.5 bg-[#0B6B4F] hover:bg-[#0d7d5c] disabled:opacity-50 text-white px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
                {decidingId === b.id ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={13} />} Approve
              </button>
              <button onClick={askReject} disabled={decidingId === b.id} data-testid={`broker-reject-${b.id}`}
                className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#9E2A2B]/50 text-[#FF8585] hover:bg-[#9E2A2B]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"><XCircle size={13} /> Reject</button>
            </>
          )}
          {(b.status === "approved" || b.status === "suspended") && (
            <>
              {b.status === "approved" && (
                <button onClick={askSuspend} data-testid={`broker-suspend-${b.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#13130E] border border-[#B8860B]/50 text-[#E4C97A] hover:bg-[#B8860B]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"><Ban size={13} /> Suspend</button>
              )}
              {b.status === "suspended" && (
                <button onClick={() => onDecide(b.id, "restore")} data-testid={`broker-restore-${b.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 border border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"><RotateCcw size={13} /> Reinstate</button>
              )}
              <button onClick={askTerminate} data-testid={`broker-terminate-${b.id}`}
                className="inline-flex items-center gap-1.5 bg-[#9E2A2B]/20 border border-[#9E2A2B]/60 text-[#FF8585] hover:bg-[#9E2A2B]/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"><Trash2 size={13} /> Terminate</button>
            </>
          )}
          {b.status === "rejected" && (
            <button onClick={() => onDecide(b.id, "restore")} data-testid={`broker-restore-${b.id}`}
              className="inline-flex items-center gap-1.5 bg-[#0B6B4F]/20 border border-[#0B6B4F]/50 text-[#3DBB8E] hover:bg-[#0B6B4F]/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm"><RotateCcw size={13} /> Reinstate</button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(b.id)} data-testid={`broker-delete-${b.id}`}
              className="inline-flex items-center gap-1.5 bg-[#9E2A2B]/25 border border-[#9E2A2B]/70 text-[#FF8585] hover:bg-[#9E2A2B]/40 px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm" title="Permanently delete this broker & their client transactions"><Trash2 size={13} /> Delete</button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-t border-[#2A2A22] p-5 bg-[#0A0A0A]/40" data-testid={`broker-clients-${b.id}`}>
          {loading ? (
            <div className="text-[12px] text-[#5C5C5C] flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading clients…</div>
          ) : !clients || clients.length === 0 ? (
            <div className="text-[12px] text-[#5C5C5C]">No clients onboarded by this broker yet.</div>
          ) : (
            <div className="space-y-2">
              {clients.map((c) => (
                <div key={c.id} className="bg-[#13130E] border border-[#2A2A22] rounded-md p-3" data-testid={`broker-client-row-${c.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-[13px] font-semibold">{c.borrower_name} <span className="text-[#8A8A8A] text-[11px]">· {c.borrower_email}</span></div>
                      <div className="text-[11px] text-[#8A8A8A] mt-0.5">{fmtUSD(c.current_balance)} → 0% note {fmtUSD(c.refi?.new_principal)} · {fmtUSD(c.refi?.new_monthly_payment)}/mo · {c.property_use || "residential"}</div>
                    </div>
                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] border rounded-sm ${brStatusCls(c.status)}`}>{c.status}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-[0.1em]">
                    {["intake", "approval", "disbursement", "closing"].map((p) => {
                      const st = c.pipeline?.[p] || "pending";
                      const color = st === "success" ? "text-[#3DBB8E]" : st === "rejected" ? "text-[#FF8585]" : st === "pending" ? "text-[#5C5C5C]" : "text-[#E4C97A]";
                      return <span key={p} className={color}>{st === "success" ? "✓" : "•"} {p}</span>;
                    })}
                  </div>
                  {(c.documents || []).length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-2">
                      {c.documents.map((d) => (
                        <a key={d.id} href={`${API}/broker/documents/${d.id}?auth=${token}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-[#7DD3B8] hover:text-[#3DBB8E]" data-testid={`admin-broker-doc-${d.id}`}>
                          <FileText size={11} /> {d.original_filename} <span className="text-[#5C5C5C]">· {d.doc_type}</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const ADMIN_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminTeamSection() {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${ADMIN_API}/admin/team`); setAdmins(r.data.admins || []); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || form.password.length < 8) { toast.error("Enter an email and a password of at least 8 characters."); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${ADMIN_API}/admin/team`, { email: form.email.trim(), password: form.password, name: form.name.trim() || "Administrator" });
      toast.success(`Admin ${r.data.action === "promoted" ? "promoted" : "created"}: ${r.data.email}`);
      setForm({ email: "", password: "", name: "" });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setBusy(false);
  };

  const revoke = async (id, email) => {
    if (!window.confirm(`Revoke admin access for ${email}?`)) return;
    try { await axios.delete(`${ADMIN_API}/admin/team/${id}`); toast.success("Admin access revoked."); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const toggleFullAccess = async (a) => {
    const next = !a.full_access;
    let mode = "edit";
    if (next) {
      const viewOnly = window.confirm(`Give ${a.name || a.email} full access.\n\nClick OK for VIEW-ONLY (see all files, cannot change anything).\nClick Cancel for FULL EDIT (see and edit all files).`);
      mode = viewOnly ? "view" : "edit";
    }
    try {
      await axios.post(`${ADMIN_API}/admin/team/${a.id}/full-access`, { full_access: next, mode });
      toast.success(`${a.email}: full access ${next ? `granted (${mode === "view" ? "view-only" : "full edit"})` : "revoked"}.`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <section id="sec-admin-team" className="mb-12 scroll-mt-32" data-testid="admin-team-section">
      <div className="mb-5 flex items-center gap-3">
        <ShieldCheck size={18} className="text-[#B8860B]" />
        <h2 className="text-2xl font-serif">Admin Team</h2>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">
          <h3 className="text-[13px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold mb-4">Grant Back-Office Access</h3>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Full Name</label>
              <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Linda Bailey" data-testid="admin-team-name"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} placeholder="name@example.com" data-testid="admin-team-email"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Password (min 8)</label>
              <input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} placeholder="Set a password" data-testid="admin-team-password"
                className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none font-mono" />
            </div>
            <button type="submit" disabled={busy} data-testid="admin-team-submit"
              className="w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm flex items-center justify-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add / Promote Admin
            </button>
            <p className="text-[11px] text-[#5C5C5C]">If the email already has a borrower/broker account, it will be promoted to admin. They sign in at <span className="text-[#8A8A8A]">/admin/login</span>.</p>
          </form>
        </div>
        <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-6">
          <h3 className="text-[13px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold mb-4">Current Admins ({admins.length})</h3>
          <div className="space-y-2" data-testid="admin-team-list">
            {admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-[#2A2A22] rounded-sm px-3 py-2" data-testid={`admin-team-row-${a.id}`}>
                <div>
                  <div className="text-[#ECECE4] text-[13px] font-semibold flex items-center gap-2">
                    {a.name || "Administrator"}
                    {a.is_owner && <span className="text-[9px] uppercase tracking-[0.12em] text-[#E4C97A] border border-[#E4C97A]/40 rounded px-1.5 py-0.5">Owner</span>}
                    {a.full_access && !a.is_owner && <span className="text-[9px] uppercase tracking-[0.12em] text-[#3DBB8E] border border-[#3DBB8E]/40 rounded px-1.5 py-0.5" data-testid={`admin-team-fullaccess-badge-${a.id}`}>Full access{a.full_access_mode === "view" ? " · view-only" : ""}</span>}
                  </div>
                  <div className="text-[11px] text-[#8A8A8A]">{a.email}{a.promoted ? " · promoted" : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  {!a.is_owner && (
                    <button onClick={() => toggleFullAccess(a)} data-testid={`admin-team-fullaccess-${a.id}`}
                      title={a.full_access ? "Revoke full access" : "Grant full access to all files"}
                      className={`text-[10px] uppercase tracking-[0.1em] font-bold rounded-sm border px-2.5 py-1.5 ${a.full_access ? "border-[#3DBB8E] text-[#7fe7c2] bg-[#3DBB8E]/10" : "border-[#2A2A22] text-[#8A8A80] hover:border-[#3DBB8E]/50"}`}>
                      {a.full_access ? "Full access ✓" : "Grant full access"}
                    </button>
                  )}
                  {!a.is_owner && (
                    <button onClick={() => revoke(a.id, a.email)} data-testid={`admin-team-revoke-${a.id}`} title="Revoke admin access"
                      className="text-[#C77] hover:text-[#FF8585] p-1.5 rounded hover:bg-[#7A3A3A]/20"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ClosingFeeSettingsCard />
    </section>
  );
}

function ClosingFeeSettingsCard() {
  const [f, setF] = useState({ mortgage_total: "", auto_total: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${ADMIN_API}/admin/borrower-applications/closing-fees/settings`)
      .then((r) => setF({ mortgage_total: r.data.mortgage_total, auto_total: r.data.auto_total })).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${ADMIN_API}/admin/borrower-applications/closing-fees/settings`,
        { mortgage_total: Number(f.mortgage_total || 0), auto_total: Number(f.auto_total || 0) });
      toast.success("Pre-closing fees updated. New applications use these amounts.");
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    setBusy(false);
  };
  return (
    <div className="mt-5 bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="closing-fee-settings">
      <h3 className="text-[13px] uppercase tracking-[0.12em] text-[#E4C97A] font-bold mb-1">Pre-Closing Cost Settings (Owner)</h3>
      <p className="text-[11px] text-[#8A8A8A] mb-4">Set the flat pre-closing cost charged per transaction by obligation type. Applies to newly generated/updated files.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Mortgage total ($)</label>
          <input type="number" value={f.mortgage_total} onChange={(e) => setF((p) => ({ ...p, mortgage_total: e.target.value }))} data-testid="fee-mortgage-total"
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.16em] text-[#8A8A8A] block mb-1">Auto total ($)</label>
          <input type="number" value={f.auto_total} onChange={(e) => setF((p) => ({ ...p, auto_total: e.target.value }))} data-testid="fee-auto-total"
            className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none" />
        </div>
        <button onClick={save} disabled={busy} data-testid="fee-save"
          className="bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm">
          {busy ? "Saving…" : "Save Fees"}
        </button>
      </div>
    </div>
  );
}
